import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, User, Building2, CreditCard, Settings,
  LogOut, AlertCircle,
  LayoutDashboard, Send, RefreshCw, Phone, Mail,
  Shield, TicketIcon, Plus, Search, Calendar, Menu, X,
  Activity, ChevronRight, ChevronLeft, ChevronDown, CheckCircle, XCircle, Banknote,
  MapPin, TrendingUp, Eye, FileText, Paperclip, Lock, Star, Pencil, Trash2, Edit, Clock, Truck,
  ClipboardList, ArrowRight, StickyNote
} from "lucide-react";
import { adminApi, gardeApi, pharmacieApi, pharmacienApi, localisationApi } from "./services/api";
import LivraisonOperateur from "./LivraisonOperateur";
import logoHorizontale from "./assets/logo/logo-horizontale.svg";
import logoMobile from "./assets/logo/logo-mobile.svg";
import waveQr from "./assets/wave/wave_qr-1.png";

// ═══════════════════════════════════════════════════════
// Utilitaires
// ═══════════════════════════════════════════════════════

const StatutBadge = ({ statut }) => {
  const cfg = {
    ACTIF: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500", label: "Actif" },
    SUSPENDU: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500", label: "Suspendu" },
    INACTIF: { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400", label: "Inactif" },
    OUVERT: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500", label: "Ouvert" },
    EN_COURS: { bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500", label: "En cours" },
    RESOLU: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500", label: "Résolu" },
    FERME: { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400", label: "Fermé" },
    VALIDE: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500", label: "Validé" },
    EN_ATTENTE: { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500", label: "En attente" },
    // Statuts de garde
    PLANIFIEE:  { bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500",   label: "Planifiée" },
    TERMINEE:   { bg: "bg-gray-100",   text: "text-gray-600",   dot: "bg-gray-400",   label: "Terminée" },
    ANNULEE:    { bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500",    label: "Annulée" },
    CONFIRME:   { bg: "bg-emerald-100",text: "text-emerald-700",dot: "bg-emerald-500",label: "Confirmée" },
  };
  const c = cfg[statut] || cfg.INACTIF;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
};

const PharmacieStatutBadge = ({ statut }) => {
  const cfg = {
    ACTIVE:     { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500", label: "Active" },
    VALIDEE:    { bg: "bg-blue-100",    text: "text-blue-700",    dot: "bg-blue-500",    label: "Validée" },
    EN_ATTENTE: { bg: "bg-orange-100",  text: "text-orange-700",  dot: "bg-orange-500",  label: "En attente" },
    SUSPENDUE:  { bg: "bg-red-100",     text: "text-red-700",     dot: "bg-red-500",     label: "Suspendue" },
    REJETEE:    { bg: "bg-red-100",     text: "text-red-700",     dot: "bg-red-500",     label: "Rejetée" },
    FERMEE:     { bg: "bg-gray-100",    text: "text-gray-600",    dot: "bg-gray-400",    label: "Fermée" },
  };
  const c = cfg[statut] || cfg.EN_ATTENTE;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
};

const PrioriteBadge = ({ priorite }) => {
  const cfg = {
    URGENTE: "bg-red-100 text-red-700",
    HAUTE: "bg-orange-100 text-orange-700",
    NORMALE: "bg-blue-100 text-blue-700",
    BASSE: "bg-gray-100 text-gray-600",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg[priorite] || cfg.NORMALE}`}>
      {priorite}
    </span>
  );
};

const PAGE_SIZE = 10;

const Pagination = ({ total, page, setPage }) => {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-white">
      <p className="text-xs text-gray-500">{total} élément{total > 1 ? "s" : ""} · Page {page} / {totalPages}</p>
      <div className="flex gap-2">
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
          className="flex items-center gap-1 px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
          <ChevronLeft size={12} /> Préc.
        </button>
        <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
          className="flex items-center gap-1 px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
          Suiv. <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// Menu items
// ═══════════════════════════════════════════════════════

const MENU_ITEMS = [
  { id: "dashboard",  label: "Tableau de bord",   icon: LayoutDashboard },
  { id: "syndicats",  label: "Syndicats",          icon: Building2 },
  { id: "reseau",     label: "Gestion du réseau",  icon: Activity },
  { id: "gardes",     label: "Planning de garde",  icon: Calendar },
  { id: "support",    label: "Support",            icon: TicketIcon },
  { id: "patients",   label: "Patients",           icon: Users },
  { id: "paiements",      label: "Paiements",          icon: CreditCard },
  { id: "demandes",      label: "Demandes",            icon: ClipboardList },
  { id: "livraisons",    label: "Livraisons",          icon: Truck },
  { id: "localisation",  label: "Localisation",       icon: MapPin },
  { id: "parametres",    label: "Paramètres",         icon: Settings },
];

// ═══════════════════════════════════════════════════════
// Composant principal
// ═══════════════════════════════════════════════════════

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const userName  = localStorage.getItem("userName") || "Super Admin";
  const userEmail = localStorage.getItem("userEmail") || "";
  const initials  = userName.split(" ").map(n => n.charAt(0)).join("").toUpperCase().slice(0, 2) || "SA";

  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [activeTab, setActiveTab]     = useState("dashboard");
  const [loading, setLoading]         = useState(false);

  // Data
  const [syndicats,   setSyndicats]   = useState([]);
  const [pharmacies,  setPharmacies]  = useState([]);
  const [gardes,      setGardes]      = useState([]);
  const [tickets,     setTickets]     = useState([]);

  // Plannings tab state
  const [plannings,               setPlannings]               = useState([]);
  const [planningsMois,           setPlanningsMois]           = useState(() => {
    const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`;
  });
  const [planningsSyndicatFilter, setPlanningsSyndicatFilter] = useState("");
  const [planningStatutFilter,    setPlanningStatutFilter]    = useState("");
  const [expandedPlanningId,      setExpandedPlanningId]      = useState(null);
  const [planningsLoading,        setPlanningsLoading]        = useState(false);
  const [patients,    setPatients]    = useState([]);
  const [ticketStats, setTicketStats] = useState({});

  // UI state
  const [selectedTicket,      setSelectedTicket]      = useState(null);
  const [reponseText,         setReponseText]         = useState("");
  const [showNewSyndicatForm, setShowNewSyndicatForm] = useState(false);
  const [searchSyndicat,      setSearchSyndicat]      = useState("");
  const [searchPharmacie,     setSearchPharmacie]     = useState("");
  const [reseauFilterStatut,  setReseauFilterStatut]  = useState("");
  const [reseauFilterPays,    setReseauFilterPays]    = useState("");
  const [reseauFilterRegion,  setReseauFilterRegion]  = useState("");
  const [reseauFilterDep,     setReseauFilterDep]     = useState("");
  const [reseauFilterCommune, setReseauFilterCommune] = useState("");
  const [filterTicketStatut,  setFilterTicketStatut]  = useState("");
  const [newSyndicat, setNewSyndicat] = useState({
    nom: "", username: "", motDePasse: "", email: "", telephone: "",
    type: "DEPARTEMENT", departementId: "", communeId: "", regionId: "", communeIds: [],
  });
  // Zone picker state for new/edit syndicat form
  const [syndicatZones, setSyndicatZones]               = useState([]);
  const [syndicatZonesLoading, setSyndicatZonesLoading] = useState(false);
  const [syndicatZoneRegions, setSyndicatZoneRegions]   = useState([]);
  const [syndicatZoneDeps, setSyndicatZoneDeps]         = useState([]);
  const [syndicatZoneRegionId, setSyndicatZoneRegionId] = useState("");
  const [syndicatZoneDepId, setSyndicatZoneDepId]       = useState("");
  // Edit modal zone state (réutilise les mêmes listes syndicatZones* mais mode "edit")
  const [editZoneMode, setEditZoneMode]                 = useState(false); // true = modification de zone active

  // Réseau state
  const [reseauTab, setReseauTab] = useState("pharmacies");
  const [pharmaciens, setPharmaciens] = useState([]);
  const [pharmaciesEnAttente, setPharmaciesEnAttente] = useState([]);
  const [showAddPharmacieModal, setShowAddPharmacieModal] = useState(false);
  const [showAddPharmacienModal, setShowAddPharmacienModal] = useState(false);
  const [erreursPharmacie, setErreursPharmacie] = useState({});
  const [erreursPharmacien, setErreursPharmacien] = useState({});
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignPharmacieId, setAssignPharmacieId] = useState("");
  const [assignSyndicatId, setAssignSyndicatId] = useState("");
  const [showAssignPharmacienModal, setShowAssignPharmacienModal] = useState(false);
  const [assignPharmacienTarget, setAssignPharmacienTarget] = useState(null);
  const [assignPharmacienId, setAssignPharmacienId] = useState("");

  const JOURS_SEMAINE = ["lundi","mardi","mercredi","jeudi","vendredi","samedi","dimanche"];
  const JOURS_LABELS  = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];
  const initHoraires = () => Object.fromEntries(JOURS_SEMAINE.map(j => [j, { ouverture: "08:00", fermeture: "21:00", ferme: false }]));

  const [newPharmacie, setNewPharmacie] = useState({
    nom: "", adresseComplete: "", quartier: "", telephone: "", email: "", siteWeb: "",
    communeId: "", syndicatId: "", pharmacienId: "",
    latitude: "", longitude: "",
    numeroAgrementMinistere: "", numeroOrdre: "",
    description: "", services: "",
    accepteCommandes: false, proposeLivraison: false, rayonLivraisonKm: ""
  });
  const [newPharmacieHoraires, setNewPharmacieHoraires] = useState(initHoraires);
  const [newPharmacien, setNewPharmacien] = useState({
    prenom: "", nom: "", email: "", telephone: "",
    dateNaissance: "", sexe: "M", numeroOrdreNational: "", universiteFormation: "", anneeDiplome: ""
  });
  const [reseauLoading, setReseauLoading] = useState(false);
  const [selectedSyndicatDetail, setSelectedSyndicatDetail] = useState(null);
  const [selectedPharmacieDetail, setSelectedPharmacieDetail] = useState(null);
  // Localisation pour modal pharmacie
  const [regions, setRegions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [communesList, setCommunesList] = useState([]);
  const [detectedSyndicat, setDetectedSyndicat] = useState(null); // auto-détecté depuis commune

  // Gestion Localisation tab
  const [pays, setPays] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [localisationTab, setLocalisationTab] = useState("regions");
  const [localisationModal, setLocalisationModal] = useState(null);
  const [localisationForm, setLocalisationForm] = useState({});
  const [localisationTarget, setLocalisationTarget] = useState(null);
  const [localisationLoading, setLocalisationLoading] = useState(false);
  const [localisationInitLoading, setLocalisationInitLoading] = useState(false);
  const [locSearch, setLocSearch] = useState("");
  const [locFilterPays, setLocFilterPays] = useState("");
  const [locFilterRegion, setLocFilterRegion] = useState("");
  const [locFilterDep, setLocFilterDep] = useState("");
  const [locPage, setLocPage] = useState(1);
  const LOC_PAGE_SIZE = 15;

  // Sous-comptes syndicat
  const [selectedSyndicatForComptes, setSelectedSyndicatForComptes] = useState(null);
  const [sousComptes, setSousComptes] = useState([]);
  const [sousComptesLoading, setSousComptesLoading] = useState(false);
  const [showResetSousCompteModal, setShowResetSousCompteModal] = useState(false);
  const [selectedSousCompte, setSelectedSousCompte] = useState(null);
  const [resetMdpForm, setResetMdpForm] = useState({ mdp: "", confirm: "" });
  const [resetMdpLoading, setResetMdpLoading] = useState(false);

  // Pagination
  const [syndicatPage, setSyndicatPage] = useState(1);
  const [pharmaciePage, setPharmaciePage] = useState(1);
  const [pharmacienPage, setPharmacienPage] = useState(1);

  // Detail modals
  const [selectedPharmacienDetail, setSelectedPharmacienDetail] = useState(null);

  // Edit modals
  const [showEditSyndicatModal, setShowEditSyndicatModal] = useState(false);
  const [showEditPharmacieModal, setShowEditPharmacieModal] = useState(false);
  const [showEditPharmacienModal, setShowEditPharmacienModal] = useState(false);
  const [editPharmacieHoraires, setEditPharmacieHoraires] = useState({});
  const [editSyndicatData, setEditSyndicatData] = useState({});
  const [editPharmacieData, setEditPharmacieData] = useState({});
  const [editPharmacienData, setEditPharmacienData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // Demandes inscription
  const [demandes, setDemandes] = useState([]);
  const [demandeSearch, setDemandeSearch] = useState("");
  const [demandeStatutFilter, setDemandeStatutFilter] = useState("");
  const [demandePage, setDemandePage] = useState(1);
  const [selectedDemandeDetail, setSelectedDemandeDetail] = useState(null);
  const [demandeActionLoading, setDemandeActionLoading] = useState(null);
  const [demandeNotes, setDemandeNotes] = useState("");
  const [demandeNotesSaving, setDemandeNotesSaving] = useState(false);

  // Paiements state
  const [paiementTab, setPaiementTab] = useState("syndicats");
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [paiementSyndicat, setPaiementSyndicat] = useState(null);
  const [paiementForm, setPaiementForm] = useState({ datePaiement: new Date().toISOString().slice(0,10), reference: "", notes: "" });
  const [paiementLoading, setPaiementLoading] = useState(false);
  const [pharmPaiementSearch, setPharmPaiementSearch] = useState("");
  const [relancerLoading, setRelancerLoading] = useState(null);
  const [syndicatSearch, setSyndicatSearch] = useState("");
  const [pharmPage, setPharmPage] = useState(1);
  const [paiementSyndicatPage, setPaiementSyndicatPage] = useState(1);
  const [patientPage, setPatientPage] = useState(1);
  const [patientSearch, setPatientSearch] = useState("");
  const [patientFilterStatut, setPatientFilterStatut] = useState("");
  const [patientFilterPremium, setPatientFilterPremium] = useState("");
  const [patientFilterSexe, setPatientFilterSexe] = useState("");
  const [patientFilterRegion, setPatientFilterRegion] = useState("");
  // Filtres Paiements
  const [paiementSyndicatFilterStatut, setPaiementSyndicatFilterStatut] = useState("");
  const [paiementSyndicatFilterPlan, setPaiementSyndicatFilterPlan] = useState("");
  const [paiementPharmFilterStatut, setPaiementPharmFilterStatut] = useState("");
  const [paiementPharmFilterSyndicat, setPaiementPharmFilterSyndicat] = useState("");
  // Filtres Paiements > Patients (séparés de l'onglet Patients)
  const [paiPatSearch, setPaiPatSearch] = useState("");
  const [paiPatFilterPremium, setPaiPatFilterPremium] = useState("");
  const [paiPatPage, setPaiPatPage] = useState(1);
  // Modaux paiement pharmacie
  const [showPharmPaiementModal, setShowPharmPaiementModal] = useState(false);
  const [pharmPaiementTarget, setPharmPaiementTarget] = useState(null);
  const [pharmPaiementRef, setPharmPaiementRef] = useState("");
  const [pharmPaiementLoading, setPharmPaiementLoading] = useState(false);
  // Détail patient
  const [selectedPatientDetail, setSelectedPatientDetail] = useState(null);
  // Modal assigner pharmacie → pharmacien
  const [showAssignPharmacieToPharmacienModal, setShowAssignPharmacieToPharmacienModal] = useState(false);
  const [assignPharmacieToPharmacienTarget, setAssignPharmacieToPharmacienTarget] = useState(null);
  const [assignPharmacieForPharmacienId, setAssignPharmacieForPharmacienId] = useState("");
  // Modal activation premium patient
  const [showPatientPremiumModal, setShowPatientPremiumModal] = useState(false);
  const [patientPremiumTarget, setPatientPremiumTarget] = useState(null);
  const [patientPremiumRef, setPatientPremiumRef] = useState("");
  const [patientPremiumLoading, setPatientPremiumLoading] = useState(false);
  const ITEMS_PER_PAGE = 10;

  // ── Chargement données ──
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [synd, pharm, pharmEn, pharmiens, gard, tick, pat, stats, dem, pending] = await Promise.allSettled([
        adminApi.getSyndicats(),
        adminApi.getPharmacies(),
        adminApi.getPharmaciesEnAttente(),
        adminApi.getPharmaciens(),
        adminApi.getAllGardes(),
        adminApi.getTickets(),
        adminApi.getPatients(),
        adminApi.getTicketStats(),
        adminApi.getDemandes(),
        adminApi.getPendingPayments(),
      ]);
      if (synd.status      === "fulfilled") setSyndicats(Array.isArray(synd.value)      ? synd.value      : []);
      if (pharm.status     === "fulfilled") setPharmacies(Array.isArray(pharm.value)    ? pharm.value     : []);
      if (pharmEn.status   === "fulfilled") setPharmaciesEnAttente(Array.isArray(pharmEn.value) ? pharmEn.value : []);
      if (pharmiens.status === "fulfilled") setPharmaciens(Array.isArray(pharmiens.value) ? pharmiens.value : []);
      if (gard.status      === "fulfilled") setGardes(Array.isArray(gard.value)         ? gard.value      : []);
      if (tick.status      === "fulfilled") setTickets(Array.isArray(tick.value)        ? tick.value      : []);
      if (pat.status       === "fulfilled") setPatients(Array.isArray(pat.value)        ? pat.value       : []);
      if (stats.status     === "fulfilled") setTicketStats(stats.value || {});
      if (dem.status       === "fulfilled") setDemandes(Array.isArray(dem.value)        ? dem.value       : []);
      if (pending.status   === "fulfilled") setPendingPayments(Array.isArray(pending.value) ? pending.value : []);
      else console.error(pending.reason);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Charger données localisation
  const loadLocalisation = () => {
    localisationApi.getPays().then(d => setPays(Array.isArray(d) ? d : [])).catch(() => {});
    localisationApi.getRegions().then(d => setRegions(Array.isArray(d) ? d : [])).catch(() => {});
    localisationApi.getDepartements().then(d => setDepartements(Array.isArray(d) ? d : [])).catch(() => {});
    localisationApi.getCommunes().then(d => setCommunes(Array.isArray(d) ? d : [])).catch(() => {});
  };
  useEffect(() => { loadLocalisation(); }, []);

  const loadPlannings = useCallback(async () => {
    setPlanningsLoading(true);
    try {
      const params = { mois: planningsMois };
      if (planningsSyndicatFilter) params.syndicatId = planningsSyndicatFilter;
      if (planningStatutFilter)    params.statut     = planningStatutFilter;
      const data = await adminApi.getAllPlannings(params);
      setPlannings(Array.isArray(data) ? data : []);
    } catch { setPlannings([]); }
    finally  { setPlanningsLoading(false); }
  }, [planningsMois, planningsSyndicatFilter, planningStatutFilter]);

  useEffect(() => { if (activeTab === "gardes") loadPlannings(); }, [activeTab, loadPlannings]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  // ── Actions syndicat ──
  const handleSuspendre = async (id) => {
    const motif = window.prompt("Motif de suspension :");
    if (motif === null) return;
    try {
      await adminApi.suspendreSyndicat(id, motif);
      loadAll();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  const handleReactiver = async (id) => {
    try {
      await adminApi.reactiverSyndicat(id);
      loadAll();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  const handleSupprimerSyndicat = async (s) => {
    if (!window.confirm(`Supprimer définitivement le syndicat "${s.nom}" ?\n\nSes pharmacies seront détachées mais pas supprimées.`)) return;
    try {
      await adminApi.deleteSyndicat(s.id);
      loadAll();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  const handleSupprimerPharmacie = async (p) => {
    if (!window.confirm(`Supprimer définitivement la pharmacie "${p.nom}" ?`)) return;
    try {
      await adminApi.deletePharmacie(p.id);
      loadAll();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  const handleSupprimerPharmacien = async (ph) => {
    if (!window.confirm(`Supprimer définitivement le pharmacien "${ph.prenom} ${ph.nom}" ?\n\nSes pharmacies seront détachées mais pas supprimées.`)) return;
    try {
      await adminApi.deletePharmacien(ph.id);
      loadAll();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  const handleDeleteLocalisation = async (type, item) => {
    const labels = { pays: "ce pays", region: "cette région", dep: "ce département", commune: "cette commune" };
    if (!window.confirm(`Supprimer définitivement ${labels[type]} "${item.nom}" ?`)) return;
    try {
      const apis = {
        pays:    { del: () => localisationApi.deletePays(item.id),        setter: setPays },
        region:  { del: () => localisationApi.deleteRegion(item.id),      setter: setRegions },
        dep:     { del: () => localisationApi.deleteDepartement(item.id), setter: setDepartements },
        commune: { del: () => localisationApi.deleteCommune(item.id),     setter: setCommunes },
      };
      await apis[type].del();
      apis[type].setter(prev => prev.filter(x => x.id !== item.id));
    } catch (e) { alert("Erreur : " + e.message); }
  };

  // Load available zones when syndicat type changes in the creation form
  const loadSyndicatZonesForType = async (type, targetSetter) => {
    setSyndicatZonesLoading(true);
    setSyndicatZones([]);
    setSyndicatZoneRegionId("");
    setSyndicatZoneDepId("");
    setSyndicatZoneDeps([]);
    try {
      if (type === "REGION") {
        const zones = await adminApi.getZonesDisponibles("REGION");
        setSyndicatZones(Array.isArray(zones) ? zones : []);
      } else if (type === "DEPARTEMENT") {
        const base = process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";
        const regRes = await fetch(`${base}/public/regions`);
        const regJson = await regRes.json();
        setSyndicatZoneRegions(Array.isArray(regJson) ? regJson : (regJson.data || []));
        const zones = await adminApi.getZonesDisponibles("DEPARTEMENT");
        setSyndicatZones(Array.isArray(zones) ? zones : []);
      } else if (type === "COMMUNE" || type === "ZONE") {
        const base = process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";
        const regRes = await fetch(`${base}/public/regions`);
        const regJson = await regRes.json();
        setSyndicatZoneRegions(Array.isArray(regJson) ? regJson : (regJson.data || []));
        const apiType = type === "ZONE" ? "ZONE" : "COMMUNE";
        const zones = await adminApi.getZonesDisponibles(apiType);
        setSyndicatZones(Array.isArray(zones) ? zones : []);
      }
    } catch { setSyndicatZones([]); }
    finally { setSyndicatZonesLoading(false); }
    if (targetSetter) targetSetter();
  };

  const handleSyndicatTypeChange = async (type) => {
    setNewSyndicat(prev => ({ ...prev, type, departementId: "", communeId: "", regionId: "", communeIds: [] }));
    await loadSyndicatZonesForType(type);
  };

  // When region is picked in syndicat cascade (DEPARTEMENT or COMMUNE)
  const handleSyndicatZoneRegionChange = async (regionId) => {
    setSyndicatZoneRegionId(regionId);
    setSyndicatZoneDepId("");
    setNewSyndicat(prev => ({ ...prev, departementId: "", communeId: "", communeIds: [] }));
    if (!regionId) { setSyndicatZoneDeps([]); setSyndicatZones([]); return; }
    try {
      const base = process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";
      // Charger les départements de la région
      const depRes = await fetch(`${base}/public/regions/${regionId}/departements`);
      const depJson = await depRes.json();
      setSyndicatZoneDeps(Array.isArray(depJson) ? depJson : (depJson.data || []));
      // Charger les communes de la région
      const comRes = await fetch(`${base}/public/regions/${regionId}/communes`);
      const comJson = await comRes.json();
      setSyndicatZones(Array.isArray(comJson) ? comJson : (comJson.data || []));
    } catch { setSyndicatZoneDeps([]); setSyndicatZones([]); }
  };

  const handleCreerSyndicat = async () => {
    try {
      await adminApi.createSyndicat({ ...newSyndicat });
      setShowNewSyndicatForm(false);
      setNewSyndicat({ nom: "", username: "", motDePasse: "", email: "", telephone: "", type: "DEPARTEMENT", departementId: "", communeId: "", regionId: "" });
      setSyndicatZones([]); setSyndicatZoneRegions([]); setSyndicatZoneDeps([]);
      setSyndicatZoneRegionId(""); setSyndicatZoneDepId("");
      loadAll();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  // ── Réseau actions ──
  const handleValiderPharmacie = async (id) => {
    setReseauLoading(true);
    try {
      await adminApi.validerPharmacie(id, userName);
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setReseauLoading(false); }
  };

  const handleRejeterPharmacie = async (id) => {
    const motif = window.prompt("Motif de rejet :");
    if (motif === null) return;
    setReseauLoading(true);
    try {
      await adminApi.rejeterPharmacie(id, motif);
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setReseauLoading(false); }
  };

  const ouvrirModalPharmacie = async () => {
    setShowAddPharmacieModal(true);
    if (regions.length === 0) {
      try {
        const base = process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";
        const res = await fetch(`${base}/public/regions`);
        const json = await res.json();
        setRegions(Array.isArray(json) ? json : (json.data || []));
      } catch { setRegions([]); }
    }
  };

  const handleRegionChange = async (regionId) => {
    setSelectedRegionId(regionId);
    setNewPharmacie(prev => ({ ...prev, communeId: "", syndicatId: "" }));
    setDetectedSyndicat(null);
    if (!regionId) { setCommunesList([]); return; }
    try {
      const base = process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";
      const res = await fetch(`${base}/public/regions/${regionId}/communes`);
      const json = await res.json();
      setCommunesList(Array.isArray(json) ? json : (json.data || []));
    } catch { setCommunesList([]); }
  };

  const handleCommuneChange = async (communeId) => {
    setNewPharmacie(prev => ({ ...prev, communeId, syndicatId: "" }));
    setDetectedSyndicat(null);
    if (!communeId) return;
    try {
      const detected = await adminApi.getSyndicatByCommune(communeId);
      if (detected && detected.id) {
        setDetectedSyndicat(detected);
        setNewPharmacie(prev => ({ ...prev, communeId, syndicatId: detected.id }));
      }
    } catch { /* aucun syndicat détecté, ce n'est pas bloquant */ }
  };

  const handleCreerPharmacie = async () => {
    const errs = {};
    if (!newPharmacie.nom.trim())          errs.nom          = "Le nom est obligatoire";
    if (!newPharmacie.adresseComplete.trim()) errs.adresseComplete = "L'adresse est obligatoire";
    if (!newPharmacie.telephone.trim())    errs.telephone    = "Le téléphone est obligatoire";
    if (!newPharmacie.communeId)           errs.communeId    = "La commune est obligatoire";
    if (!selectedRegionId)                 errs.regionId     = "La région est obligatoire";
    setErreursPharmacie(errs);
    if (Object.keys(errs).length > 0) return;
    try {
      const payload = {
        ...newPharmacie,
        latitude:  newPharmacie.latitude  ? parseFloat(newPharmacie.latitude)  : null,
        longitude: newPharmacie.longitude ? parseFloat(newPharmacie.longitude) : null,
        rayonLivraisonKm: newPharmacie.rayonLivraisonKm ? parseInt(newPharmacie.rayonLivraisonKm) : null,
        horaires: JSON.stringify(newPharmacieHoraires),
        pharmacienId: newPharmacie.pharmacienId || null,
        syndicatId:   newPharmacie.syndicatId   || null,
      };
      await adminApi.createPharmacie(payload);
      setShowAddPharmacieModal(false);
      setErreursPharmacie({});
      setNewPharmacie({ nom: "", adresseComplete: "", quartier: "", telephone: "", email: "", siteWeb: "", communeId: "", syndicatId: "", pharmacienId: "", latitude: "", longitude: "", numeroAgrementMinistere: "", numeroOrdre: "", description: "", services: "", accepteCommandes: true, proposeLivraison: false, rayonLivraisonKm: "" });
      setNewPharmacieHoraires(initHoraires());
      setSelectedRegionId(""); setCommunesList([]); setDetectedSyndicat(null);
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  const handleCreerPharmacien = async () => {
    const errs = {};
    if (!newPharmacien.nom.trim())        errs.nom       = "Le nom est obligatoire";
    if (!newPharmacien.prenom.trim())     errs.prenom    = "Le prénom est obligatoire";
    if (!newPharmacien.telephone.trim())  errs.telephone = "Le téléphone est obligatoire";
    setErreursPharmacien(errs);
    if (Object.keys(errs).length > 0) return;
    try {
      const payload = {
        ...newPharmacien,
        anneeDiplome: newPharmacien.anneeDiplome ? parseInt(newPharmacien.anneeDiplome) : null,
      };
      await adminApi.createPharmacien(payload);
      setShowAddPharmacienModal(false);
      setErreursPharmacien({});
      setNewPharmacien({ prenom: "", nom: "", email: "", telephone: "", dateNaissance: "", sexe: "M", numeroOrdreNational: "", universiteFormation: "", anneeDiplome: "" });
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  const handleAssignerPharmacienAPharmacie = async () => {
    if (!assignPharmacienTarget || !assignPharmacienId) return;
    try {
      await adminApi.updatePharmacie(assignPharmacienTarget.id, { pharmacienId: assignPharmacienId });
      setShowAssignPharmacienModal(false);
      setAssignPharmacienTarget(null);
      setAssignPharmacienId("");
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  const handleAssignerPharmacieAPharmacien = async () => {
    if (!assignPharmacieToPharmacienTarget || !assignPharmacieForPharmacienId) return;
    try {
      await adminApi.updatePharmacie(assignPharmacieForPharmacienId, { pharmacienId: assignPharmacieToPharmacienTarget.id });
      setShowAssignPharmacieToPharmacienModal(false);
      setAssignPharmacieToPharmacienTarget(null);
      setAssignPharmacieForPharmacienId("");
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  const handleAssignerPharmacie = async () => {
    if (!assignPharmacieId || !assignSyndicatId) return;
    try {
      await adminApi.assignerPharmacieAdmin(assignPharmacieId, assignSyndicatId);
      setShowAssignModal(false);
      setAssignPharmacieId(""); setAssignSyndicatId("");
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  // ── Paiements actions ──
  const ouvrirPaiementModal = (syndicat) => {
    setPaiementSyndicat(syndicat);
    setPaiementForm({ datePaiement: new Date().toISOString().slice(0,10), reference: "", notes: "" });
    setShowPaiementModal(true);
  };

  const handleEnregistrerPaiement = async () => {
    if (!paiementSyndicat) return;
    if (!paiementSyndicat._renouvelerMois && !paiementForm.datePaiement) return;
    if (!paiementForm.reference.trim()) return;
    setPaiementLoading(true);
    try {
      if (paiementSyndicat._renouvelerMois) {
        await adminApi.renouvelerAbonnement(paiementSyndicat.id, paiementSyndicat._renouvelerMois);
      } else {
        await adminApi.enregistrerPaiement(paiementSyndicat.id, paiementForm);
      }
      setShowPaiementModal(false);
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setPaiementLoading(false); }
  };

  const handleRenouveler = (id, mois = 1) => {
    const syndicat = syndicats.find(s => s.id === id);
    if (!syndicat) return;
    setPaiementSyndicat({ ...syndicat, _renouvelerMois: mois });
    setPaiementForm({ datePaiement: new Date().toISOString().slice(0, 10), reference: "", notes: "" });
    setShowPaiementModal(true);
  };

  // ── Paiements pharmacies ──
  const handleRenouvelerPharmacie = (id, nom) => {
    setPharmPaiementTarget({ id, nom, type: "renouveler" });
    setPharmPaiementRef("");
    setShowPharmPaiementModal(true);
  };

  const handleRelancerPharmacie = async (id) => {
    setRelancerLoading(id);
    try { await adminApi.relancerPharmacie(id); alert("E-mail de rappel envoyé !"); }
    catch (e) { alert("Erreur : " + e.message); }
    finally { setRelancerLoading(null); }
  };

  const handleRelancerSyndicat = async (id) => {
    setRelancerLoading(id);
    try { await adminApi.enregistrerPaiement(id, { notes: "Rappel automatique" }); alert("E-mail de rappel envoyé !"); }
    catch (e) { alert("Erreur : " + e.message); }
    finally { setRelancerLoading(null); }
  };

  const handleEnregistrerPaiementPharmacie = (id, nom) => {
    setPharmPaiementTarget({ id, nom, type: "enregistrer" });
    setPharmPaiementRef("");
    setShowPharmPaiementModal(true);
  };

  const handleConfirmPharmPaiement = async () => {
    if (!pharmPaiementTarget || !pharmPaiementRef.trim()) return;
    setPharmPaiementLoading(true);
    try {
      if (pharmPaiementTarget.type === "renouveler") {
        await adminApi.renouvelerAbonnementPharmacie(pharmPaiementTarget.id, 12, pharmPaiementRef.trim());
      } else {
        await adminApi.enregistrerPaiementPharmacie(pharmPaiementTarget.id, pharmPaiementRef.trim());
      }
      setShowPharmPaiementModal(false);
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setPharmPaiementLoading(false); }
  };

  // ── Actions pharmacies (activer/suspendre) ──
  const handleActiverPharmacie = async (id) => {
    try { await pharmacieApi.activer(id); loadAll(); }
    catch (e) { alert("Erreur : " + e.message); }
  };

  const handleSuspendrePharmacie = async (id) => {
    const motif = window.prompt("Motif de suspension (optionnel) :", "Non-paiement");
    if (motif === null) return;
    try { await pharmacieApi.suspendre(id, motif || "Non-paiement"); loadAll(); }
    catch (e) { alert("Erreur : " + e.message); }
  };

  // ── Sous-comptes syndicat ──
  const voirSousComptes = async (syndicat) => {
    setSelectedSyndicatForComptes(syndicat);
    setSousComptesLoading(true);
    try {
      const comptes = await adminApi.getComptesAssocies(syndicat.id);
      setSousComptes(Array.isArray(comptes) ? comptes : []);
    } catch { setSousComptes([]); }
    finally { setSousComptesLoading(false); }
  };

  const handleResetSousCompteMdp = async () => {
    if (resetMdpForm.mdp !== resetMdpForm.confirm) { alert("Les mots de passe ne correspondent pas"); return; }
    if (resetMdpForm.mdp.length < 6) { alert("Minimum 6 caractères"); return; }
    setResetMdpLoading(true);
    try {
      await adminApi.resetPasswordSyndicat(selectedSousCompte.id, resetMdpForm.mdp);
      alert("Mot de passe réinitialisé avec succès !");
      setShowResetSousCompteModal(false);
      setResetMdpForm({ mdp: "", confirm: "" });
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setResetMdpLoading(false); }
  };

  // ── Actions patients premium ──
  const openPatientPremiumModal = (patientId, mois = 1, nomPatient = "") => {
    setPatientPremiumTarget({ id: patientId, mois, nom: nomPatient });
    setPatientPremiumRef("");
    setShowPatientPremiumModal(true);
  };

  const handleConfirmPatientPremium = async () => {
    if (!patientPremiumTarget || !patientPremiumRef.trim()) return;
    setPatientPremiumLoading(true);
    try {
      await adminApi.activerPremiumPatient(patientPremiumTarget.id, patientPremiumTarget.mois, patientPremiumRef.trim());
      setShowPatientPremiumModal(false);
      loadAll();
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setPatientPremiumLoading(false); }
  };

  const handleSuspendrePatient = async (patientId) => {
    if (!window.confirm("Suspendre ce compte patient ?")) return;
    try { await adminApi.suspendrePatient(patientId); loadAll(); }
    catch (e) { alert("Erreur : " + e.message); }
  };

  const handleReactiverPatient = async (patientId) => {
    try { await adminApi.reactiverPatient(patientId); loadAll(); }
    catch (e) { alert("Erreur : " + e.message); }
  };

  const handleDeletePatient = async (patientId, nom) => {
    if (!window.confirm(`Supprimer définitivement le compte de "${nom}" ? Cette action est irréversible.`)) return;
    try {
      await adminApi.deletePatient(patientId);
      setPatients(prev => prev.filter(p => p.id !== patientId));
    } catch (e) { alert("Erreur : " + e.message); }
  };

  // ── Paiements manuels ──
  const loadPendingPayments = async () => {
    setPendingLoading(true);
    try { const data = await adminApi.getPendingPayments(); setPendingPayments(data || []); }
    catch (e) { console.error(e); alert("Erreur lors du chargement des demandes de paiement : " + e.message); }
    finally { setPendingLoading(false); }
  };

  const handleValiderPaiement = async (ref) => {
    if (!window.confirm("Valider ce paiement et activer l'abonnement ?")) return;
    try { await adminApi.validerPaiement(ref); loadPendingPayments(); loadAll(); }
    catch (e) { alert("Erreur : " + e.message); }
  };

  const handleRejeterPaiement = async (ref) => {
    const motif = window.prompt("Motif du rejet (optionnel) :");
    if (motif === null) return; // annulé
    try { await adminApi.rejeterPaiement(ref, motif); loadPendingPayments(); }
    catch (e) { alert("Erreur : " + e.message); }
  };

  // ── Répondre ticket ──
  const handleRepondreTicket = async () => {
    if (!reponseText.trim() || !selectedTicket) return;
    try {
      await adminApi.repondreTicket(selectedTicket.id, {
        reponse: reponseText,
        nouveauStatut: "RESOLU",
      });
      setSelectedTicket(null);
      setReponseText("");
      loadAll();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  // ── Stats calculées ──
  const syndicatsActifs   = syndicats.filter(s => s.statut === "ACTIF").length;
  const syndicatsSuspendu = syndicats.filter(s => s.statut === "SUSPENDU").length;
  const pharmaciesActives = pharmacies.filter(p => p.statut === "ACTIF" || p.actif).length;
  const ticketsOuverts    = ticketStats.ouverts || tickets.filter(t => t.statut === "OUVERT").length;

  // ── Handlers édition ──
  const handleEditSyndicat = (s) => {
    const zoneNom = s.nomZone || s.nomRegion || "";
    const typeZone = s.type || "";
    setEditSyndicatData({ id: s.id, nom: s.nom || "", email: s.email || "", telephone: s.telephone || "", nomResponsable: s.nomResponsable || "", telephoneResponsable: s.telephoneResponsable || "", zoneNom, typeZone });
    setShowEditSyndicatModal(true);
  };
  const submitEditSyndicat = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const payload = {
        nom: editSyndicatData.nom,
        email: editSyndicatData.email,
        telephone: editSyndicatData.telephone,
        nomResponsable: editSyndicatData.nomResponsable,
        telephoneResponsable: editSyndicatData.telephoneResponsable,
      };
      // Ajouter les champs de zone seulement si on a modifié la zone
      if (editZoneMode && editSyndicatData.newType) {
        payload.type = editSyndicatData.newType;
        if (editSyndicatData.newType === "COMMUNE" && editSyndicatData.newCommuneId)
          payload.communeId = editSyndicatData.newCommuneId;
        if (editSyndicatData.newType === "ZONE" && editSyndicatData.newCommuneIds?.length)
          payload.communeIds = editSyndicatData.newCommuneIds;
        if (editSyndicatData.newType === "DEPARTEMENT" && editSyndicatData.newDepartementId)
          payload.departementId = editSyndicatData.newDepartementId;
        if (editSyndicatData.newType === "REGION" && editSyndicatData.newRegionId)
          payload.regionId = editSyndicatData.newRegionId;
      }
      await adminApi.updateSyndicat(editSyndicatData.id, payload);
      setShowEditSyndicatModal(false);
      setEditZoneMode(false);
      setSyndicatZones([]); setSyndicatZoneRegions([]); setSyndicatZoneDeps([]);
      setSyndicatZoneRegionId(""); setSyndicatZoneDepId("");
      loadAll();
    }
    catch (err) { alert("Erreur : " + (err.message || "inconnue")); }
    finally { setEditLoading(false); }
  };

  const handleEditPharmacie = (p) => {
    let parsedHoraires = {};
    try {
      const raw = p.horaires;
      parsedHoraires = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : {};
    } catch { parsedHoraires = {}; }
    setEditPharmacieHoraires(parsedHoraires);
    setEditPharmacieData({
      id: p.id,
      nom: p.nom || "",
      adresseComplete: p.adresseComplete || p.adresse || "",
      quartier: p.quartier || "",
      telephone: p.telephone || "",
      telephoneSecondaire: p.telephoneSecondaire || "",
      email: p.email || "",
      siteWeb: p.siteWeb || "",
      latitude: p.latitude ?? "",
      longitude: p.longitude ?? "",
      description: p.description || "",
      services: p.services || "",
      accepteCommandes: p.accepteCommandes ?? false,
      proposeLivraison: p.proposeLivraison ?? false,
    });
    setShowEditPharmacieModal(true);
  };
  const submitEditPharmacie = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const payload = {
        ...editPharmacieData,
        latitude:  editPharmacieData.latitude  !== "" ? parseFloat(editPharmacieData.latitude)  : null,
        longitude: editPharmacieData.longitude !== "" ? parseFloat(editPharmacieData.longitude) : null,
        horaires: Object.keys(editPharmacieHoraires).length > 0 ? JSON.stringify(editPharmacieHoraires) : null,
      };
      await adminApi.updatePharmacie(payload.id, payload);
      setShowEditPharmacieModal(false);
      loadAll();
    }
    catch (err) { alert("Erreur : " + (err.message || "inconnue")); }
    finally { setEditLoading(false); }
  };

  const handleEditPharmacien = (ph) => {
    setEditPharmacienData({
      id: ph.id,
      prenom: ph.prenom || "",
      nom: ph.nom || "",
      email: ph.email || "",
      telephone: ph.telephone || "",
      numeroOrdreNational: ph.numeroOrdreNational || "",
      universiteFormation: ph.universiteFormation || "",
      anneeDiplome: ph.anneeDiplome || "",
      type: ph.type || "PROPRIETAIRE",
      actif: ph.actif ?? true,
    });
    setShowEditPharmacienModal(true);
  };
  const submitEditPharmacien = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await adminApi.updatePharmacien(editPharmacienData.id, editPharmacienData);
      setShowEditPharmacienModal(false);
      loadAll();
    }
    catch (err) { alert("Erreur : " + (err.message || "inconnue")); }
    finally { setEditLoading(false); }
  };

  // ── Filtres ──
  const filteredSyndicats = syndicats.filter(s =>
    s.nom?.toLowerCase().includes(searchSyndicat.toLowerCase()) ||
    s.code?.toLowerCase().includes(searchSyndicat.toLowerCase())
  );
  const filteredPharmacies = pharmacies.filter(p => {
    const q = searchPharmacie.toLowerCase();
    if (q && !p.nom?.toLowerCase().includes(q) && !p.communeNom?.toLowerCase().includes(q) &&
        !p.adresseComplete?.toLowerCase().includes(q) && !p.telephone?.includes(q)) return false;
    if (reseauFilterStatut && p.statut !== reseauFilterStatut) return false;
    if (reseauFilterCommune && p.communeId !== reseauFilterCommune) return false;
    if (reseauFilterDep) {
      const comm = communes.find(c => c.id === p.communeId);
      if (!comm || comm.departementId !== reseauFilterDep) return false;
    }
    if (reseauFilterRegion) {
      const comm = communes.find(c => c.id === p.communeId);
      const dep = comm ? departements.find(d => d.id === comm.departementId) : null;
      if (!dep || dep.regionId !== reseauFilterRegion) return false;
    }
    if (reseauFilterPays) {
      const comm = communes.find(c => c.id === p.communeId);
      const dep = comm ? departements.find(d => d.id === comm.departementId) : null;
      const reg = dep ? regions.find(r => r.id === dep.regionId) : null;
      if (!reg || reg.paysId !== reseauFilterPays) return false;
    }
    return true;
  });
  // Données paginées
  const paginatedSyndicats = filteredSyndicats.slice((syndicatPage - 1) * PAGE_SIZE, syndicatPage * PAGE_SIZE);
  const paginatedPharmacies = filteredPharmacies.slice((pharmaciePage - 1) * PAGE_SIZE, pharmaciePage * PAGE_SIZE);
  const paginatedPharmaciens = pharmaciens.slice((pharmacienPage - 1) * PAGE_SIZE, pharmacienPage * PAGE_SIZE);

  const filteredTickets = filterTicketStatut
    ? tickets.filter(t => t.statut === filterTicketStatut)
    : tickets;

  // ── Badge par tab ──
  const badgeFor = (id) => {
    if (id === "syndicats") return syndicats.length || null;
    if (id === "support")   return ticketsOuverts > 0 ? ticketsOuverts : null;
    if (id === "demandes")  { const n = demandes.filter(d => d.statut === "EN_ATTENTE").length; return n || null; }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">

      {/* Backdrop mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-20 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside className={`fixed top-0 left-0 h-full bg-white border-r border-gray-100 z-30 transition-all duration-300 flex flex-col shadow-sm
        ${sidebarOpen ? "translate-x-0 w-64" : "-translate-x-full md:translate-x-0 md:w-16"}`}>

        {/* Logo */}
        <div className={`flex items-center border-b border-gray-100 h-16 flex-shrink-0 ${sidebarOpen ? "px-5" : "px-3 justify-center"}`}>
          {sidebarOpen ? (
            <div>
              <img src={logoHorizontale} alt="SunuFarmasi" className="h-8 w-auto" />
              <span className="text-[10px] text-red-600 font-semibold tracking-wider uppercase flex items-center gap-1">
                <Shield size={9} /> Super Admin
              </span>
            </div>
          ) : (
            <img src={logoMobile} alt="SunuFarmasi" className="h-9 w-9" />
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {MENU_ITEMS.map(item => {
            const badge = badgeFor(item.id);
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); if (window.innerWidth < 768) setSidebarOpen(false); }}
                title={!sidebarOpen ? item.label : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium relative
                  ${isActive ? "bg-red-50 text-red-700" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"}
                  ${!sidebarOpen ? "justify-center" : ""}`}
              >
                {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-red-600 rounded-r-full" />}
                <item.icon size={18} className="flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {badge != null && (
                      <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                        {badge}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {/* User + Logout */}
        <div className="border-t border-gray-100 p-3 space-y-2 flex-shrink-0">
          <div className={`flex items-center gap-3 px-1 py-1 ${!sidebarOpen ? "justify-center" : ""}`}>
            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              {initials}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
                {userEmail && <p className="text-[11px] text-gray-400 truncate">{userEmail}</p>}
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            title={!sidebarOpen ? "Déconnexion" : undefined}
            className={`w-full flex items-center gap-3 px-3 py-2 text-red-500 hover:bg-red-50 rounded-xl transition-all text-sm ${!sidebarOpen ? "justify-center" : ""}`}
          >
            <LogOut size={16} className="flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className={`transition-all duration-300 flex flex-col min-h-screen ${sidebarOpen ? "md:ml-64" : "ml-0 md:ml-16"}`}>

        {/* Header */}
        <header className="bg-white sticky top-0 z-20 border-b border-gray-100 h-16 flex items-center">
          <div className="flex items-center justify-between px-3 md:px-6 w-full">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
                <Menu size={20} />
              </button>
              <div>
                <h1 className="text-base font-bold text-gray-900 leading-tight">
                  {MENU_ITEMS.find(m => m.id === activeTab)?.label || "Tableau de bord"}
                </h1>
                <p className="text-xs text-gray-400 flex items-center gap-1.5">
                  <Shield size={10} className="text-red-500" />
                  <span className="text-red-600 font-medium">Super Admin</span>
                  <span className="text-gray-300">·</span>
                  <span>{userName}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg">
                🇸🇳 <span>Sénégal</span>
              </span>
              <button onClick={loadAll}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors" title="Rafraîchir">
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                {initials}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 md:p-6">

          {/* ═══════════════════════════════════════════
              TABLEAU DE BORD
          ═══════════════════════════════════════════ */}
          {activeTab === "dashboard" && (
            <div className="space-y-6">
              {/* Stats cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Syndicats actifs",  value: syndicatsActifs,   icon: Building2,   color: "green",  sub: `${syndicats.length} total` },
                  { label: "Pharmacies",         value: pharmaciesActives, icon: Activity,    color: "blue",   sub: `${pharmacies.length} enregistrées` },
                  { label: "Tickets ouverts",    value: ticketsOuverts,    icon: TicketIcon,  color: "orange", sub: `${tickets.length} total` },
                  { label: "Patients inscrits",  value: patients.length,   icon: Users,       color: "purple", sub: "app mobile" },
                ].map((stat, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className={`w-10 h-10 rounded-lg bg-${stat.color}-100 flex items-center justify-center mb-3`}>
                      <stat.icon size={18} className={`text-${stat.color}-600`} />
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                    <p className="text-sm font-medium text-gray-700 mt-0.5">{stat.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stat.sub}</p>
                  </div>
                ))}
              </div>

              {/* Syndicats suspendus */}
              {syndicatsSuspendu > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0" />
                  <p className="text-red-700 text-sm">
                    <strong>{syndicatsSuspendu} syndicat(s)</strong> suspendu(s) — à réactiver.
                    <button onClick={() => setActiveTab("syndicats")} className="ml-2 underline font-medium">Voir</button>
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Tickets récents */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800 text-sm">Tickets récents</h2>
                    <button onClick={() => setActiveTab("support")} className="text-xs text-green-600 hover:underline flex items-center gap-1">
                      Voir tout <ChevronRight size={12} />
                    </button>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {tickets.slice(0, 5).map(ticket => (
                      <div key={ticket.id} className="p-4 flex items-center gap-3 hover:bg-gray-50">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <TicketIcon size={13} className="text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{ticket.sujet}</p>
                          <p className="text-xs text-gray-400">{ticket.syndicatNom}</p>
                        </div>
                        <StatutBadge statut={ticket.statut} />
                      </div>
                    ))}
                    {tickets.length === 0 && (
                      <p className="p-8 text-center text-gray-400 text-sm">Aucun ticket</p>
                    )}
                  </div>
                </div>

                {/* Syndicats */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800 text-sm">Syndicats ({syndicats.length})</h2>
                    <button onClick={() => setActiveTab("syndicats")} className="text-xs text-green-600 hover:underline flex items-center gap-1">
                      Gérer <ChevronRight size={12} />
                    </button>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {syndicats.slice(0, 5).map(s => (
                      <div key={s.id} className="p-4 flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 size={13} className="text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{s.nom}</p>
                          <p className="text-xs text-gray-400">{s.code} · {s.nomZone || s.username}</p>
                        </div>
                        <StatutBadge statut={s.statut} />
                      </div>
                    ))}
                    {syndicats.length === 0 && (
                      <p className="p-8 text-center text-gray-400 text-sm">Aucun syndicat</p>
                    )}
                  </div>
                </div>

                {/* Réseau + Patients */}
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                      <h2 className="font-semibold text-gray-800 text-sm">Pharmacies</h2>
                      <button onClick={() => setActiveTab("reseau")} className="text-xs text-green-600 hover:underline flex items-center gap-1">Voir <ChevronRight size={12}/></button>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-3">
                      {[
                        { label: "Validées", value: pharmacies.filter(p=>p.statut==="VALIDEE").length, color: "text-green-600" },
                        { label: "En attente", value: pharmaciesEnAttente.length, color: "text-orange-600" },
                        { label: "Total", value: pharmacies.length, color: "text-blue-600" },
                        { label: "Pharmaciens", value: pharmaciens.length, color: "text-purple-600" },
                      ].map((s, i) => (
                        <div key={i} className="text-center p-2 bg-gray-50 rounded-lg">
                          <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                      <h2 className="font-semibold text-gray-800 text-sm">Patients app</h2>
                      <button onClick={() => setActiveTab("patients")} className="text-xs text-green-600 hover:underline flex items-center gap-1">Voir <ChevronRight size={12}/></button>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-3">
                      {[
                        { label: "Total", value: patients.length, color: "text-gray-800" },
                        { label: "Premium", value: patients.filter(p=>p.premiumActif).length, color: "text-yellow-600" },
                        { label: "Actifs", value: patients.filter(p=>p.actif).length, color: "text-green-600" },
                        { label: "Suspendus", value: patients.filter(p=>!p.actif).length, color: "text-red-500" },
                      ].map((s, i) => (
                        <div key={i} className="text-center p-2 bg-gray-50 rounded-lg">
                          <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Alertes & Actions rapides */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h2 className="font-semibold text-gray-800 text-sm mb-4 flex items-center gap-2">
                    <AlertCircle size={15} className="text-orange-500" /> Actions à traiter
                  </h2>
                  <div className="space-y-3">
                    {pharmaciesEnAttente.length > 0 && (
                      <button onClick={() => setActiveTab("reseau")} className="w-full flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors">
                        <Building2 size={16} className="text-orange-600 flex-shrink-0" />
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-orange-800">{pharmaciesEnAttente.length} pharmacie(s) en attente</p>
                          <p className="text-xs text-orange-600">Validation requise → paiement 2 000 FCFA</p>
                        </div>
                        <ChevronRight size={14} className="text-orange-400" />
                      </button>
                    )}
                    {tickets.filter(t => t.statut === "OUVERT").length > 0 && (
                      <button onClick={() => setActiveTab("support")} className="w-full flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                        <TicketIcon size={16} className="text-blue-600 flex-shrink-0" />
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-blue-800">{tickets.filter(t => t.statut === "OUVERT").length} ticket(s) sans réponse</p>
                          <p className="text-xs text-blue-600">Répondre aux syndicats</p>
                        </div>
                        <ChevronRight size={14} className="text-blue-400" />
                      </button>
                    )}
                    {syndicats.filter(s => s.dateFinAbonnement && new Date(s.dateFinAbonnement) < new Date()).length > 0 && (
                      <button onClick={() => setActiveTab("paiements")} className="w-full flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                        <CreditCard size={16} className="text-red-600 flex-shrink-0" />
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-red-800">{syndicats.filter(s => s.dateFinAbonnement && new Date(s.dateFinAbonnement) < new Date()).length} abonnement(s) expiré(s)</p>
                          <p className="text-xs text-red-600">Renouveler ou suspendre</p>
                        </div>
                        <ChevronRight size={14} className="text-red-400" />
                      </button>
                    )}
                    {pharmaciesEnAttente.length === 0 && tickets.filter(t => t.statut === "OUVERT").length === 0 && syndicats.filter(s => s.dateFinAbonnement && new Date(s.dateFinAbonnement) < new Date()).length === 0 && (
                      <div className="text-center py-6">
                        <CheckCircle size={32} className="text-green-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Aucune action urgente</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h2 className="font-semibold text-gray-800 text-sm mb-4">Navigation rapide</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "Syndicats", icon: Building2, tab: "syndicats", color: "green" },
                      { label: "Réseau", icon: Activity, tab: "reseau", color: "blue" },
                      { label: "Support", icon: TicketIcon, tab: "support", color: "orange" },
                      { label: "Patients", icon: Users, tab: "patients", color: "purple" },
                      { label: "Paiements", icon: CreditCard, tab: "paiements", color: "green" },
                      { label: "Paramètres", icon: Settings, tab: "parametres", color: "gray" },
                    ].map((nav, i) => {
                      const Icon = nav.icon;
                      return (
                        <button key={i} onClick={() => setActiveTab(nav.tab)}
                          className="flex items-center gap-2 p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                          <Icon size={16} className={`text-${nav.color}-600`} />
                          <span className="text-gray-700 font-medium">{nav.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════
              SYNDICATS
          ═══════════════════════════════════════════ */}
          {activeTab === "syndicats" && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher par nom ou code..."
                    value={searchSyndicat}
                    onChange={e => setSearchSyndicat(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <button
                  onClick={() => setShowNewSyndicatForm(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-all"
                >
                  <Plus size={15} />
                  Nouveau syndicat
                </button>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {["Code", "Nom / Email", "Zone", "Pharmacies", "Plan", "Statut", "Actions", ""].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedSyndicats.map(s => (
                      <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">{s.code}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{s.nom}</p>
                          <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                            <Mail size={10} />{s.email}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {(() => {
                            const zoneName = s.nomZone || s.nomRegion || null;
                            const typeInfo = {
                              COMMUNE:     { label: "Commune",     cls: "bg-blue-100 text-blue-700" },
                              DEPARTEMENT: { label: "Département", cls: "bg-teal-100 text-teal-700" },
                              REGION:      { label: "Région",      cls: "bg-indigo-100 text-indigo-700" },
                            }[s.type] || null;
                            return zoneName ? (
                              <div className="flex flex-col gap-0.5">
                                {typeInfo && (
                                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold w-fit ${typeInfo.cls}`}>
                                    {typeInfo.label}
                                  </span>
                                )}
                                <span className="text-gray-700 font-medium">{zoneName}</span>
                              </div>
                            ) : <span className="text-gray-400">—</span>;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-center font-semibold text-gray-700">{s.nombrePharmacies ?? 0}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">{s.plan || "—"}</span>
                        </td>
                        <td className="px-4 py-3"><StatutBadge statut={s.statut} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {s.statut === "ACTIF" ? (
                              <button onClick={() => handleSuspendre(s.id)}
                                className="px-2.5 py-1 bg-red-100 text-red-600 rounded-lg text-xs hover:bg-red-200 font-medium">
                                Suspendre
                              </button>
                            ) : (
                              <button onClick={() => handleReactiver(s.id)}
                                className="px-2.5 py-1 bg-green-100 text-green-600 rounded-lg text-xs hover:bg-green-200 font-medium">
                                Réactiver
                              </button>
                            )}
                            <button onClick={() => voirSousComptes(s)}
                              className="px-2.5 py-1 border border-blue-200 text-blue-600 rounded-lg text-xs hover:bg-blue-50 font-medium flex items-center gap-1">
                              <Users size={10}/> Comptes
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleEditSyndicat(s)}
                              className="flex items-center gap-1 px-2.5 py-1 border border-gray-200 text-gray-600 rounded-lg text-xs hover:bg-gray-50">
                              <Pencil size={10}/> Modifier
                            </button>
                            <button onClick={() => handleSupprimerSyndicat(s)}
                              className="flex items-center gap-1 px-2.5 py-1 bg-red-50 border border-red-200 text-red-600 rounded-lg text-xs hover:bg-red-100">
                              <Trash2 size={10}/> Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
                {filteredSyndicats.length === 0 && (
                  <p className="text-center py-12 text-gray-400 text-sm">Aucun syndicat trouvé</p>
                )}
                <Pagination total={filteredSyndicats.length} page={syndicatPage} setPage={setSyndicatPage} />
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════
              GESTION DU RÉSEAU (Pharmacies + Pharmaciens)
          ═══════════════════════════════════════════ */}
          {activeTab === "reseau" && (
            <div className="space-y-5">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: "Total pharmacies",   value: pharmacies.length,                     color: "blue" },
                  { label: "Actives",             value: pharmacies.filter(p=>p.statut==="ACTIVE").length, color: "green" },
                  { label: "Suspendues",          value: pharmacies.filter(p=>p.statut==="SUSPENDUE").length, color: "orange" },
                  { label: "Pharmaciens",         value: pharmaciens.length,                    color: "purple" },
                ].map((s, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
                    <p className="text-2xl font-bold text-gray-800">{s.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Actions rapides */}
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={ouvrirModalPharmacie} className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
                  <Plus size={15} /> Ajouter une pharmacie
                </button>
                <button onClick={() => setShowAddPharmacienModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                  <Plus size={15} /> Ajouter un pharmacien
                </button>
                <button onClick={() => setShowAssignModal(true)} className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                  <Activity size={15} /> Assigner pharmacie → syndicat
                </button>
              </div>

              {/* Pharmacies en attente de validation */}
              {pharmaciesEnAttente.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-orange-200 flex items-center gap-2">
                    <AlertCircle size={16} className="text-orange-600" />
                    <h3 className="font-semibold text-orange-800 text-sm">En attente de validation ({pharmaciesEnAttente.length})</h3>
                  </div>
                  <div className="divide-y divide-orange-100">
                    {pharmaciesEnAttente.map(p => (
                      <div key={p.id} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{p.nom}</p>
                          <p className="text-xs text-gray-500">{p.adresseComplete || p.adresse || "—"} · {p.communeNom || "—"}</p>
                          {p.telephone && <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><Phone size={10}/>{p.telephone}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleValiderPharmacie(p.id)} disabled={reseauLoading}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50">
                            <CheckCircle size={12}/> Valider
                          </button>
                          <button onClick={() => handleRejeterPharmacie(p.id)} disabled={reseauLoading}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-600 text-xs font-medium rounded-lg hover:bg-red-200 disabled:opacity-50">
                            <XCircle size={12}/> Rejeter
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sub-tabs */}
              <div className="flex gap-2 border-b border-gray-200">
                {[{ id: "pharmacies", label: `Pharmacies (${pharmacies.length})` }, { id: "pharmaciens", label: `Pharmaciens (${pharmaciens.length})` }].map(t => (
                  <button key={t.id} onClick={() => setReseauTab(t.id)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${reseauTab === t.id ? "border-green-600 text-green-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Toutes les pharmacies */}
              {reseauTab === "pharmacies" && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-100 space-y-3">
                    {/* Ligne 1 : Recherche + Statut */}
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-[180px] px-3 py-2 border-2 border-gray-200 rounded-xl focus-within:border-green-400 transition-colors bg-white">
                        <Search size={13} className="text-gray-400 flex-shrink-0"/>
                        <input type="text" placeholder="Rechercher une pharmacie..." value={searchPharmacie}
                          onChange={e => { setSearchPharmacie(e.target.value); setPharmaciePage(1); }}
                          className="flex-1 text-sm outline-none bg-transparent"/>
                        {searchPharmacie && <button onClick={() => setSearchPharmacie("")}><X size={12} className="text-gray-400"/></button>}
                      </div>
                      <select value={reseauFilterStatut} onChange={e => { setReseauFilterStatut(e.target.value); setPharmaciePage(1); }}
                        className={`px-3 py-2 border-2 rounded-xl text-sm bg-white focus:outline-none transition-all ${reseauFilterStatut ? "border-green-400 text-green-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Tous les statuts</option>
                        <option value="ACTIVE">✅ Active</option>
                        <option value="VALIDEE">🔵 Validée</option>
                        <option value="EN_ATTENTE">🟠 En attente</option>
                        <option value="SUSPENDUE">🔴 Suspendue</option>
                        <option value="REJETEE">❌ Rejetée</option>
                      </select>
                    </div>
                    {/* Ligne 2 : Filtres géographiques en cascade */}
                    <div className="flex flex-wrap gap-2">
                      <select value={reseauFilterPays} onChange={e => { setReseauFilterPays(e.target.value); setReseauFilterRegion(""); setReseauFilterDep(""); setReseauFilterCommune(""); setPharmaciePage(1); }}
                        className={`px-3 py-2 border-2 rounded-xl text-sm bg-white focus:outline-none transition-all ${reseauFilterPays ? "border-teal-400 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Tous les pays</option>
                        {pays.map(p => <option key={p.id} value={p.id}>{p.drapeau} {p.nom}</option>)}
                      </select>
                      <select value={reseauFilterRegion} onChange={e => { setReseauFilterRegion(e.target.value); setReseauFilterDep(""); setReseauFilterCommune(""); setPharmaciePage(1); }}
                        className={`px-3 py-2 border-2 rounded-xl text-sm bg-white focus:outline-none transition-all ${reseauFilterRegion ? "border-teal-400 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Toutes les régions</option>
                        {(reseauFilterPays ? regions.filter(r=>r.paysId===reseauFilterPays) : regions).map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                      </select>
                      <select value={reseauFilterDep} onChange={e => { setReseauFilterDep(e.target.value); setReseauFilterCommune(""); setPharmaciePage(1); }}
                        className={`px-3 py-2 border-2 rounded-xl text-sm bg-white focus:outline-none transition-all ${reseauFilterDep ? "border-teal-400 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Tous les dép.</option>
                        {(reseauFilterRegion ? departements.filter(d=>d.regionId===reseauFilterRegion) : reseauFilterPays ? departements.filter(d=>regions.find(r=>r.id===d.regionId)?.paysId===reseauFilterPays) : departements).map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                      </select>
                      <select value={reseauFilterCommune} onChange={e => { setReseauFilterCommune(e.target.value); setPharmaciePage(1); }}
                        className={`px-3 py-2 border-2 rounded-xl text-sm bg-white focus:outline-none transition-all ${reseauFilterCommune ? "border-teal-400 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Toutes les communes</option>
                        {(reseauFilterDep ? communes.filter(c=>c.departementId===reseauFilterDep) : reseauFilterRegion ? communes.filter(c=>departements.find(d=>d.id===c.departementId)?.regionId===reseauFilterRegion) : communes).map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                      </select>
                      {(searchPharmacie || reseauFilterStatut || reseauFilterPays || reseauFilterRegion || reseauFilterDep || reseauFilterCommune) && (
                        <button onClick={() => { setSearchPharmacie(""); setReseauFilterStatut(""); setReseauFilterPays(""); setReseauFilterRegion(""); setReseauFilterDep(""); setReseauFilterCommune(""); setPharmaciePage(1); }}
                          className="flex items-center gap-1 px-3 py-2 border-2 border-gray-200 text-gray-400 rounded-xl text-xs font-semibold hover:text-gray-600 bg-white">
                          <X size={12}/> Reset
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{filteredPharmacies.length} pharmacie{filteredPharmacies.length !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="overflow-x-auto"><table className="w-full text-sm min-w-[750px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {["Pharmacie", "Localisation", "Syndicat", "Pharmacien", "Contact", "Statut", "Actions"].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedPharmacies.map(p => {
                        const hasPharmacien = !!p.pharmacienProprietaireId;
                        const pharmExpiry = p.dateFinAbonnement ? new Date(p.dateFinAbonnement) : null;
                        const pharmExpired = pharmExpiry && pharmExpiry < new Date();
                        const pharmSoon = pharmExpiry && !pharmExpired && (pharmExpiry - new Date()) < 30 * 86400000;
                        return (
                          <tr key={p.id} className={`hover:bg-gray-50 ${p.statut === "SUSPENDUE" ? "bg-red-50/20" : ""}`}>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-sm font-bold">{(p.nom || "?")[0].toUpperCase()}</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-800">{p.nom}</p>
                                  <p className="text-xs text-gray-400">{p.adresseComplete || p.adresse || ""}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="space-y-0.5">
                                <span className="flex items-center gap-1 text-gray-700 text-sm font-medium">
                                  <MapPin size={12} className="text-gray-400"/>{p.communeNom || p.commune || "—"}
                                </span>
                                {(() => {
                                  const comm = communes.find(c => c.id === p.communeId);
                                  const dep = comm ? departements.find(d => d.id === comm.departementId) : null;
                                  const reg = dep ? regions.find(r => r.id === dep.regionId) : null;
                                  return dep || reg ? (
                                    <p className="text-xs text-gray-400">{dep?.nom}{reg ? ` · ${reg.nom}` : ""}</p>
                                  ) : null;
                                })()}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {p.syndicatNom ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg border border-blue-100">
                                  <Building2 size={10}/> {p.syndicatNom}
                                </span>
                              ) : (
                                <button onClick={() => { setAssignPharmacieId(p.id); setAssignSyndicatId(""); setShowAssignModal(true); }}
                                  className="flex items-center gap-1.5 px-2.5 py-1 border border-dashed border-orange-300 text-orange-500 text-xs rounded-lg hover:bg-orange-50 transition-colors">
                                  <Plus size={11}/> Assigner
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              {hasPharmacien ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Users size={13} className="text-blue-600"/>
                                  </div>
                                  <span className="text-sm text-gray-700 font-medium">{p.pharmacienProprietaireNom}</span>
                                </div>
                              ) : (
                                <button onClick={() => { setAssignPharmacienTarget(p); setAssignPharmacienId(""); setShowAssignPharmacienModal(true); }}
                                  className="flex items-center gap-1.5 px-2.5 py-1 border border-dashed border-blue-300 text-blue-500 text-xs rounded-lg hover:bg-blue-50 transition-colors">
                                  <Plus size={11}/> Assigner
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-4 text-gray-600 text-sm">
                              {p.telephone ? <span className="flex items-center gap-1"><Phone size={12}/>{p.telephone}</span> : "—"}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex flex-col gap-1">
                                <PharmacieStatutBadge statut={p.statut} />
                                {p.statut === "ACTIVE" && pharmExpiry && (
                                  <span className={`text-xs font-medium ${pharmExpired ? "text-red-500" : pharmSoon ? "text-amber-500" : "text-gray-400"}`}>
                                    exp. {pharmExpiry.toLocaleDateString("fr-FR")}
                                  </span>
                                )}
                                {p.statut === "ACTIVE" && p.dateDernierPaiement && (
                                  <span className="text-xs text-gray-400">payé {new Date(p.dateDernierPaiement).toLocaleDateString("fr-FR")}</span>
                                )}
                                {!hasPharmacien && (
                                  <span className="inline-flex items-center gap-1 text-xs text-yellow-600 font-medium">
                                    <AlertCircle size={10}/> Incomplet
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => setSelectedPharmacieDetail(p)} className="p-1.5 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 transition-colors" title="Détail">
                                  <Eye size={14}/>
                                </button>
                                <button onClick={() => handleEditPharmacie(p)} className="p-1.5 border border-blue-200 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors" title="Modifier">
                                  <Pencil size={14}/>
                                </button>
                                {p.statut !== "ACTIVE" && hasPharmacien && (
                                  <button onClick={() => handleActiverPharmacie(p.id)}
                                    className="px-2 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">
                                    Activer
                                  </button>
                                )}
                                {p.statut === "ACTIVE" && (
                                  <button onClick={() => handleSuspendrePharmacie(p.id)}
                                    className="p-1.5 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors" title="Suspendre">
                                    <XCircle size={14}/>
                                  </button>
                                )}
                                <button onClick={() => handleSupprimerPharmacie(p)}
                                  className="p-1.5 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors" title="Supprimer">
                                  <Trash2 size={14}/>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredPharmacies.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">Aucune pharmacie</td></tr>
                      )}
                    </tbody>
                  </table></div>
                  <Pagination total={filteredPharmacies.length} page={pharmaciePage} setPage={setPharmaciePage} />
                </div>
              )}

              {/* Pharmaciens */}
              {reseauTab === "pharmaciens" && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto"><table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {["Pharmacien", "Formation", "N° Ordre", "Pharmacie", "Statut", "Actions"].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedPharmaciens.map(ph => {
                        const pharmacieLiee = pharmacies.find(p => p.pharmacienProprietaireId === ph.id);
                        return (
                          <tr key={ph.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-sm font-bold">{((ph.prenom || "?")[0] + (ph.nom || "?")[0]).toUpperCase()}</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-800">Dr. {ph.prenom} {ph.nom}</p>
                                  <p className="text-xs text-gray-400">{ph.email || ""}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {ph.universiteFormation ? (
                                <div>
                                  <p className="text-sm text-gray-700 font-medium">{ph.universiteFormation}</p>
                                  {ph.anneeDiplome && <p className="text-xs text-gray-400">Diplôme {ph.anneeDiplome}</p>}
                                </div>
                              ) : <span className="text-gray-400 text-sm">—</span>}
                            </td>
                            <td className="px-4 py-4 text-gray-500 text-sm font-mono">{ph.numeroOrdreNational || "—"}</td>
                            <td className="px-4 py-4">
                              {pharmacieLiee ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-green-700 text-xs font-bold">{(pharmacieLiee.nom || "?")[0]}</span>
                                  </div>
                                  <span className="text-sm text-gray-700 font-medium truncate max-w-[140px]">{pharmacieLiee.nom}</span>
                                </div>
                              ) : (
                                <button onClick={() => { setAssignPharmacieToPharmacienTarget(ph); setAssignPharmacieForPharmacienId(""); setShowAssignPharmacieToPharmacienModal(true); }}
                                  className="flex items-center gap-1.5 px-2.5 py-1 border border-dashed border-green-300 text-green-600 text-xs rounded-lg hover:bg-green-50 transition-colors">
                                  <Plus size={11}/> Assigner
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <StatutBadge statut={ph.actif ? "ACTIF" : "INACTIF"} />
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => setSelectedPharmacienDetail(ph)} className="p-1.5 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 transition-colors" title="Détail">
                                  <Eye size={14}/>
                                </button>
                                <button onClick={() => handleEditPharmacien(ph)} className="p-1.5 border border-purple-200 text-purple-500 rounded-lg hover:bg-purple-50 transition-colors" title="Modifier">
                                  <Pencil size={14}/>
                                </button>
                                <button onClick={() => handleSupprimerPharmacien(ph)}
                                  className="p-1.5 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors" title="Supprimer">
                                  <Trash2 size={14}/>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {pharmaciens.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">Aucun pharmacien enregistré</td></tr>
                      )}
                    </tbody>
                  </table></div>
                  <Pagination total={pharmaciens.length} page={pharmacienPage} setPage={setPharmacienPage} />
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════
              PLANNING DE GARDE
          ═══════════════════════════════════════════ */}
          {activeTab === "gardes" && (() => {
            const MOIS_NOMS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
            const [moisY, moisM] = planningsMois.split("-").map(Number);
            const moisLabel = `${MOIS_NOMS[moisM - 1]} ${moisY}`;
            const statutPlanningStyle = (s) => ({
              BROUILLON:     "bg-gray-100 text-gray-600",
              EN_VALIDATION: "bg-yellow-100 text-yellow-700",
              VALIDE:        "bg-blue-100 text-blue-700",
              PUBLIE:        "bg-green-100 text-green-700",
              ARCHIVE:       "bg-gray-100 text-gray-500",
            }[s] || "bg-gray-100 text-gray-500");
            const prevMois = () => {
              const d = new Date(moisY, moisM - 2, 1);
              setPlanningsMois(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
            };
            const nextMois = () => {
              const d = new Date(moisY, moisM, 1);
              setPlanningsMois(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
            };
            const fmtDate = (s) => s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";
            return (
              <div className="space-y-5">
                {/* Barre de filtres */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    {/* Navigateur de mois (calendrier) */}
                    <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-1 py-1">
                      <button onClick={prevMois} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-600">
                        <ChevronLeft size={16} />
                      </button>
                      <span className="min-w-[140px] text-center text-sm font-semibold text-gray-800 px-2 select-none">
                        <Calendar size={14} className="inline mr-1.5 text-green-600 -mt-0.5" />{moisLabel}
                      </span>
                      <button onClick={nextMois} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-600">
                        <ChevronRight size={16} />
                      </button>
                    </div>

                    {/* Filtre syndicat */}
                    <select
                      value={planningsSyndicatFilter}
                      onChange={e => setPlanningsSyndicatFilter(e.target.value)}
                      className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                    >
                      <option value="">Tous les syndicats</option>
                      {syndicats.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                    </select>

                    {/* Filtre statut */}
                    <div className="flex gap-1.5 flex-wrap">
                      {[["", "Tous"], ["BROUILLON","Brouillon"], ["EN_VALIDATION","En validation"], ["VALIDE","Validé"], ["PUBLIE","Publié"]].map(([v, l]) => (
                        <button
                          key={v}
                          onClick={() => setPlanningStatutFilter(v)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${planningStatutFilter === v ? "bg-green-600 text-white border-green-600" : "bg-white border-gray-200 text-gray-600 hover:border-green-400"}`}
                        >{l}</button>
                      ))}
                    </div>

                    <div className="ml-auto flex items-center gap-2">
                      <span className="text-sm text-gray-500">{plannings.length} planning(s)</span>
                      <button onClick={loadPlannings} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Actualiser">
                        <RefreshCw size={15} className={`text-gray-500 ${planningsLoading ? "animate-spin" : ""}`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Planning cards */}
                {planningsLoading ? (
                  <div className="flex justify-center py-16">
                    <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : plannings.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center">
                    <Calendar size={40} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">Aucun planning pour {moisLabel}</p>
                    <p className="text-gray-300 text-sm mt-1">Naviguez vers un autre mois ou modifiez les filtres</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...plannings].sort((a, b) => new Date(b.dateDebut) - new Date(a.dateDebut)).map(p => {
                      const today = new Date();
                      const debut = new Date(p.dateDebut);
                      const fin   = new Date(p.dateFin);
                      fin.setHours(23, 59, 59);
                      const isEnCours = today >= debut && today <= fin && p.statut === "PUBLIE";
                      return (
                      <div key={p.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isEnCours ? "border-green-400 ring-1 ring-green-200" : "border-gray-200"}`}>
                        {/* Bandeau "en cours" */}
                        {isEnCours && (
                          <div className="bg-green-600 text-white text-xs font-semibold px-4 py-1.5 flex items-center gap-2">
                            <span className="w-2 h-2 bg-white rounded-full animate-pulse inline-block" />
                            Planning en cours cette semaine
                          </div>
                        )}
                        {/* En-tête planning */}
                        <button
                          className="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                          onClick={() => setExpandedPlanningId(expandedPlanningId === p.id ? null : p.id)}
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isEnCours ? "bg-green-100 border border-green-200" : "bg-gray-100 border border-gray-200"}`}>
                              <Building2 size={20} className={isEnCours ? "text-green-600" : "text-gray-500"} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-base text-gray-900 truncate">{p.titre || "Planning sans titre"}</p>
                              <p className="text-sm text-gray-600 mt-0.5 font-medium">
                                {p.syndicatNom || "Syndicat inconnu"}
                              </p>
                              {p.syndicatRegion && (
                                <p className="text-xs text-gray-400">{p.syndicatRegion}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-4">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold px-3 py-1 rounded-full ${statutPlanningStyle(p.statut)}`}>
                                {p.statutLibelle || p.statut}
                              </span>
                              <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${expandedPlanningId === p.id ? "rotate-180" : ""}`} />
                            </div>
                            <div className="flex items-center gap-1.5 text-sm text-gray-500">
                              <Calendar size={13} className="text-gray-400" />
                              {fmtDate(p.dateDebut)} → {fmtDate(p.dateFin)}
                            </div>
                            <span className="text-xs text-gray-400">{p.nombreGardes} pharmacie(s)</span>
                          </div>
                        </button>

                        {/* Liste pharmacies (expandable) — grille compacte */}
                        {expandedPlanningId === p.id && (
                          <div className="border-t border-gray-100 px-5 py-4">
                            {(!p.gardes || p.gardes.length === 0) ? (
                              <p className="py-3 text-sm text-gray-400 italic text-center">Aucune pharmacie dans ce planning</p>
                            ) : (
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {p.gardes.map((g, idx) => {
                                  const statutStyle = {
                                    PLANIFIEE: "border-blue-200 bg-blue-50 text-blue-800",
                                    EN_COURS:  "border-yellow-200 bg-yellow-50 text-yellow-800",
                                    TERMINEE:  "border-gray-200 bg-gray-50 text-gray-500",
                                    ANNULEE:   "border-red-200 bg-red-50 text-red-700 line-through",
                                  }[g.statut] || "border-gray-200 bg-gray-50 text-gray-600";
                                  return (
                                    <div key={g.id || idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${statutStyle}`}>
                                      <MapPin size={12} className="flex-shrink-0 opacity-60" />
                                      <div className="min-w-0">
                                        <p className="font-medium truncate">{g.pharmacieNom || "—"}</p>
                                        {g.zone && <p className="text-xs opacity-70 truncate">{g.zone}</p>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ═══════════════════════════════════════════
              SUPPORT (Tickets)
          ═══════════════════════════════════════════ */}
          {activeTab === "support" && (
            <div className="space-y-5">
              {/* Stats */}
              <div className="grid grid-cols-5 gap-3">
                {[
                  { label: "Total",    value: ticketStats.total   || tickets.length,                          color: "gray" },
                  { label: "Ouverts",  value: ticketStats.ouverts || tickets.filter(t=>t.statut==="OUVERT").length,   color: "blue" },
                  { label: "En cours", value: ticketStats.enCours || tickets.filter(t=>t.statut==="EN_COURS").length, color: "yellow" },
                  { label: "Résolus",  value: ticketStats.resolus || tickets.filter(t=>t.statut==="RESOLU").length,   color: "green" },
                  { label: "Fermés",   value: ticketStats.fermes  || tickets.filter(t=>t.statut==="FERME").length,    color: "gray" },
                ].map((s, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 text-center shadow-sm">
                    <p className="text-2xl font-bold text-gray-800">{s.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Filtres */}
              <div className="flex gap-2 flex-wrap">
                {["", "OUVERT", "EN_COURS", "RESOLU", "FERME"].map(s => (
                  <button
                    key={s}
                    onClick={() => setFilterTicketStatut(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filterTicketStatut === s
                        ? "bg-green-600 text-white"
                        : "bg-white border border-gray-200 text-gray-600 hover:border-green-400"
                    }`}
                  >
                    {s || "Tous"}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-5">
                {/* Liste */}
                <div className="col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="divide-y divide-gray-100">
                    {filteredTickets.map(ticket => (
                      <div
                        key={ticket.id}
                        onClick={() => setSelectedTicket(ticket)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedTicket?.id === ticket.id ? "bg-green-50 border-l-2 border-green-500" : ""
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <PrioriteBadge priorite={ticket.priorite} />
                              <StatutBadge statut={ticket.statut} />
                            </div>
                            <p className="font-medium text-gray-800 text-sm truncate">{ticket.sujet}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{ticket.syndicatNom}</p>
                          </div>
                          <p className="text-xs text-gray-400 flex-shrink-0">
                            {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString("fr-FR") : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                    {filteredTickets.length === 0 && (
                      <p className="p-12 text-center text-gray-400 text-sm">Aucun ticket</p>
                    )}
                  </div>
                </div>

                {/* Détail + réponse */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col">
                  {selectedTicket ? (
                    <div className="space-y-4 flex-1 flex flex-col">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <PrioriteBadge priorite={selectedTicket.priorite} />
                          <StatutBadge statut={selectedTicket.statut} />
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm">{selectedTicket.sujet}</h3>
                        <p className="text-xs text-gray-400 mt-1">
                          {selectedTicket.syndicatNom} · {selectedTicket.createdAt ? new Date(selectedTicket.createdAt).toLocaleString("fr-FR") : ""}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.message}</p>
                      </div>
                      {selectedTicket.reponse && (
                        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                          <p className="text-xs text-green-600 font-medium mb-1">Réponse admin</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.reponse}</p>
                        </div>
                      )}
                      {(selectedTicket.statut === "OUVERT" || selectedTicket.statut === "EN_COURS") && (
                        <div className="flex-1 flex flex-col gap-2 mt-auto">
                          <label className="text-xs font-medium text-gray-600">Votre réponse</label>
                          <textarea
                            value={reponseText}
                            onChange={e => setReponseText(e.target.value)}
                            placeholder="Saisir votre réponse..."
                            className="flex-1 w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                            rows={4}
                          />
                          <button
                            onClick={handleRepondreTicket}
                            disabled={!reponseText.trim()}
                            className="flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-all"
                          >
                            <Send size={14} />
                            Envoyer la réponse
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                      <div className="text-center">
                        <TicketIcon size={36} className="mx-auto mb-3 opacity-30" />
                        <p>Sélectionnez un ticket</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════
              PATIENTS
          ═══════════════════════════════════════════ */}
          {activeTab === "patients" && (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <User size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Utilisateurs mobiles</h2>
                  <p className="text-sm text-gray-500">Patients inscrits sur l'application SunuFarmasi</p>
                </div>
              </div>

              {/* KPI Stats */}
              {(() => {
                const total       = patients.length;
                const actifs      = patients.filter(p => p.actif).length;
                const suspendus   = patients.filter(p => !p.actif).length;
                const premiumPaye = patients.filter(p => p.premiumActif && p.montantPremium > 0).length;
                const essai       = patients.filter(p => p.premiumActif && (!p.montantPremium || p.montantPremium === 0)).length;
                const gratuit     = patients.filter(p => !p.premiumActif).length;
                const hommes      = patients.filter(p => p.sexe === "M").length;
                const femmes      = patients.filter(p => p.sexe === "F").length;
                const revenuMois  = premiumPaye * 500;
                return (
                  <div className="space-y-3">
                    {/* Ligne 1 : inscrits, actifs, suspendus */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: "Total inscrits",  value: total,     bg: "from-violet-50 to-purple-50",  border: "border-violet-100",  text: "text-violet-700",  icon: <Users size={15} className="text-violet-500" /> },
                        { label: "Actifs",           value: actifs,    bg: "from-emerald-50 to-teal-50",   border: "border-emerald-100", text: "text-emerald-700", icon: <CheckCircle size={15} className="text-emerald-500" /> },
                        { label: "Suspendus",        value: suspendus, bg: "from-red-50 to-rose-50",       border: "border-red-100",     text: "text-red-700",     icon: <XCircle size={15} className="text-red-500" /> },
                        { label: "Revenus/mois est.", value: revenuMois.toLocaleString("fr-FR") + " FCFA", bg: "from-green-50 to-emerald-50", border: "border-green-100", text: "text-green-700", icon: <Banknote size={15} className="text-green-500" /> },
                      ].map((s, i) => (
                        <div key={i} className={`bg-gradient-to-br ${s.bg} border ${s.border} rounded-2xl p-4`}>
                          <div className="flex items-center gap-2 mb-2">{s.icon}<p className="text-xs font-semibold text-gray-600">{s.label}</p></div>
                          <p className={`text-2xl font-black ${s.text}`}>{s.value}</p>
                        </div>
                      ))}
                    </div>
                    {/* Ligne 2 : abonnements + genre */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {[
                        { label: "Premium payant", value: premiumPaye, color: "yellow",  pct: total ? Math.round(premiumPaye/total*100) : 0 },
                        { label: "Essai gratuit",  value: essai,       color: "blue",    pct: total ? Math.round(essai/total*100) : 0 },
                        { label: "Gratuit",        value: gratuit,     color: "gray",    pct: total ? Math.round(gratuit/total*100) : 0 },
                        { label: "Hommes",         value: hommes,      color: "indigo",  pct: total ? Math.round(hommes/total*100) : 0 },
                        { label: "Femmes",         value: femmes,      color: "pink",    pct: total ? Math.round(femmes/total*100) : 0 },
                      ].map((s, i) => (
                        <div key={i} className="bg-white border border-gray-100 rounded-2xl p-3">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-semibold text-gray-500">{s.label}</p>
                            <span className={`text-xs font-bold text-${s.color}-600`}>{s.pct}%</span>
                          </div>
                          <p className={`text-xl font-black text-${s.color}-700`}>{s.value}</p>
                          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full bg-${s.color}-400 rounded-full`} style={{ width: `${s.pct}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Recherche + Filtres */}
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher par nom, email, téléphone…"
                    value={patientSearch}
                    onChange={e => { setPatientSearch(e.target.value); setPatientPage(1); }}
                    className="w-full pl-9 pr-4 py-2 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-violet-400"
                  />
                </div>
                <select
                  value={patientFilterStatut}
                  onChange={e => { setPatientFilterStatut(e.target.value); setPatientPage(1); }}
                  className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${patientFilterStatut ? "border-violet-400 text-violet-700" : "border-gray-200 text-gray-500"}`}
                >
                  <option value="">Tous statuts</option>
                  <option value="ACTIF">Actif</option>
                  <option value="SUSPENDU">Suspendu</option>
                </select>
                <select
                  value={patientFilterPremium}
                  onChange={e => { setPatientFilterPremium(e.target.value); setPatientPage(1); }}
                  className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${patientFilterPremium ? "border-yellow-400 text-yellow-700" : "border-gray-200 text-gray-500"}`}
                >
                  <option value="">Tous abonnements</option>
                  <option value="premium">Premium payant</option>
                  <option value="essai">Essai gratuit</option>
                  <option value="gratuit">Gratuit</option>
                </select>
                <select
                  value={patientFilterSexe}
                  onChange={e => { setPatientFilterSexe(e.target.value); setPatientPage(1); }}
                  className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${patientFilterSexe ? "border-indigo-400 text-indigo-700" : "border-gray-200 text-gray-500"}`}
                >
                  <option value="">Tous genres</option>
                  <option value="M">Homme</option>
                  <option value="F">Femme</option>
                </select>
                <select
                  value={patientFilterRegion}
                  onChange={e => { setPatientFilterRegion(e.target.value); setPatientPage(1); }}
                  className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${patientFilterRegion ? "border-teal-400 text-teal-700" : "border-gray-200 text-gray-500"}`}
                >
                  <option value="">Toutes régions</option>
                  {[...new Set(patients.map(p => p.commune?.region).filter(Boolean))].sort().map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                {(patientSearch || patientFilterStatut || patientFilterPremium || patientFilterSexe || patientFilterRegion) && (
                  <button
                    onClick={() => { setPatientSearch(""); setPatientFilterStatut(""); setPatientFilterPremium(""); setPatientFilterSexe(""); setPatientFilterRegion(""); setPatientPage(1); }}
                    className="px-3 py-2 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-xl"
                  >
                    ✕ Réinitialiser
                  </button>
                )}
              </div>

              {/* Table */}
              {(() => {
                const q = patientSearch.toLowerCase();
                const filtered = patients.filter(p => {
                  if (q && !(p.nomComplet || "").toLowerCase().includes(q)
                        && !(p.email || "").toLowerCase().includes(q)
                        && !(p.telephone || "").toLowerCase().includes(q)
                        && !(p.commune?.nom || "").toLowerCase().includes(q)) return false;
                  if (patientFilterStatut === "ACTIF" && !p.actif) return false;
                  if (patientFilterStatut === "SUSPENDU" && p.actif) return false;
                  const isTrial = p.premiumActif && (p.montantPremium === 0 || !p.montantPremium);
                  const isPaid  = p.premiumActif && p.montantPremium > 0;
                  if (patientFilterPremium === "premium" && !isPaid) return false;
                  if (patientFilterPremium === "essai" && !isTrial) return false;
                  if (patientFilterPremium === "gratuit" && p.premiumActif) return false;
                  if (patientFilterSexe && p.sexe !== patientFilterSexe) return false;
                  if (patientFilterRegion && p.commune?.region !== patientFilterRegion) return false;
                  return true;
                });
                const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
                const pageSafe = Math.min(patientPage, totalPages);
                const paginated = filtered.slice((pageSafe - 1) * ITEMS_PER_PAGE, pageSafe * ITEMS_PER_PAGE);
                return (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                {q || patientFilterStatut || patientFilterPremium ? (
                  <div className="px-4 py-2 bg-violet-50 border-b border-violet-100 text-xs text-violet-700">
                    {filtered.length} résultat{filtered.length !== 1 ? "s" : ""} sur {patients.length}
                  </div>
                ) : null}
                <div className="overflow-x-auto">
                  <div className="overflow-x-auto"><table className="w-full min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        {["Utilisateur", "Contact", "Sexe", "Localité", "Inscription", "Premium", "Statut", "Actions"].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {paginated.map(p => {
                        const nom = p.nomComplet || `${p.prenom || ""} ${p.nom || ""}`.trim() || "—";
                        const expiry = p.dateFinPremium ? new Date(p.dateFinPremium) : null;
                        const daysLeft = expiry ? Math.ceil((expiry - new Date()) / 86400000) : null;
                        const isTrial = p.premiumActif && (p.montantPremium === 0 || !p.montantPremium);
                        const isPaid  = p.premiumActif && p.montantPremium > 0;
                        return (
                          <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 bg-gradient-to-br from-violet-100 to-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <User size={14} className="text-violet-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm leading-tight">{nom}</p>
                                  <p className="text-xs text-gray-400">{p.email || ""}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3.5 text-sm text-gray-600">{p.telephone || "—"}</td>
                            <td className="px-4 py-3.5 text-center">
                              {p.sexe === "M" ? <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-lg">H</span>
                               : p.sexe === "F" ? <span className="px-2 py-0.5 bg-pink-50 text-pink-700 text-xs font-semibold rounded-lg">F</span>
                               : <span className="text-gray-300 text-xs">—</span>}
                            </td>
                            <td className="px-4 py-3.5 text-sm text-gray-500">
                              <div>{p.commune?.nom || "—"}</div>
                              {p.commune?.region && <div className="text-xs text-gray-400">{p.commune.region}</div>}
                            </td>
                            <td className="px-4 py-3.5 text-xs text-gray-500">{p.createdAt ? new Date(p.createdAt).toLocaleDateString("fr-FR") : "—"}</td>
                            <td className="px-4 py-3.5">
                              {isTrial ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-lg border ${daysLeft != null && daysLeft <= 0 ? "bg-red-50 text-red-700 border-red-100" : daysLeft != null && daysLeft <= 3 ? "bg-orange-50 text-orange-700 border-orange-100" : "bg-blue-50 text-blue-700 border-blue-100"}`}>
                                    🎁 Essai{daysLeft != null ? ` · ${daysLeft}j` : ""}
                                  </span>
                                  <p className="text-xs text-gray-500 font-medium">
                                    {expiry ? `exp. ${expiry.toLocaleDateString("fr-FR")}` : "Date non disponible"}
                                  </p>
                                </div>
                              ) : isPaid ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-lg border ${daysLeft != null && daysLeft <= 0 ? "bg-red-50 text-red-700 border-red-100" : daysLeft != null && daysLeft <= 7 ? "bg-orange-50 text-orange-700 border-orange-100" : "bg-yellow-50 text-yellow-700 border-yellow-100"}`}>
                                    <Star size={10} fill="currentColor"/>Premium{daysLeft != null ? ` · ${daysLeft}j` : ""}
                                  </span>
                                  <p className="text-xs text-gray-500 font-medium">
                                    {expiry ? `exp. ${expiry.toLocaleDateString("fr-FR")}` : "Date non disponible"}
                                  </p>
                                </div>
                              ) : (
                                <span className="inline-flex px-2 py-0.5 bg-gray-50 text-gray-500 text-xs font-medium rounded-lg border border-gray-100">Gratuit</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5">
                              <StatutBadge statut={p.actif ? "ACTIF" : "SUSPENDU"} />
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex flex-wrap gap-1">
                                <button onClick={() => setSelectedPatientDetail(p)} title="Voir les détails"
                                  className="p-1.5 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 transition-colors">
                                  <Eye size={13}/>
                                </button>
                                <button onClick={() => openPatientPremiumModal(p.id, 1, nom)} title="1 mois · 500 FCFA"
                                  className="px-2 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg text-xs font-semibold hover:bg-yellow-100">
                                  ⭐ 1M
                                </button>
                                <button onClick={() => openPatientPremiumModal(p.id, 12, nom)} title="1 an · 5 000 FCFA"
                                  className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-semibold hover:bg-amber-100">
                                  ⭐ 1An
                                </button>
                                {p.actif ? (
                                  <button onClick={() => handleSuspendrePatient(p.id)}
                                    className="px-2 py-1 bg-red-50 text-red-600 border border-red-100 rounded-lg text-xs font-semibold hover:bg-red-100">
                                    ⊘ Susp.
                                  </button>
                                ) : (
                                  <button onClick={() => handleReactiverPatient(p.id)}
                                    className="px-2 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-lg text-xs font-semibold hover:bg-emerald-100">
                                    ✓ Réact.
                                  </button>
                                )}
                                <button onClick={() => handleDeletePatient(p.id, nom)} title="Supprimer définitivement"
                                  className="p-1.5 bg-red-100 text-red-700 border border-red-200 rounded-lg hover:bg-red-200 transition-colors">
                                  <Trash2 size={13}/>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filtered.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm">
                          {q || patientFilterStatut || patientFilterPremium ? "Aucun résultat pour ces filtres" : "Aucun patient enregistré"}
                        </td></tr>
                      )}
                    </tbody>
                  </table></div>
                </div>
                <Pagination page={pageSafe} setPage={setPatientPage} total={filtered.length} />
              </div>
                );
              })()}
            </div>
          )}

          {/* ═══════════════════════════════════════════
              PAIEMENTS
          ═══════════════════════════════════════════ */}
          {activeTab === "paiements" && (() => {
            const now = new Date();
            const syndicatsExpires   = syndicats.filter(s => s.dateFinAbonnement && new Date(s.dateFinAbonnement) < now);
            const syndicatsExpiring  = syndicats.filter(s => s.dateFinAbonnement && new Date(s.dateFinAbonnement) > now && (new Date(s.dateFinAbonnement) - now) < 7 * 86400000);
            const revenuMensuelTotal = syndicats.reduce((sum, s) => sum + (s.montantMensuel || 0), 0);
            return (
              <div className="space-y-5">
                {/* KPIs */}
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { label: "Revenus/mois (estimé)", value: revenuMensuelTotal.toLocaleString("fr-FR") + " FCFA", icon: Banknote, color: "green" },
                    { label: "Syndicats actifs",       value: syndicats.filter(s=>s.statut==="ACTIF").length, icon: Building2, color: "blue" },
                    { label: "Expirent bientôt",       value: syndicatsExpiring.length,  icon: AlertCircle, color: "orange" },
                    { label: "Abonnements expirés",    value: syndicatsExpires.length,   icon: XCircle, color: "red" },
                  ].map((s, i) => (
                    <div key={i} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                      <div className={`w-10 h-10 rounded-lg bg-${s.color}-100 flex items-center justify-center mb-3`}>
                        <s.icon size={18} className={`text-${s.color}-600`} />
                      </div>
                      <p className="text-xl font-bold text-gray-800">{s.value}</p>
                      <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Alertes expirations */}
                {(syndicatsExpires.length > 0 || syndicatsExpiring.length > 0) && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-1">
                    {syndicatsExpires.map(s => (
                      <div key={s.id} className="flex items-center justify-between text-sm">
                        <span className="text-red-700 font-medium">{s.nom} — abonnement expiré ({s.dateFinAbonnement})</span>
                        <div className="flex gap-2">
                          <button onClick={() => handleRenouveler(s.id)} className="px-2 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">Renouveler</button>
                          <button onClick={() => handleSuspendre(s.id)} className="px-2 py-1 bg-red-200 text-red-700 text-xs rounded-lg hover:bg-red-300">Suspendre</button>
                        </div>
                      </div>
                    ))}
                    {syndicatsExpiring.map(s => (
                      <div key={s.id} className="flex items-center text-sm text-orange-700">
                        <AlertCircle size={13} className="mr-2 flex-shrink-0" />
                        {s.nom} expire le {s.dateFinAbonnement}
                      </div>
                    ))}
                  </div>
                )}

                {/* Onglets */}
                <div className="flex gap-2 border-b border-gray-200">
                  {[
                    { id: "syndicats", label: "Syndicats" },
                    { id: "pharmacies", label: "Pharmacies" },
                    { id: "patients", label: "Patients" },
                    { id: "demandes", label: `Demandes${pendingPayments.length > 0 ? ` (${pendingPayments.length})` : ""}` },
                  ].map(t => (
                    <button key={t.id} onClick={() => { setPaiementTab(t.id); if (t.id === "demandes") loadPendingPayments(); }}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${paiementTab === t.id ? "border-green-600 text-green-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Table syndicats */}
                {paiementTab === "syndicats" && (() => {
                  const syndicatsFiltered = syndicats.filter(s => {
                    const q = syndicatSearch.toLowerCase();
                    if (q && !(s.nom || "").toLowerCase().includes(q) && !(s.code || "").toLowerCase().includes(q)) return false;
                    if (paiementSyndicatFilterStatut && s.statut !== paiementSyndicatFilterStatut) return false;
                    if (paiementSyndicatFilterPlan && (s.plan || "COMMUNE") !== paiementSyndicatFilterPlan) return false;
                    return true;
                  });
                  const now = new Date();
                  const totalSynd = syndicatsFiltered.length;
                  const totalPagesSynd = Math.max(1, Math.ceil(totalSynd / ITEMS_PER_PAGE));
                  const syndicatPageSafe = Math.min(paiementSyndicatPage, totalPagesSynd);
                  const syndicatsPaged = syndicatsFiltered.slice((syndicatPageSafe - 1) * ITEMS_PER_PAGE, syndicatPageSafe * ITEMS_PER_PAGE);
                  const kpiActifs = syndicats.filter(s => s.statut === "ACTIF").length;
                  const kpiSuspendus = syndicats.filter(s => s.statut === "SUSPENDU").length;
                  const kpiExpires = syndicats.filter(s => s.dateFinAbonnement && new Date(s.dateFinAbonnement) < now).length;
                  const kpiCA = syndicats.reduce((acc, s) => acc + (s.montantMensuel || (s.plan === "DEPARTEMENT" ? 11000 : 5500)), 0);
                  return (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* KPI strip */}
                    <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
                      {[
                        { label: "Total syndicats", value: syndicats.length, color: "text-gray-800" },
                        { label: "Actifs", value: kpiActifs, color: "text-emerald-600" },
                        { label: "Suspendus", value: kpiSuspendus, color: "text-red-500" },
                        { label: "CA mensuel estimé", value: `${kpiCA.toLocaleString("fr-FR")} FCFA`, color: "text-green-700" },
                      ].map(k => (
                        <div key={k.label} className="px-4 py-3 text-center">
                          <p className={`text-lg font-bold ${k.color}`}>{k.value}</p>
                          <p className="text-xs text-gray-400">{k.label}</p>
                        </div>
                      ))}
                    </div>
                    {/* Barre de filtres */}
                    <div className="p-4 border-b border-gray-100 flex flex-wrap gap-2 items-center">
                      <div className="relative flex-1 min-w-[180px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                        <input type="text" value={syndicatSearch} onChange={e => { setSyndicatSearch(e.target.value); setPaiementSyndicatPage(1); }}
                          placeholder="Rechercher un syndicat…"
                          className="w-full pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-300"/>
                      </div>
                      <select value={paiementSyndicatFilterStatut} onChange={e => { setPaiementSyndicatFilterStatut(e.target.value); setPaiementSyndicatPage(1); }}
                        className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${paiementSyndicatFilterStatut ? "border-green-400 text-green-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Tous les statuts</option>
                        <option value="ACTIF">Actif</option>
                        <option value="SUSPENDU">Suspendu</option>
                        <option value="INACTIF">Inactif</option>
                      </select>
                      <select value={paiementSyndicatFilterPlan} onChange={e => { setPaiementSyndicatFilterPlan(e.target.value); setPaiementSyndicatPage(1); }}
                        className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${paiementSyndicatFilterPlan ? "border-purple-400 text-purple-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Tous les plans</option>
                        <option value="COMMUNE">Commune — 5 500 FCFA</option>
                        <option value="DEPARTEMENT">Département — 11 000 FCFA</option>
                      </select>
                      {(syndicatSearch || paiementSyndicatFilterStatut || paiementSyndicatFilterPlan) && (
                        <button onClick={() => { setSyndicatSearch(""); setPaiementSyndicatFilterStatut(""); setPaiementSyndicatFilterPlan(""); setPaiementSyndicatPage(1); }}
                          className="flex items-center gap-1 px-3 py-2 text-xs border-2 border-gray-200 text-gray-400 rounded-xl hover:text-gray-600 bg-white font-semibold">
                          <X size={12}/> Reset
                        </button>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">{totalSynd} résultat{totalSynd !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="overflow-x-auto"><table className="w-full text-sm min-w-[700px]">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {["Syndicat", "Plan", "Montant", "Inscription", "Dernier paiement", "Expire le", "Statut", "Actions"].map(h => (
                            <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {syndicatsPaged.map(s => {
                          const echeance = s.dateFinAbonnement ? new Date(s.dateFinAbonnement) : null;
                          const isExpired = echeance && echeance < now;
                          const isSoon    = echeance && !isExpired && (echeance - now) < 7 * 86400000;
                          const daysLeft = echeance && !isExpired ? Math.ceil((echeance - now) / 86400000) : null;
                          return (
                            <tr key={s.id} className={`hover:bg-gray-50 transition-colors ${isExpired ? "bg-red-50/60" : isSoon ? "bg-amber-50/60" : s.statut === "SUSPENDU" ? "bg-red-50/20" : ""}`}>
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-sm ${s.statut === "ACTIF" ? "bg-green-600" : "bg-red-400"}`}>
                                    {(s.nom || "?")[0].toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-gray-800">{s.nom}</p>
                                    <p className="text-xs text-gray-400 font-mono">{s.code}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${s.plan === "DEPARTEMENT" ? "bg-indigo-100 text-indigo-700" : "bg-purple-100 text-purple-700"}`}>{s.plan || "COMMUNE"}</span>
                              </td>
                              <td className="px-3 py-3 text-sm font-bold text-green-700 whitespace-nowrap">
                                {s.montantMensuel != null ? Number(s.montantMensuel).toLocaleString("fr-FR") : (s.plan === "DEPARTEMENT" ? "11 000" : "5 500")} <span className="text-xs font-normal text-gray-400">FCFA/mois</span>
                              </td>
                              <td className="px-3 py-3 text-xs text-gray-400">{s.createdAt ? new Date(s.createdAt).toLocaleDateString("fr-FR") : "—"}</td>
                              <td className="px-3 py-3 text-xs">
                                {s.dateDernierPaiement
                                  ? <span className="text-gray-600">{new Date(s.dateDernierPaiement).toLocaleDateString("fr-FR")}</span>
                                  : <span className="px-2 py-0.5 bg-red-50 text-red-500 rounded text-xs font-medium">Jamais</span>}
                              </td>
                              <td className="px-3 py-3">
                                {echeance ? (
                                  <div>
                                    <span className={`text-xs font-semibold block ${isExpired ? "text-red-600" : isSoon ? "text-amber-600" : "text-gray-600"}`}>
                                      {echeance.toLocaleDateString("fr-FR")}{isExpired ? " ⛔" : isSoon ? " ⚠️" : ""}
                                    </span>
                                    {daysLeft !== null && <span className="text-xs text-gray-400">{daysLeft}j restants</span>}
                                    {isExpired && <span className="text-xs text-red-400">Expiré</span>}
                                  </div>
                                ) : <span className="text-gray-300 text-sm">—</span>}
                              </td>
                              <td className="px-3 py-3"><StatutBadge statut={s.statut} /></td>
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-1.5">
                                  <button title="Enregistrer un paiement" onClick={() => ouvrirPaiementModal(s)}
                                    className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                                    <Banknote size={14}/>
                                  </button>
                                  <button title="Renouveler +1 mois" onClick={() => handleRenouveler(s.id, 1)}
                                    className="p-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                                    <RefreshCw size={14}/>
                                  </button>
                                  <button title="Renouveler +1 an" onClick={() => handleRenouveler(s.id, 12)}
                                    className="p-1.5 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-xs font-bold px-2">
                                    +1 an
                                  </button>
                                  <button title="Envoyer un rappel par email" onClick={() => handleRelancerSyndicat(s.id)}
                                    disabled={relancerLoading === s.id}
                                    className="p-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50">
                                    <Mail size={14}/>
                                  </button>
                                  {s.statut === "ACTIF" ? (
                                    <button title="Suspendre le syndicat" onClick={() => handleSuspendre(s.id)}
                                      className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors">
                                      <XCircle size={14}/>
                                    </button>
                                  ) : (
                                    <button title="Réactiver le syndicat" onClick={() => handleReactiver(s.id)}
                                      className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors">
                                      <CheckCircle size={14}/>
                                    </button>
                                  )}
                                  <button title="Supprimer le syndicat" onClick={() => handleSupprimerSyndicat(s)}
                                    className="p-1.5 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                                    <Trash2 size={14}/>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {syndicatsPaged.length === 0 && (
                          <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm">
                            {syndicatSearch ? `Aucun résultat pour "${syndicatSearch}"` : "Aucun syndicat"}
                          </td></tr>
                        )}
                      </tbody>
                    </table></div>
                    {totalPagesSynd > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">{totalSynd} syndicat(s) · page {syndicatPageSafe}/{totalPagesSynd}</p>
                        <div className="flex gap-1">
                          <button onClick={() => setPaiementSyndicatPage(p => Math.max(1, p - 1))} disabled={syndicatPageSafe === 1}
                            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40">‹ Préc</button>
                          {Array.from({length: totalPagesSynd}, (_, i) => i + 1).filter(p => Math.abs(p - syndicatPageSafe) <= 2).map(p => (
                            <button key={p} onClick={() => setPaiementSyndicatPage(p)}
                              className={`px-3 py-1.5 text-xs rounded-lg ${p === syndicatPageSafe ? "bg-green-600 text-white" : "border border-gray-200 hover:bg-gray-50"}`}>
                              {p}
                            </button>
                          ))}
                          <button onClick={() => setPaiementSyndicatPage(p => Math.min(totalPagesSynd, p + 1))} disabled={syndicatPageSafe === totalPagesSynd}
                            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40">Suiv ›</button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* Table pharmacies */}
                {paiementTab === "pharmacies" && (() => {
                  const now2 = new Date();
                  const pharmFiltered = pharmacies.filter(p => {
                    const q = pharmPaiementSearch.toLowerCase();
                    if (q && !(p.nom || "").toLowerCase().includes(q) && !(p.code || "").toLowerCase().includes(q)
                      && !(p.syndicatNom || "").toLowerCase().includes(q) && !(p.communeNom || p.commune || "").toLowerCase().includes(q)) return false;
                    if (paiementPharmFilterStatut && p.statut !== paiementPharmFilterStatut) return false;
                    if (paiementPharmFilterSyndicat && p.syndicatId !== paiementPharmFilterSyndicat) return false;
                    return true;
                  });
                  const totalPharm = pharmFiltered.length;
                  const totalPagesPharm = Math.max(1, Math.ceil(totalPharm / ITEMS_PER_PAGE));
                  const pharmPageSafe = Math.min(pharmPage, totalPagesPharm);
                  const pharmaciesPaged = pharmFiltered.slice((pharmPageSafe - 1) * ITEMS_PER_PAGE, pharmPageSafe * ITEMS_PER_PAGE);
                  const kpiActive = pharmacies.filter(p=>p.statut==="ACTIVE").length;
                  const kpiSuspendues = pharmacies.filter(p=>p.statut==="SUSPENDUE").length;
                  const kpiExpires = pharmacies.filter(p=>p.dateFinAbonnement && new Date(p.dateFinAbonnement) < now2).length;
                  return (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* KPI strip */}
                    <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-green-50">
                      {[
                        { label: "Total pharmacies", value: pharmacies.length, color: "text-gray-800" },
                        { label: "Actives", value: kpiActive, color: "text-emerald-600" },
                        { label: "Suspendues", value: kpiSuspendues, color: "text-red-500" },
                        { label: "Expirées", value: kpiExpires, color: "text-amber-600" },
                      ].map(k => (
                        <div key={k.label} className="px-4 py-3 text-center">
                          <p className={`text-lg font-bold ${k.color}`}>{k.value}</p>
                          <p className="text-xs text-gray-400">{k.label}</p>
                        </div>
                      ))}
                    </div>
                    {/* Filtres */}
                    <div className="p-4 border-b border-gray-100 flex flex-wrap gap-2 items-center">
                      <div className="relative flex-1 min-w-[180px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                        <input type="text" value={pharmPaiementSearch} onChange={e => { setPharmPaiementSearch(e.target.value); setPharmPage(1); }}
                          placeholder="Nom, code, syndicat, commune…"
                          className="w-full pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-300"/>
                      </div>
                      <select value={paiementPharmFilterStatut} onChange={e => { setPaiementPharmFilterStatut(e.target.value); setPharmPage(1); }}
                        className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${paiementPharmFilterStatut ? "border-green-400 text-green-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Tous les statuts</option>
                        <option value="ACTIVE">Active</option>
                        <option value="VALIDEE">Validée</option>
                        <option value="EN_ATTENTE">En attente</option>
                        <option value="SUSPENDUE">Suspendue</option>
                      </select>
                      <select value={paiementPharmFilterSyndicat} onChange={e => { setPaiementPharmFilterSyndicat(e.target.value); setPharmPage(1); }}
                        className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${paiementPharmFilterSyndicat ? "border-blue-400 text-blue-700" : "border-gray-200 text-gray-500"}`}>
                        <option value="">Tous les syndicats</option>
                        {syndicats.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                      </select>
                      {(pharmPaiementSearch || paiementPharmFilterStatut || paiementPharmFilterSyndicat) && (
                        <button onClick={() => { setPharmPaiementSearch(""); setPaiementPharmFilterStatut(""); setPaiementPharmFilterSyndicat(""); setPharmPage(1); }}
                          className="flex items-center gap-1 px-3 py-2 text-xs border-2 border-gray-200 text-gray-400 rounded-xl hover:text-gray-600 bg-white font-semibold">
                          <X size={12}/> Reset
                        </button>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">{totalPharm} résultat{totalPharm !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="overflow-x-auto"><table className="w-full text-sm min-w-[750px]">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>{["Pharmacie", "Syndicat", "Zone", "Inscrit le", "Dernier paiement", "Expire le", "Statut", "Actions"].map(h => (
                          <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pharmaciesPaged.map(p => {
                          const expire = p.dateFinAbonnement ? new Date(p.dateFinAbonnement) : null;
                          const isExpired = expire && expire < now2;
                          const isSoon = expire && !isExpired && (expire - now2) < 30 * 86400000;
                          const daysLeft = expire && !isExpired ? Math.ceil((expire - now2) / 86400000) : null;
                          return (
                            <tr key={p.id} className={`hover:bg-gray-50 transition-colors ${isExpired ? "bg-red-50/40" : isSoon ? "bg-amber-50/40" : p.statut === "SUSPENDUE" ? "bg-red-50/20" : ""}`}>
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-sm ${p.statut === "ACTIVE" ? "bg-emerald-500" : p.statut === "SUSPENDUE" ? "bg-red-400" : "bg-gray-400"}`}>
                                    {(p.nom || "?")[0].toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-800 text-sm">{p.nom}</p>
                                    <p className="text-xs text-gray-400 font-mono">{p.code}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                {p.syndicatNom
                                  ? <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{p.syndicatNom}</span>
                                  : <span className="text-red-400 italic text-xs">Non assigné</span>}
                              </td>
                              <td className="px-3 py-3 text-xs text-gray-500">
                                <div className="flex items-center gap-1"><MapPin size={11} className="text-gray-300"/>{p.communeNom || p.commune || "—"}</div>
                              </td>
                              <td className="px-3 py-3 text-xs text-gray-400">{p.createdAt ? new Date(p.createdAt).toLocaleDateString("fr-FR") : "—"}</td>
                              <td className="px-3 py-3 text-xs">
                                {p.dateDernierPaiement
                                  ? <span className="text-gray-600">{new Date(p.dateDernierPaiement).toLocaleDateString("fr-FR")}</span>
                                  : <span className="px-2 py-0.5 bg-red-50 text-red-500 rounded text-xs font-medium">Jamais</span>}
                              </td>
                              <td className="px-3 py-3 text-xs">
                                {expire ? (
                                  <div>
                                    <span className={`font-medium block ${isExpired ? "text-red-600" : isSoon ? "text-amber-600" : "text-gray-600"}`}>
                                      {expire.toLocaleDateString("fr-FR")}{isExpired ? " ⛔" : isSoon ? " ⚠️" : ""}
                                    </span>
                                    {daysLeft !== null && <span className="text-xs text-gray-400">{daysLeft}j restants</span>}
                                    {isExpired && <span className="text-xs text-red-400">Expiré</span>}
                                  </div>
                                ) : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-3 py-3"><PharmacieStatutBadge statut={p.statut} /></td>
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {/* Enregistrer paiement (EN_ATTENTE) */}
                                  {(p.statut === "EN_ATTENTE" || p.statut === "VALIDEE") && (
                                    <button title="Enregistrer paiement 5 000 FCFA — Active la pharmacie"
                                      onClick={() => handleEnregistrerPaiementPharmacie(p.id, p.nom)}
                                      className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                                      <Banknote size={14}/>
                                    </button>
                                  )}
                                  {/* Renouveler */}
                                  {(p.statut === "ACTIVE" || isExpired) && (
                                    <button title="Renouveler abonnement (1 an)" onClick={() => handleRenouvelerPharmacie(p.id, p.nom)}
                                      className="p-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                                      <RefreshCw size={14}/>
                                    </button>
                                  )}
                                  {/* Relancer (rappel email) */}
                                  {(isExpired || isSoon || !p.dateDernierPaiement) && (
                                    <button title="Envoyer rappel par email"
                                      onClick={() => handleRelancerPharmacie(p.id)}
                                      disabled={relancerLoading === p.id}
                                      className="p-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50">
                                      <Mail size={14}/>
                                    </button>
                                  )}
                                  {/* Valider / Rejeter (EN_ATTENTE) */}
                                  {p.statut === "EN_ATTENTE" && (
                                    <>
                                      <button title="Valider la pharmacie" onClick={() => handleValiderPharmacie(p.id)}
                                        className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                                        <CheckCircle size={14}/>
                                      </button>
                                      <button title="Rejeter la pharmacie" onClick={() => handleRejeterPharmacie(p.id)}
                                        className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors">
                                        <XCircle size={14}/>
                                      </button>
                                    </>
                                  )}
                                  {/* Suspendre / Activer */}
                                  {p.statut === "ACTIVE" && (
                                    <button title="Suspendre la pharmacie" onClick={() => handleSuspendrePharmacie(p.id)}
                                      className="p-1.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors">
                                      <XCircle size={14}/>
                                    </button>
                                  )}
                                  {(p.statut === "SUSPENDUE" || p.statut === "VALIDEE") && (
                                    <button title="Activer la pharmacie" onClick={() => handleActiverPharmacie(p.id)}
                                      className="p-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors">
                                      <CheckCircle size={14}/>
                                    </button>
                                  )}
                                  <button title="Voir les détails" onClick={() => setSelectedPharmacieDetail(p)}
                                    className="p-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                                    <Eye size={14}/>
                                  </button>
                                  <button title="Supprimer la pharmacie" onClick={() => handleSupprimerPharmacie(p)}
                                    className="p-1.5 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                                    <Trash2 size={14}/>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {pharmaciesPaged.length === 0 && (
                          <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm">
                            {pharmPaiementSearch ? `Aucun résultat pour "${pharmPaiementSearch}"` : "Aucune pharmacie"}
                          </td></tr>
                        )}
                      </tbody>
                    </table></div>
                    {totalPagesPharm > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">{totalPharm} pharmacie(s) · page {pharmPageSafe}/{totalPagesPharm}</p>
                        <div className="flex gap-1">
                          <button onClick={() => setPharmPage(p => Math.max(1, p - 1))} disabled={pharmPageSafe === 1}
                            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40">‹ Préc</button>
                          {Array.from({length: totalPagesPharm}, (_, i) => i + 1).filter(p => Math.abs(p - pharmPageSafe) <= 2).map(p => (
                            <button key={p} onClick={() => setPharmPage(p)}
                              className={`px-3 py-1.5 text-xs rounded-lg ${p === pharmPageSafe ? "bg-green-600 text-white" : "border border-gray-200 hover:bg-gray-50"}`}>
                              {p}
                            </button>
                          ))}
                          <button onClick={() => setPharmPage(p => Math.min(totalPagesPharm, p + 1))} disabled={pharmPageSafe === totalPagesPharm}
                            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40">Suiv ›</button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* Patients */}
                {/* Demandes paiement manuel */}
                {paiementTab === "demandes" && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Demandes de paiement manuel Wave</p>
                        <p className="text-xs text-gray-400 mt-0.5">Patients qui ont envoyé de l'argent via Wave B2B et attendent la validation</p>
                      </div>
                      <button onClick={loadPendingPayments} className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg flex items-center gap-1">
                        ↻ Actualiser
                      </button>
                    </div>
                    {pendingLoading ? (
                      <div className="py-12 text-center text-gray-400 text-sm">Chargement…</div>
                    ) : (
                      <div className="overflow-x-auto"><table className="w-full text-sm min-w-[600px]">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>{["Patient", "Plan", "Montant", "Réf. Wave", "Date", "Actions"].map(h => (
                            <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                          ))}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {pendingPayments.map(p => (
                            <tr key={p.id} className="hover:bg-yellow-50">
                              <td className="px-3 py-3 font-medium text-gray-800">{p.patientNom || p.patientId || "—"}</td>
                              <td className="px-3 py-3 text-xs text-gray-500">{p.planCode || "—"}</td>
                              <td className="px-3 py-3 text-sm font-semibold text-green-700">{p.montantFormate || `${p.montant} FCFA`}</td>
                              <td className="px-3 py-3 text-xs font-mono text-blue-600">{p.referenceExterne || "—"}</td>
                              <td className="px-3 py-3 text-xs text-gray-400">{p.createdAt ? new Date(p.createdAt).toLocaleDateString("fr-FR") : "—"}</td>
                              <td className="px-3 py-3">
                                <div className="flex gap-2">
                                  <button onClick={() => handleValiderPaiement(p.referenceInterne)}
                                    className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-lg hover:bg-green-200 font-medium">
                                    ✓ Valider
                                  </button>
                                  <button onClick={() => handleRejeterPaiement(p.referenceInterne)}
                                    className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-lg hover:bg-red-200">
                                    ✗ Rejeter
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {pendingPayments.length === 0 && (
                            <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">Aucune demande en attente</td></tr>
                          )}
                        </tbody>
                      </table></div>
                    )}
                  </div>
                )}

                {paiementTab === "patients" && (() => {
                  const nEssai   = patients.filter(p => p.premiumActif && (p.montantPremium === 0 || !p.montantPremium)).length;
                  const nPremium = patients.filter(p => p.premiumActif && p.montantPremium > 0).length;
                  const nGratuit = patients.filter(p => !p.premiumActif).length;

                  // Filtrage
                  const patPaiFiltered = patients.filter(p => {
                    const q = paiPatSearch.toLowerCase();
                    if (q && !((p.nomComplet||"").toLowerCase().includes(q) || (p.telephone||"").toLowerCase().includes(q))) return false;
                    if (paiPatFilterPremium === "premium" && !(p.premiumActif && p.montantPremium > 0)) return false;
                    if (paiPatFilterPremium === "essai"   && !(p.premiumActif && (p.montantPremium === 0 || !p.montantPremium))) return false;
                    if (paiPatFilterPremium === "gratuit" &&  p.premiumActif) return false;
                    return true;
                  });
                  const PAI_PAGE = 15;
                  const totalPaiPages = Math.max(1, Math.ceil(patPaiFiltered.length / PAI_PAGE));
                  const paiPatPageSafe = Math.min(paiPatPage, totalPaiPages);
                  const patPaiPaged = patPaiFiltered.slice((paiPatPageSafe - 1) * PAI_PAGE, paiPatPageSafe * PAI_PAGE);

                  return (
                    <div className="space-y-4">
                      {/* KPI mini */}
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: "Premium payant", value: nPremium, color: "yellow", icon: Star },
                          { label: "Essai gratuit",  value: nEssai,   color: "blue",   icon: null },
                          { label: "Gratuit",        value: nGratuit, color: "gray",   icon: null },
                        ].map((k, i) => (
                          <div key={i} className={`bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex items-center gap-3`}>
                            <div className={`w-9 h-9 rounded-lg bg-${k.color}-100 flex items-center justify-center text-${k.color}-600 font-bold text-sm`}>
                              {k.icon ? <Star size={16} fill="currentColor"/> : k.label[0]}
                            </div>
                            <div>
                              <p className="text-xl font-bold text-gray-800">{k.value}</p>
                              <p className="text-xs text-gray-400">{k.label}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        {/* Barre de filtres */}
                        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
                          <input type="text" placeholder="Rechercher nom, téléphone…" value={paiPatSearch}
                            onChange={e => { setPaiPatSearch(e.target.value); setPaiPatPage(1); }}
                            className="flex-1 min-w-48 px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-400"/>
                          <select value={paiPatFilterPremium} onChange={e => { setPaiPatFilterPremium(e.target.value); setPaiPatPage(1); }}
                            className={`px-3 py-2 text-sm border-2 rounded-xl bg-white focus:outline-none ${paiPatFilterPremium ? "border-yellow-400 text-yellow-700" : "border-gray-200 text-gray-500"}`}>
                            <option value="">Tous types</option>
                            <option value="premium">Premium payant</option>
                            <option value="essai">Essai gratuit</option>
                            <option value="gratuit">Gratuit</option>
                          </select>
                          {(paiPatSearch || paiPatFilterPremium) && (
                            <button onClick={() => { setPaiPatSearch(""); setPaiPatFilterPremium(""); setPaiPatPage(1); }}
                              className="px-3 py-2 text-xs text-gray-500 border-2 border-gray-200 rounded-xl hover:border-red-300 hover:text-red-500">
                              Réinitialiser
                            </button>
                          )}
                          <span className="text-xs text-gray-400 ml-auto">{patPaiFiltered.length} patient{patPaiFiltered.length !== 1 ? "s" : ""}</span>
                        </div>

                        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[600px]">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{["Nom", "Téléphone", "Inscrit le", "Abonnement", "Expiration", "Statut", "Actions"].map(h => (
                              <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                            ))}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {patPaiPaged.map(p => {
                              const expiry = p.dateFinPremium ? new Date(p.dateFinPremium) : null;
                              const daysLeft = expiry ? Math.ceil((expiry - new Date()) / 86400000) : null;
                              const isTrial  = p.premiumActif && (p.montantPremium === 0 || !p.montantPremium);
                              const isPaid   = p.premiumActif && p.montantPremium > 0;
                              return (
                                <tr key={p.id} className="hover:bg-gray-50">
                                  <td className="px-3 py-3 font-medium text-gray-800">
                                    {p.nomComplet || `${p.prenom || ""} ${p.nom || ""}`.trim() || "—"}
                                  </td>
                                  <td className="px-3 py-3 text-xs text-gray-500">{p.telephone || "—"}</td>
                                  <td className="px-3 py-3 text-xs text-gray-400">
                                    {p.createdAt ? new Date(p.createdAt).toLocaleDateString("fr-FR") : "—"}
                                  </td>
                                  <td className="px-3 py-3">
                                    {isTrial ? (
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-lg border ${daysLeft != null && daysLeft <= 0 ? "bg-red-50 text-red-700 border-red-100" : daysLeft != null && daysLeft <= 3 ? "bg-orange-50 text-orange-700 border-orange-100" : "bg-blue-50 text-blue-700 border-blue-100"}`}>
                                        🎁 Essai{daysLeft != null ? ` · ${daysLeft}j` : ""}
                                      </span>
                                    ) : isPaid ? (
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-lg border ${daysLeft != null && daysLeft <= 0 ? "bg-red-50 text-red-700 border-red-100" : daysLeft != null && daysLeft <= 7 ? "bg-orange-50 text-orange-700 border-orange-100" : "bg-yellow-50 text-yellow-700 border-yellow-100"}`}>
                                        <Star size={10} fill="currentColor"/> Premium{daysLeft != null ? ` · ${daysLeft}j` : ""}
                                      </span>
                                    ) : (
                                      <span className="inline-flex px-2 py-0.5 bg-gray-50 text-gray-500 text-xs font-medium rounded-lg border border-gray-100">Gratuit</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-3 text-xs">
                                    {expiry ? (
                                      <span className={daysLeft != null && daysLeft <= 0 ? "text-red-600 font-semibold" : daysLeft != null && daysLeft <= 7 ? "text-orange-500 font-medium" : "text-gray-600"}>
                                        {expiry.toLocaleDateString("fr-FR")}
                                      </span>
                                    ) : (
                                      <span className="text-gray-300">—</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-3"><StatutBadge statut={p.actif ? "ACTIF" : "SUSPENDU"} /></td>
                                  <td className="px-3 py-3">
                                    <div className="flex flex-wrap items-center gap-1">
                                      <button onClick={() => openPatientPremiumModal(p.id, 1, p.nomComplet || "")} title="500 FCFA · 1 mois"
                                        className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-lg hover:bg-yellow-200 flex items-center gap-1">
                                        <Star size={10}/> 1M
                                      </button>
                                      <button onClick={() => openPatientPremiumModal(p.id, 12, p.nomComplet || "")} title="5 000 FCFA · 1 an"
                                        className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-lg hover:bg-amber-200 flex items-center gap-1">
                                        <Star size={10}/> 1An
                                      </button>
                                      {p.actif ? (
                                        <button onClick={() => handleSuspendrePatient(p.id)}
                                          className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-lg hover:bg-red-200">
                                          Susp.
                                        </button>
                                      ) : (
                                        <button onClick={() => handleReactiverPatient(p.id)}
                                          className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-lg hover:bg-green-200">
                                          Réact.
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {patPaiFiltered.length === 0 && (
                              <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">Aucun patient trouvé</td></tr>
                            )}
                          </tbody>
                        </table></div>

                        {/* Pagination */}
                        {totalPaiPages > 1 && (
                          <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-xs text-gray-400">Page {paiPatPageSafe}/{totalPaiPages}</span>
                            <div className="flex gap-1">
                              <button onClick={() => setPaiPatPage(p => Math.max(1, p - 1))} disabled={paiPatPageSafe === 1}
                                className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">←</button>
                              {Array.from({length: Math.min(5, totalPaiPages)}, (_, i) => {
                                const start = Math.max(1, Math.min(paiPatPageSafe - 2, totalPaiPages - 4));
                                return start + i;
                              }).map(pg => (
                                <button key={pg} onClick={() => setPaiPatPage(pg)}
                                  className={`px-3 py-1 text-xs border rounded-lg ${paiPatPageSafe === pg ? "bg-green-600 text-white border-green-600" : "border-gray-200 hover:bg-gray-50"}`}>
                                  {pg}
                                </button>
                              ))}
                              <button onClick={() => setPaiPatPage(p => Math.min(totalPaiPages, p + 1))} disabled={paiPatPageSafe === totalPaiPages}
                                className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">→</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* ═══════════════════════════════════════════
              LOCALISATION
          ═══════════════════════════════════════════ */}
          {activeTab === "localisation" && (() => {
            // ── Filtrage + recherche ──
            const q = locSearch.toLowerCase();
            const filteredPays = pays.filter(p =>
              !q || p.nom?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q) || p.codeIso2?.toLowerCase().includes(q)
            );
            const filteredRegions = regions.filter(r =>
              (!locFilterPays || r.paysId === locFilterPays) &&
              (!q || r.nom?.toLowerCase().includes(q) || r.code?.toLowerCase().includes(q) || r.chefLieu?.toLowerCase().includes(q))
            );
            const filteredDeps = departements.filter(d =>
              (!locFilterRegion || d.regionId === locFilterRegion) &&
              (!locFilterPays || regions.find(r=>r.id===d.regionId)?.paysId === locFilterPays) &&
              (!q || d.nom?.toLowerCase().includes(q) || d.code?.toLowerCase().includes(q))
            );
            const filteredCommunes = communes.filter(c =>
              (!locFilterDep || c.departementId === locFilterDep) &&
              (!locFilterRegion || departements.find(d=>d.id===c.departementId)?.regionId === locFilterRegion) &&
              (!locFilterPays || regions.find(r=>r.id===departements.find(d=>d.id===c.departementId)?.regionId)?.paysId === locFilterPays) &&
              (!q || c.nom?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q) || c.type?.toLowerCase().includes(q))
            );
            const dataMap = { pays: filteredPays, regions: filteredRegions, departements: filteredDeps, communes: filteredCommunes };
            const currentData = dataMap[localisationTab] || [];
            const totalPages = Math.ceil(currentData.length / LOC_PAGE_SIZE);
            const pagedData = currentData.slice((locPage - 1) * LOC_PAGE_SIZE, locPage * LOC_PAGE_SIZE);

            const locTabs = [
              { id: "pays",        label: `Pays (${filteredPays.length})` },
              { id: "regions",     label: `Régions (${filteredRegions.length})` },
              { id: "departements",label: `Départements (${filteredDeps.length})` },
              { id: "communes",    label: `Communes (${filteredCommunes.length})` },
            ];
            const addLabels = { pays: "un pays", regions: "une région", departements: "un département", communes: "une commune" };
            const addModals = { pays: "addPays", regions: "addRegion", departements: "addDep", communes: "addCommune" };
            return (
              <div className="space-y-5">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Gestion des Localités</h2>
                      <p className="text-sm text-gray-500">Pays → Régions → Départements → Communes</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        if (!window.confirm("Initialiser toutes les données du Sénégal (14 régions, départements, communes) ?")) return;
                        setLocalisationInitLoading(true);
                        const token = localStorage.getItem("token");
                        try {
                          await localisationApi.initSenegal();
                          loadLocalisation();
                        } catch { alert("Erreur initialisation"); }
                        setLocalisationInitLoading(false);
                      }}
                      disabled={localisationInitLoading}
                      className="flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl font-semibold text-xs hover:bg-amber-100 transition-all disabled:opacity-50"
                    >
                      <RefreshCw size={13} className={localisationInitLoading ? "animate-spin" : ""} />
                      Init Sénégal
                    </button>
                    <button
                      onClick={() => { setLocalisationForm({}); setLocalisationTarget(null); setLocalisationModal(addModals[localisationTab]); }}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all"
                    >
                      <Plus size={15} /> Ajouter {addLabels[localisationTab]}
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: "Pays",         value: pays.length,        color: "violet" },
                    { label: "Régions",      value: regions.length,     color: "teal" },
                    { label: "Départements", value: departements.length, color: "blue" },
                    { label: "Communes",     value: communes.length,    color: "emerald" },
                  ].map(s => (
                    <div key={s.label} className={`bg-gradient-to-br from-${s.color}-50 to-${s.color}-100/50 border border-${s.color}-100 rounded-xl p-4`}>
                      <p className={`text-xs font-semibold text-${s.color}-600 mb-1`}>{s.label}</p>
                      <p className={`text-2xl font-black text-${s.color}-700`}>{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Sub-tabs */}
                <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
                  {locTabs.map(tab => (
                    <button key={tab.id} onClick={() => { setLocalisationTab(tab.id); setLocPage(1); setLocSearch(""); }}
                      className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-all ${localisationTab === tab.id ? "border-b-2 border-teal-500 text-teal-600" : "text-gray-500 hover:text-gray-700"}`}>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Recherche + Filtres */}
                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-[200px] px-3 py-2 bg-white border-2 border-gray-200 rounded-xl focus-within:border-teal-400 transition-colors">
                    <Search size={14} className="text-gray-400 flex-shrink-0" />
                    <input
                      type="text" value={locSearch} placeholder={`Rechercher ${addLabels[localisationTab]}...`}
                      onChange={e => { setLocSearch(e.target.value); setLocPage(1); }}
                      className="flex-1 text-sm outline-none bg-transparent"
                    />
                    {locSearch && <button onClick={() => { setLocSearch(""); setLocPage(1); }}><X size={13} className="text-gray-400 hover:text-gray-600"/></button>}
                  </div>

                  {/* Filtre Pays — visible sur régions, départements, communes */}
                  {localisationTab !== "pays" && (
                    <select value={locFilterPays} onChange={e => { setLocFilterPays(e.target.value); setLocFilterRegion(""); setLocFilterDep(""); setLocPage(1); }}
                      className={`px-3 py-2 border-2 rounded-xl text-sm bg-white focus:outline-none transition-all ${locFilterPays ? "border-teal-400 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                      <option value="">Tous les pays</option>
                      {pays.map(p => <option key={p.id} value={p.id}>{p.drapeau} {p.nom}</option>)}
                    </select>
                  )}

                  {/* Filtre Région — visible sur départements, communes */}
                  {(localisationTab === "departements" || localisationTab === "communes") && (
                    <select value={locFilterRegion} onChange={e => { setLocFilterRegion(e.target.value); setLocFilterDep(""); setLocPage(1); }}
                      className={`px-3 py-2 border-2 rounded-xl text-sm bg-white focus:outline-none transition-all ${locFilterRegion ? "border-teal-400 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                      <option value="">Toutes les régions</option>
                      {(locFilterPays ? regions.filter(r=>r.paysId===locFilterPays) : regions).map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                    </select>
                  )}

                  {/* Filtre Département — visible sur communes */}
                  {localisationTab === "communes" && (
                    <select value={locFilterDep} onChange={e => { setLocFilterDep(e.target.value); setLocPage(1); }}
                      className={`px-3 py-2 border-2 rounded-xl text-sm bg-white focus:outline-none transition-all ${locFilterDep ? "border-teal-400 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                      <option value="">Tous les départements</option>
                      {(locFilterRegion ? departements.filter(d=>d.regionId===locFilterRegion) : departements).map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                    </select>
                  )}

                  {/* Reset filtres */}
                  {(locSearch || locFilterPays || locFilterRegion || locFilterDep) && (
                    <button onClick={() => { setLocSearch(""); setLocFilterPays(""); setLocFilterRegion(""); setLocFilterDep(""); setLocPage(1); }}
                      className="flex items-center gap-1 px-3 py-2 border-2 border-gray-200 text-gray-400 rounded-xl text-xs font-semibold hover:text-gray-600 hover:border-gray-300 bg-white">
                      <X size={12}/> Reset
                    </button>
                  )}
                </div>

                {/* Compteur résultats */}
                <p className="text-xs text-gray-400">
                  {currentData.length} résultat{currentData.length !== 1 ? "s" : ""}
                  {currentData.length !== (dataMap[localisationTab]?.length ?? currentData.length) ? ` sur ${pays.length + regions.length + departements.length + communes.length}` : ""}
                  {totalPages > 1 && ` · Page ${locPage}/${totalPages}`}
                </p>

                {/* PAYS */}
                {localisationTab === "pays" && (
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto"><table className="w-full min-w-[600px]">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>{["#","Pays","Code ISO","Capitale","Téléphone","Régions","Actions"].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {pagedData.map((p, i) => (
                          <tr key={p.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-xs text-gray-400">{i+1}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {p.drapeau && <span className="text-lg">{p.drapeau}</span>}
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">{p.nom}</p>
                                  <p className="text-xs text-gray-400">{p.nomEn}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3"><span className="px-2 py-0.5 bg-violet-50 text-violet-700 text-xs font-bold rounded-lg">{p.codeIso2 || p.code}</span></td>
                            <td className="px-4 py-3 text-sm text-gray-600">{p.capitale || "—"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{p.indicatifTelephonique || "—"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{p.nombreRegions ?? regions.filter(r=>r.paysId===p.id).length}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <button onClick={()=>{setLocalisationForm({nom:p.nom,nomEn:p.nomEn||"",code:p.code||"",codeIso2:p.codeIso2||"",codeIso3:p.codeIso3||"",capitale:p.capitale||"",indicatifTelephonique:p.indicatifTelephonique||"",devise:p.devise||"",fuseauHoraire:p.fuseauHoraire||"",drapeau:p.drapeau||""});setLocalisationTarget(p);setLocalisationModal("editPays");}}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-100">
                                  <Pencil size={11}/> Modifier
                                </button>
                                {(() => { const nb = regions.filter(r=>r.paysId===p.id).length; return nb === 0
                                  ? <button onClick={()=>handleDeleteLocalisation("pays",p)} className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100"><Trash2 size={11}/> Supprimer</button>
                                  : <span title={`${nb} région(s) associée(s)`} className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 text-gray-300 rounded-lg text-xs font-semibold cursor-not-allowed"><Trash2 size={11}/> Supprimer</span>;
                                })()}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    {pagedData.length===0&&<div className="text-center py-12 text-gray-400"><p>Aucun pays{locSearch ? ` pour "${locSearch}"` : ""}</p></div>}
                  </div>
                )}

                {/* RÉGIONS */}
                {localisationTab === "regions" && (
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto"><table className="w-full min-w-[600px]">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>{["#","Région","Code","Pays","Chef-lieu","Dép.","Communes","Actions"].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {pagedData.map((r,i)=>(
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-xs text-gray-400">{i+1}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2"><div className="w-7 h-7 bg-teal-100 rounded-lg flex items-center justify-center"><MapPin size={13} className="text-teal-600"/></div>
                              <span className="font-semibold text-sm text-gray-900">{r.nom}</span></div>
                            </td>
                            <td className="px-4 py-3"><span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-bold rounded-lg">{r.code}</span></td>
                            <td className="px-4 py-3 text-xs text-gray-500">{r.paysNom||pays.find(p=>p.id===r.paysId)?.nom||"—"}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">{r.chefLieu||"—"}</td>
                            <td className="px-4 py-3 text-xs text-gray-600">{r.nombreDepartements??departements.filter(d=>d.regionId===r.id).length}</td>
                            <td className="px-4 py-3 text-xs text-gray-600">{r.nombreCommunes??communes.filter(c=>c.regionId===r.id).length}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <button onClick={()=>{setLocalisationForm({nom:r.nom,code:r.code||"",paysId:r.paysId||"",chefLieu:r.chefLieu||"",latitude:r.latitude||"",longitude:r.longitude||""});setLocalisationTarget(r);setLocalisationModal("editRegion");}}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-100">
                                  <Pencil size={11}/> Modifier
                                </button>
                                {(() => { const nb = departements.filter(d=>d.regionId===r.id).length; return nb === 0
                                  ? <button onClick={()=>handleDeleteLocalisation("region",r)} className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100"><Trash2 size={11}/> Supprimer</button>
                                  : <span title={`${nb} département(s) associé(s)`} className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 text-gray-300 rounded-lg text-xs font-semibold cursor-not-allowed"><Trash2 size={11}/> Supprimer</span>;
                                })()}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    {pagedData.length===0&&<div className="text-center py-12 text-gray-400"><MapPin size={32} className="mx-auto mb-2 opacity-30"/><p>Aucune région{locSearch ? ` pour "${locSearch}"` : ""}</p></div>}
                  </div>
                )}

                {/* DÉPARTEMENTS */}
                {localisationTab === "departements" && (
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto"><table className="w-full min-w-[600px]">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>{["#","Département","Code","Région","Chef-lieu","Communes","Actions"].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {pagedData.map((d,i)=>(
                          <tr key={d.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-xs text-gray-400">{i+1}</td>
                            <td className="px-4 py-3"><span className="font-semibold text-sm text-gray-900">{d.nom}</span></td>
                            <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold rounded-lg">{d.code}</span></td>
                            <td className="px-4 py-3"><span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-semibold rounded-lg">{d.regionNom||regions.find(r=>r.id===d.regionId)?.nom||"—"}</span></td>
                            <td className="px-4 py-3 text-xs text-gray-500">{d.chefLieu||"—"}</td>
                            <td className="px-4 py-3 text-xs text-gray-600">{d.nombreCommunes??communes.filter(c=>c.departementId===d.id).length}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <button onClick={()=>{setLocalisationForm({nom:d.nom,code:d.code||"",regionId:d.regionId||"",chefLieu:d.chefLieu||"",latitude:d.latitude||"",longitude:d.longitude||""});setLocalisationTarget(d);setLocalisationModal("editDep");}}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-100">
                                  <Pencil size={11}/> Modifier
                                </button>
                                {(() => { const nb = communes.filter(c=>c.departementId===d.id).length; return nb === 0
                                  ? <button onClick={()=>handleDeleteLocalisation("dep",d)} className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100"><Trash2 size={11}/> Supprimer</button>
                                  : <span title={`${nb} commune(s) associée(s)`} className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 text-gray-300 rounded-lg text-xs font-semibold cursor-not-allowed"><Trash2 size={11}/> Supprimer</span>;
                                })()}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    {pagedData.length===0&&<div className="text-center py-12 text-gray-400"><p>Aucun département{locSearch ? ` pour "${locSearch}"` : ""}</p></div>}
                  </div>
                )}

                {/* COMMUNES */}
                {localisationTab === "communes" && (
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto"><table className="w-full min-w-[600px]">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>{["#","Commune","Code","Département","Région","Type","Pharmacies","Actions"].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {pagedData.map((c,i)=>(
                          <tr key={c.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-xs text-gray-400">{i+1}</td>
                            <td className="px-4 py-3"><span className="font-semibold text-sm text-gray-900">{c.nom}</span></td>
                            <td className="px-4 py-3"><span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-lg">{c.code}</span></td>
                            <td className="px-4 py-3 text-xs text-gray-500">{c.departementNom||departements.find(d=>d.id===c.departementId)?.nom||"—"}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">{c.regionNom||"—"}</td>
                            <td className="px-4 py-3"><span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-lg">{c.typeLibelle||c.type||"—"}</span></td>
                            <td className="px-4 py-3 text-xs text-gray-600">{pharmacies.filter(p=>p.commune?.id===c.id||p.communeId===c.id).length}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <button onClick={()=>{setLocalisationForm({nom:c.nom,code:c.code||"",departementId:c.departementId||"",type:c.type||"COMMUNE",codePostal:c.codePostal||"",latitude:c.latitude||"",longitude:c.longitude||""});setLocalisationTarget(c);setLocalisationModal("editCommune");}}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-100">
                                  <Pencil size={11}/> Modifier
                                </button>
                                {(() => { const nb = pharmacies.filter(p=>p.commune?.id===c.id||p.communeId===c.id).length; return nb === 0
                                  ? <button onClick={()=>handleDeleteLocalisation("commune",c)} className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100"><Trash2 size={11}/> Supprimer</button>
                                  : <span title={`${nb} pharmacie(s) associée(s)`} className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 text-gray-300 rounded-lg text-xs font-semibold cursor-not-allowed"><Trash2 size={11}/> Supprimer</span>;
                                })()}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                    {pagedData.length===0&&<div className="text-center py-12 text-gray-400"><Building2 size={32} className="mx-auto mb-2 opacity-30"/><p>Aucune commune{locSearch ? ` pour "${locSearch}"` : ""}</p></div>}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-2 py-3">
                    <p className="text-xs text-gray-500">
                      {(locPage - 1) * LOC_PAGE_SIZE + 1}–{Math.min(locPage * LOC_PAGE_SIZE, currentData.length)} sur {currentData.length}
                    </p>
                    <div className="flex items-center gap-1">
                      <button disabled={locPage === 1} onClick={() => setLocPage(1)}
                        className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">«</button>
                      <button disabled={locPage === 1} onClick={() => setLocPage(p => p - 1)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                        <ChevronLeft size={12}/> Préc.
                      </button>
                      {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                        const start = Math.max(1, Math.min(locPage - 2, totalPages - 4));
                        const n = start + i;
                        return n <= totalPages ? (
                          <button key={n} onClick={() => setLocPage(n)}
                            className={`px-3 py-1.5 text-xs border rounded-lg ${n === locPage ? "bg-teal-500 text-white border-teal-500" : "border-gray-200 hover:bg-gray-50"}`}>
                            {n}
                          </button>
                        ) : null;
                      })}
                      <button disabled={locPage === totalPages} onClick={() => setLocPage(p => p + 1)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                        Suiv. <ChevronRight size={12}/>
                      </button>
                      <button disabled={locPage === totalPages} onClick={() => setLocPage(totalPages)}
                        className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">»</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ═══════════════════════════════════════════
              PARAMÈTRES
          ═══════════════════════════════════════════ */}
          {activeTab === "parametres" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profil Admin */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h2 className="font-semibold text-gray-800 mb-5 flex items-center gap-2">
                    <Shield size={16} className="text-green-600" /> Mon compte
                  </h2>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {initials}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-lg">{userName}</p>
                      <p className="text-sm text-gray-500">{userEmail}</p>
                      <span className="mt-1 inline-flex items-center gap-1 px-2.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        <Shield size={11} /> Super Administrateur
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {[
                      { label: "Syndicats gérés",  value: syndicats.length },
                      { label: "Pharmacies",        value: pharmacies.length },
                      { label: "Pharmaciens",       value: pharmaciens.length },
                      { label: "Tickets traités",   value: tickets.filter(t => t.statut === "RESOLU").length },
                      { label: "Patients inscrits", value: patients.length },
                      { label: "Patients premium",  value: patients.filter(p => p.premiumActif).length },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                        <p className="font-bold text-gray-800 text-lg">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions rapides */}
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <h3 className="font-semibold text-gray-800 text-sm mb-4 flex items-center gap-2">
                      <Activity size={15} className="text-blue-600" /> Actions rapides
                    </h3>
                    <div className="space-y-2">
                      {[
                        { label: "Gérer les syndicats", icon: Building2, color: "green", action: () => setActiveTab("syndicats") },
                        { label: "Voir le réseau", icon: Activity, color: "blue", action: () => setActiveTab("reseau") },
                        { label: "Tickets support", icon: TicketIcon, color: "orange", action: () => setActiveTab("support") },
                        { label: "Gestion paiements", icon: CreditCard, color: "purple", action: () => setActiveTab("paiements") },
                      ].map((a, i) => {
                        const Icon = a.icon;
                        return (
                          <button key={i} onClick={a.action}
                            className="w-full flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left">
                            <div className={`w-8 h-8 bg-${a.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <Icon size={14} className={`text-${a.color}-600`} />
                            </div>
                            <span className="text-gray-700 font-medium">{a.label}</span>
                            <ChevronRight size={14} className="ml-auto text-gray-400" />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 py-3 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-all font-medium text-sm"
                  >
                    <LogOut size={16} /> Se déconnecter
                  </button>
                </div>
              </div>

              {/* Tarification */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Banknote size={16} className="text-green-600" /> Tarification de la plateforme
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {[
                    {
                      titre: "Syndicat Commune", montant: "5 500 FCFA", periode: "/ mois",
                      features: ["1 compte principal", "Gestion pharmacies commune", "Plannings de garde", "Support prioritaire"],
                      color: "blue"
                    },
                    {
                      titre: "Syndicat Département", montant: "11 000 FCFA", periode: "/ mois",
                      features: ["2 comptes (principal + secrétaire)", "Gestion multi-communes", "Plannings de garde", "Support prioritaire"],
                      color: "green"
                    },
                    {
                      titre: "Pharmacie", montant: "10 000 FCFA", periode: "/ an",
                      features: ["Visibilité sur la plateforme", "Participation aux gardes", "Gestion planning", "Notifications patients"],
                      color: "teal"
                    },
                    {
                      titre: "Patient Premium", montant: "500 FCFA", periode: "/ mois",
                      sub: "ou 5 000 FCFA / an",
                      features: ["Recherche médicaments", "Notif. gardes en temps réel", "Filtrage par commune", "Essai gratuit 15 jours"],
                      color: "yellow"
                    },
                  ].map((plan, i) => {
                    const colors = {
                      blue:   { border: "border-blue-200",   bg: "bg-blue-50",   text: "text-blue-700",   check: "text-blue-500"   },
                      green:  { border: "border-green-200",  bg: "bg-green-50",  text: "text-green-700",  check: "text-green-500"  },
                      teal:   { border: "border-teal-200",   bg: "bg-teal-50",   text: "text-teal-700",   check: "text-teal-500"   },
                      yellow: { border: "border-yellow-200", bg: "bg-yellow-50", text: "text-yellow-700", check: "text-yellow-500" },
                    };
                    const c = colors[plan.color];
                    return (
                      <div key={i} className={`rounded-xl border-2 ${c.border} ${c.bg} p-5`}>
                        <p className="font-semibold text-gray-800 text-sm mb-2">{plan.titre}</p>
                        <p className={`text-2xl font-bold ${c.text} mb-0.5`}>
                          {plan.montant} <span className="text-sm font-normal text-gray-500">{plan.periode}</span>
                        </p>
                        {plan.sub && <p className="text-xs text-gray-500 mb-2">{plan.sub}</p>}
                        <ul className="space-y-1.5 mt-3">
                          {plan.features.map((f, j) => (
                            <li key={j} className="flex items-start gap-2 text-xs text-gray-600">
                              <CheckCircle size={12} className={`mt-0.5 flex-shrink-0 ${c.check}`} />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                  <span className="text-gray-500">Frais de validation pharmacie (unique)</span>
                  <span className="font-bold text-gray-800">2 000 FCFA</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500">Essai gratuit syndicat</span>
                  <span className="font-bold text-gray-800">30 jours</span>
                </div>
              </div>

              {/* Statistiques plateforme */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp size={16} className="text-blue-600" /> Statistiques plateforme
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Taux d'activation syndicats", value: syndicats.length > 0 ? Math.round(syndicats.filter(s=>s.statut==="ACTIF").length / syndicats.length * 100) + "%" : "—", color: "green" },
                    { label: "Revenus mensuels estimés", value: (syndicats.reduce((sum, s) => sum + (s.montantMensuel || 5500), 0)).toLocaleString("fr-FR") + " FCFA", color: "blue" },
                    { label: "Pharmacies validées", value: pharmacies.length > 0 ? Math.round(pharmacies.filter(p=>p.statut==="VALIDEE").length / pharmacies.length * 100) + "%" : "—", color: "purple" },
                    { label: "Tickets résolus", value: tickets.length > 0 ? Math.round(tickets.filter(t=>t.statut==="RESOLU").length / tickets.length * 100) + "%" : "—", color: "orange" },
                  ].map((s, i) => (
                    <div key={i} className="text-center p-4 bg-gray-50 rounded-xl">
                      <p className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</p>
                      <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════
              LIVRAISONS
          ═══════════════════════════════════════════ */}
          {activeTab === "livraisons" && <LivraisonOperateur />}

          {/* ═══════════════════════════════════════════
              DEMANDES INSCRIPTION PHARMACIE
          ═══════════════════════════════════════════ */}
          {activeTab === "demandes" && (() => {
            const STATUT_CFG = {
              EN_ATTENTE: { label: "En attente", bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500", border: "border-orange-200", card: "border-l-orange-400" },
              EN_COURS:   { label: "En cours",   bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500",   border: "border-blue-200",   card: "border-l-blue-400" },
              TERMINEE:   { label: "Terminée",   bg: "bg-emerald-100",text: "text-emerald-700",dot: "bg-emerald-500",border: "border-emerald-200",card: "border-l-emerald-400" },
            };
            const StatutDemandeBadge = ({ statut }) => {
              const c = STATUT_CFG[statut] || STATUT_CFG.EN_ATTENTE;
              return (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{c.label}
                </span>
              );
            };

            const q = demandeSearch.toLowerCase();
            const filtered = demandes.filter(d => {
              const matchSearch = !q ||
                d.nomPharmacie?.toLowerCase().includes(q) ||
                d.commune?.toLowerCase().includes(q) ||
                d.prenomTitulaire?.toLowerCase().includes(q) ||
                d.nomTitulaire?.toLowerCase().includes(q) ||
                d.telephoneTitulaire?.includes(q);
              const matchStatut = !demandeStatutFilter || d.statut === demandeStatutFilter;
              return matchSearch && matchStatut;
            });
            const paginated = filtered.slice((demandePage - 1) * PAGE_SIZE, demandePage * PAGE_SIZE);

            const nbAttente = demandes.filter(d => d.statut === "EN_ATTENTE").length;
            const nbCours   = demandes.filter(d => d.statut === "EN_COURS").length;
            const nbTermin  = demandes.filter(d => d.statut === "TERMINEE").length;

            const handleStatut = async (id, statut) => {
              setDemandeActionLoading(id + statut);
              try {
                const updated = await adminApi.changerStatutDemande(id, statut);
                setDemandes(prev => prev.map(d => d.id === id ? { ...d, statut: updated.statut, traiteeAt: updated.traiteeAt } : d));
                if (selectedDemandeDetail?.id === id) setSelectedDemandeDetail(prev => ({ ...prev, statut: updated.statut }));
              } catch (e) { alert(e.message); }
              finally { setDemandeActionLoading(null); }
            };

            const handleDelete = async (id) => {
              if (!window.confirm("Supprimer cette demande ? Cette action est irréversible.")) return;
              setDemandeActionLoading(id + "DELETE");
              try {
                await adminApi.supprimerDemande(id);
                setDemandes(prev => prev.filter(d => d.id !== id));
                if (selectedDemandeDetail?.id === id) setSelectedDemandeDetail(null);
              } catch (e) { alert(e.message); }
              finally { setDemandeActionLoading(null); }
            };

            const saveNotes = async () => {
              if (!selectedDemandeDetail) return;
              setDemandeNotesSaving(true);
              try {
                await adminApi.updateNotesDemande(selectedDemandeDetail.id, demandeNotes);
                setDemandes(prev => prev.map(d => d.id === selectedDemandeDetail.id ? { ...d, notesAdmin: demandeNotes } : d));
                setSelectedDemandeDetail(prev => ({ ...prev, notesAdmin: demandeNotes }));
              } catch (e) { alert(e.message); }
              finally { setDemandeNotesSaving(false); }
            };

            const parseJson = (str) => { try { return JSON.parse(str); } catch { return null; } };
            const JOURS_ORD = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];
            const SERVICE_LABELS = {
              garde_nuit: "Garde de nuit", preparations: "Préparations", materiel_medical: "Matériel médical",
              parapharmacie: "Parapharmacie", veterinaire: "Vétérinaire", orthopedie: "Orthopédie",
              test_rapide: "Tests rapides", livraison: "Livraison"
            };

            return (
              <div className="space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">Demandes d'inscription</h2>
                    <p className="text-sm text-gray-500 mt-0.5">Pharmacies souhaitant rejoindre SunuFarmasi via la landing page</p>
                  </div>
                  <button onClick={loadAll} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">
                    <RefreshCw size={14} />Actualiser
                  </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: "En attente",  count: nbAttente, cfg: STATUT_CFG.EN_ATTENTE, id: "EN_ATTENTE" },
                    { label: "En cours",    count: nbCours,   cfg: STATUT_CFG.EN_COURS,   id: "EN_COURS" },
                    { label: "Terminées",   count: nbTermin,  cfg: STATUT_CFG.TERMINEE,   id: "TERMINEE" },
                  ].map(s => (
                    <button key={s.id}
                      onClick={() => { setDemandeStatutFilter(demandeStatutFilter === s.id ? "" : s.id); setDemandePage(1); }}
                      className={`bg-white rounded-2xl p-4 border-2 text-left transition-all hover:shadow-md ${
                        demandeStatutFilter === s.id ? `${s.cfg.border} shadow-md` : "border-transparent shadow-sm"
                      }`}>
                      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${s.cfg.bg} ${s.cfg.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${s.cfg.dot}`} />{s.label}
                      </div>
                      <p className="text-3xl font-black text-gray-800">{s.count}</p>
                    </button>
                  ))}
                </div>

                {/* Filtres */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={demandeSearch} onChange={e => { setDemandeSearch(e.target.value); setDemandePage(1); }}
                      placeholder="Rechercher pharmacie, titulaire, téléphone…"
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                  </div>
                  <select value={demandeStatutFilter} onChange={e => { setDemandeStatutFilter(e.target.value); setDemandePage(1); }}
                    className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white">
                    <option value="">Tous les statuts</option>
                    <option value="EN_ATTENTE">En attente</option>
                    <option value="EN_COURS">En cours</option>
                    <option value="TERMINEE">Terminées</option>
                  </select>
                </div>

                {/* Liste */}
                {paginated.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
                    <ClipboardList size={40} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Aucune demande trouvée</p>
                    <p className="text-gray-400 text-sm mt-1">Les demandes du formulaire landing page apparaîtront ici</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paginated.map(d => {
                      const cfg = STATUT_CFG[d.statut] || STATUT_CFG.EN_ATTENTE;
                      const services = parseJson(d.servicesJson) || [];
                      const dateStr = d.createdAt ? new Date(d.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                      return (
                        <div key={d.id} className={`bg-white rounded-2xl border border-gray-100 shadow-sm border-l-4 ${cfg.card} overflow-hidden`}>
                          <div className="p-4 flex flex-col sm:flex-row sm:items-start gap-4">
                            {/* Info principale */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 flex-wrap">
                                <h3 className="font-bold text-gray-800 text-base">{d.nomPharmacie}</h3>
                                <StatutDemandeBadge statut={d.statut} />
                                {d.planChoisi === "PREMIUM" && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">⭐ Premium</span>
                                )}
                                {d.planChoisi === "ESSENTIEL" && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">✅ Essentiel</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                                <MapPin size={13} className="shrink-0" />
                                <span>{d.commune}, {d.departement} · {d.region}</span>
                              </div>
                              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                                <span className="flex items-center gap-1 font-medium text-gray-700">
                                  <User size={13} />{d.prenomTitulaire} {d.nomTitulaire}
                                </span>
                                <a href={`tel:${d.telephoneTitulaire}`} className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700">
                                  <Phone size={13} />{d.telephoneTitulaire}
                                </a>
                                {d.emailTitulaire && (
                                  <a href={`mailto:${d.emailTitulaire}`} className="flex items-center gap-1 text-blue-500 hover:text-blue-600">
                                    <Mail size={13} />{d.emailTitulaire}
                                  </a>
                                )}
                              </div>
                              {/* Services chips */}
                              {services.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {services.map(s => (
                                    <span key={s} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                                      {SERVICE_LABELS[s] || s}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <p className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                                <Clock size={11} />{dateStr}
                              </p>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2 shrink-0">
                              <button onClick={() => { setSelectedDemandeDetail(d); setDemandeNotes(d.notesAdmin || ""); }}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-all">
                                <Eye size={13} />Voir détail
                              </button>
                              {d.statut === "EN_ATTENTE" && (
                                <button onClick={() => handleStatut(d.id, "EN_COURS")}
                                  disabled={demandeActionLoading === d.id + "EN_COURS"}
                                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-all">
                                  <ArrowRight size={13} />
                                  {demandeActionLoading === d.id + "EN_COURS" ? "…" : "Prendre en charge"}
                                </button>
                              )}
                              {d.statut === "EN_COURS" && (
                                <button onClick={() => handleStatut(d.id, "TERMINEE")}
                                  disabled={demandeActionLoading === d.id + "TERMINEE"}
                                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-all">
                                  <CheckCircle size={13} />
                                  {demandeActionLoading === d.id + "TERMINEE" ? "…" : "Marquer terminée"}
                                </button>
                              )}
                              <button onClick={() => handleDelete(d.id)}
                                disabled={demandeActionLoading === d.id + "DELETE"}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-red-200 text-red-400 rounded-lg hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-all">
                                <Trash2 size={13} />
                                {demandeActionLoading === d.id + "DELETE" ? "…" : "Supprimer"}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <Pagination total={filtered.length} page={demandePage} setPage={setDemandePage} />
                  </div>
                )}

                {/* Modal détail */}
                {selectedDemandeDetail && (() => {
                  const d = selectedDemandeDetail;
                  const cfg = STATUT_CFG[d.statut] || STATUT_CFG.EN_ATTENTE;
                  const horaires = parseJson(d.horairesJson);
                  const services = parseJson(d.servicesJson) || [];
                  return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                        {/* Header modal */}
                        <div className="flex items-start justify-between p-5 border-b border-gray-100">
                          <div>
                            <h2 className="font-bold text-gray-800 text-lg">{d.nomPharmacie}</h2>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
                              </span>
                              <span className="text-xs text-gray-400">
                                {d.createdAt ? new Date(d.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—"}
                              </span>
                            </div>
                          </div>
                          <button onClick={() => setSelectedDemandeDetail(null)} className="p-2 rounded-full hover:bg-gray-100 text-gray-400">
                            <X size={18} />
                          </button>
                        </div>

                        {/* Contenu scrollable */}
                        <div className="flex-1 overflow-y-auto p-5 space-y-5">

                          {/* Offre choisie */}
                          {d.planChoisi && (
                            <div className={`rounded-xl p-4 border ${
                              d.planChoisi === "PREMIUM"
                                ? "bg-indigo-50 border-indigo-200"
                                : "bg-emerald-50 border-emerald-200"
                            }`}>
                              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Offre choisie</h4>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className={`font-black text-lg ${d.planChoisi === "PREMIUM" ? "text-indigo-700" : "text-emerald-700"}`}>
                                    {d.planChoisi === "PREMIUM" ? "⭐ Premium" : "✅ Essentiel"}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {d.planChoisi === "PREMIUM"
                                      ? "Visibilité + dashboard complet (horaires, gardes, syndicat)"
                                      : "Fiche visible sur l'app pour être trouvé par les patients"}
                                  </p>
                                </div>
                                <div className={`text-right px-4 py-2 rounded-xl ${d.planChoisi === "PREMIUM" ? "bg-indigo-100" : "bg-emerald-100"}`}>
                                  <p className={`font-black text-xl ${d.planChoisi === "PREMIUM" ? "text-indigo-700" : "text-emerald-700"}`}>
                                    {d.planChoisi === "PREMIUM" ? "15 000" : "10 000"}
                                  </p>
                                  <p className="text-xs text-gray-500">FCFA/mois</p>
                                </div>
                              </div>
                              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                <p className="text-xs font-bold text-amber-700">🎁 1er mois gratuit inclus</p>
                              </div>
                            </div>
                          )}

                          {/* Pharmacie */}
                          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Pharmacie</h4>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              <div><span className="text-gray-400">Région</span><p className="font-medium text-gray-700">{d.region}</p></div>
                              <div><span className="text-gray-400">Département</span><p className="font-medium text-gray-700">{d.departement}</p></div>
                              <div><span className="text-gray-400">Commune</span><p className="font-medium text-gray-700">{d.commune}</p></div>
                              <div><span className="text-gray-400">Téléphone</span>
                                <a href={`tel:${d.telephonePharmacie}`} className="font-medium text-emerald-600">{d.telephonePharmacie}</a>
                              </div>
                              <div className="col-span-2"><span className="text-gray-400">Adresse</span><p className="font-medium text-gray-700">{d.adresse}</p></div>
                              {d.emailPharmacie && <div className="col-span-2"><span className="text-gray-400">Email pharmacie</span><p className="font-medium text-gray-700">{d.emailPharmacie}</p></div>}
                            </div>
                          </div>

                          {/* Localisation */}
                          {(d.mapsLink || (d.latitude && d.longitude)) && (
                            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
                              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Localisation</h4>
                              {d.mapsLink ? (
                                <a href={d.mapsLink} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
                                  <MapPin size={14} />Voir sur Google Maps ↗
                                </a>
                              ) : (
                                <a href={`https://www.google.com/maps?q=${d.latitude},${d.longitude}`} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
                                  <MapPin size={14} />GPS : {d.latitude}, {d.longitude} ↗
                                </a>
                              )}
                            </div>
                          )}

                          {/* Titulaire */}
                          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Pharmacien titulaire</h4>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              <div><span className="text-gray-400">Nom complet</span><p className="font-medium text-gray-700">{d.prenomTitulaire} {d.nomTitulaire}</p></div>
                              <div><span className="text-gray-400">Téléphone</span>
                                <a href={`tel:${d.telephoneTitulaire}`} className="font-medium text-emerald-600">{d.telephoneTitulaire}</a>
                              </div>
                              {d.emailTitulaire && <div><span className="text-gray-400">Email</span><p className="font-medium text-gray-700">{d.emailTitulaire}</p></div>}
                              {d.licenceTitulaire && <div><span className="text-gray-400">Licence</span><p className="font-medium font-mono text-gray-700">{d.licenceTitulaire}</p></div>}
                            </div>
                            {d.messageTitulaire && (
                              <div className="mt-3 bg-white border border-gray-200 rounded-lg p-3">
                                <p className="text-xs text-gray-400 mb-1">Message</p>
                                <p className="text-sm text-gray-700 italic">{d.messageTitulaire}</p>
                              </div>
                            )}
                          </div>

                          {/* Horaires */}
                          {horaires && (
                            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Horaires d'ouverture</h4>
                              <div className="space-y-1.5">
                                {JOURS_ORD.map(jour => {
                                  const h = horaires[jour];
                                  if (!h) return null;
                                  return (
                                    <div key={jour} className="flex items-center gap-3 text-sm">
                                      <span className="w-24 text-gray-500 font-medium">{jour}</span>
                                      <span className="text-emerald-600 font-semibold">{h.ouverture}</span>
                                      <span className="text-gray-400">→</span>
                                      <span className="text-red-500 font-semibold">{h.fermeture}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Services */}
                          {services.length > 0 && (
                            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Services proposés</h4>
                              <div className="flex flex-wrap gap-2">
                                {services.map(s => (
                                  <span key={s} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                                    {SERVICE_LABELS[s] || s}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Notes admin */}
                          <div className="rounded-xl bg-yellow-50 border border-yellow-100 p-4">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <StickyNote size={12} />Notes internes
                            </h4>
                            <textarea value={demandeNotes} onChange={e => setDemandeNotes(e.target.value)} rows={3}
                              placeholder="Ajouter une note de suivi…"
                              className="w-full px-3 py-2 text-sm border border-yellow-200 rounded-lg bg-white resize-none focus:outline-none focus:ring-2 focus:ring-yellow-300" />
                            <button onClick={saveNotes} disabled={demandeNotesSaving}
                              className="mt-2 px-3 py-1.5 text-xs font-semibold bg-yellow-400 text-yellow-900 rounded-lg hover:bg-yellow-500 disabled:opacity-50 transition-all">
                              {demandeNotesSaving ? "Enregistrement…" : "Sauvegarder la note"}
                            </button>
                          </div>
                        </div>

                        {/* Footer modal — actions statut */}
                        <div className="p-4 border-t border-gray-100 flex items-center gap-3">
                          <button onClick={() => setSelectedDemandeDetail(null)}
                            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50">
                            Fermer
                          </button>
                          {d.statut === "EN_ATTENTE" && (
                            <button onClick={() => handleStatut(d.id, "EN_COURS")}
                              disabled={demandeActionLoading === d.id + "EN_COURS"}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 transition-all">
                              <ArrowRight size={14} />
                              {demandeActionLoading === d.id + "EN_COURS" ? "…" : "Prendre en charge"}
                            </button>
                          )}
                          {d.statut === "EN_COURS" && (
                            <button onClick={() => handleStatut(d.id, "TERMINEE")}
                              disabled={demandeActionLoading === d.id + "TERMINEE"}
                              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50 transition-all">
                              <CheckCircle size={14} />
                              {demandeActionLoading === d.id + "TERMINEE" ? "…" : "Marquer terminée"}
                            </button>
                          )}
                          {d.statut === "EN_ATTENTE" && (
                            <button onClick={() => handleStatut(d.id, "TERMINEE")}
                              disabled={demandeActionLoading === d.id + "TERMINEE"}
                              className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-500 rounded-xl text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 transition-all">
                              Terminer directement
                            </button>
                          )}
                          <button onClick={() => handleDelete(d.id)}
                            disabled={demandeActionLoading === d.id + "DELETE"}
                            className="ml-auto flex items-center gap-2 px-3 py-2 border border-red-200 text-red-400 rounded-xl text-xs font-semibold hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-all">
                            <Trash2 size={13} />
                            {demandeActionLoading === d.id + "DELETE" ? "Suppression…" : "Supprimer"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

        </main>

        {/* ── Modals Localisation ── */}
        {localisationModal && (() => {
          const isAdd = localisationModal.startsWith("add");
          const type  = localisationModal.replace(/^add|^edit/,"").toLowerCase();
          const titles = { pays:"pays", region:"région", dep:"département", commune:"commune" };
          const f = localisationForm;
          const set = (k,v) => setLocalisationForm(prev=>({...prev,[k]:v}));
          const inp = (label,key,placeholder) => (
            <div key={key}>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
              <input type="text" value={f[key]||""} onChange={e=>set(key,e.target.value)} placeholder={placeholder||""}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-400 text-sm"/>
            </div>
          );
          const save = async () => {
            setLocalisationLoading(true);
            const apis = {
              pays:    { create: () => localisationApi.createPays(f),                    update: () => localisationApi.updatePays(localisationTarget.id, f),    setter: setPays },
              region:  { create: () => localisationApi.createRegion(f),                  update: () => localisationApi.updateRegion(localisationTarget.id, f),  setter: setRegions },
              dep:     { create: () => localisationApi.createDepartement(f),             update: () => localisationApi.updateDepartement(localisationTarget.id, f), setter: setDepartements },
              commune: { create: () => localisationApi.createCommune(f),                 update: () => localisationApi.updateCommune(localisationTarget.id, f), setter: setCommunes },
            };
            const entry = apis[type];
            try {
              const saved = isAdd ? await entry.create() : await entry.update();
              if (isAdd) entry.setter(prev => [...prev, saved]);
              else entry.setter(prev => prev.map(x => x.id === localisationTarget.id ? saved : x));
              setLocalisationModal(null);
            } catch (e) { alert("Erreur : " + e.message); }
            setLocalisationLoading(false);
          };
          const isValid = f.nom && (
            type==="pays" ? (f.code && f.codeIso2) :
            type==="region" ? f.paysId :
            type==="dep" ? f.regionId :
            f.departementId
          );
          return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
                  <h3 className="text-lg font-bold text-gray-900">{isAdd?"Ajouter":"Modifier"} {isAdd ? "un" : "le"} {titles[type]}</h3>
                  <button onClick={()=>setLocalisationModal(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18}/></button>
                </div>
                <div className="p-6 space-y-4">
                  {inp("Nom *","nom",type==="pays"?"Ex: Sénégal":type==="region"?"Ex: Dakar":type==="dep"?"Ex: Dakar":"")}
                  {inp("Code *","code",type==="pays"?"Ex: SN":type==="region"?"Ex: DK":type==="dep"?"Ex: DK-DK":"Ex: DK-VILLE")}

                  {/* PAYS champs */}
                  {type==="pays" && <>
                    {inp("Code ISO 2 *","codeIso2","Ex: SN")}
                    {inp("Code ISO 3","codeIso3","Ex: SEN")}
                    {inp("Nom anglais","nomEn","Ex: Senegal")}
                    {inp("Capitale","capitale","Ex: Dakar")}
                    {inp("Indicatif téléphonique","indicatifTelephonique","Ex: +221")}
                    {inp("Devise","devise","Ex: XOF")}
                    {inp("Fuseau horaire","fuseauHoraire","Ex: Africa/Dakar")}
                    {inp("Drapeau (emoji)","drapeau","🇸🇳")}
                  </>}

                  {/* RÉGION champs */}
                  {type==="region" && <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Pays *</label>
                      <select value={f.paysId||""} onChange={e=>set("paysId",e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-400 text-sm bg-white">
                        <option value="">Sélectionner un pays...</option>
                        {pays.map(p=><option key={p.id} value={p.id}>{p.drapeau} {p.nom}</option>)}
                      </select>
                    </div>
                    {inp("Chef-lieu","chefLieu","Ex: Dakar")}
                    <div className="grid grid-cols-2 gap-3">
                      {inp("Latitude","latitude","14.69")}
                      {inp("Longitude","longitude","-17.44")}
                    </div>
                  </>}

                  {/* DÉPARTEMENT champs */}
                  {type==="dep" && <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Région *</label>
                      <select value={f.regionId||""} onChange={e=>set("regionId",e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-400 text-sm bg-white">
                        <option value="">Sélectionner une région...</option>
                        {regions.map(r=><option key={r.id} value={r.id}>{r.nom}</option>)}
                      </select>
                    </div>
                    {inp("Chef-lieu","chefLieu","Ex: Dakar")}
                    <div className="grid grid-cols-2 gap-3">
                      {inp("Latitude","latitude","")}
                      {inp("Longitude","longitude","")}
                    </div>
                  </>}

                  {/* COMMUNE champs */}
                  {type==="commune" && <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Département *</label>
                      <select value={f.departementId||""} onChange={e=>set("departementId",e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-400 text-sm bg-white">
                        <option value="">Sélectionner un département...</option>
                        {departements.map(d=><option key={d.id} value={d.id}>{d.nom} ({d.regionNom||""})</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type</label>
                      <select value={f.type||"COMMUNE"} onChange={e=>set("type",e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-400 text-sm bg-white">
                        {["COMMUNE","VILLE","COMMUNE_ARRONDISSEMENT","VILLAGE","CAPITALE","CHEF_LIEU_REGION","CHEF_LIEU_DEPARTEMENT"].map(t=><option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {inp("Code postal","codePostal","Ex: 13000")}
                    <div className="grid grid-cols-2 gap-3">
                      {inp("Latitude","latitude","")}
                      {inp("Longitude","longitude","")}
                    </div>
                  </>}
                </div>
                <div className="p-6 pt-0 flex gap-3">
                  <button onClick={()=>setLocalisationModal(null)} className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-50">Annuler</button>
                  <button disabled={localisationLoading||!isValid} onClick={save}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-semibold text-sm disabled:opacity-50">
                    {localisationLoading?"Enregistrement...":"Enregistrer"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Footer ── */}
        <footer className="border-t border-gray-200 bg-white px-6 py-4 mt-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 bg-gradient-to-br from-green-600 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield size={14} className="text-white" />
              </div>
              <div>
                <span className="text-sm font-semibold text-gray-700">SunuFarmasi</span>
                <span className="text-gray-400 text-xs ml-2">Super Admin · v1.0</span>
              </div>
              <div className="flex items-center gap-1 bg-green-50 border border-green-100 rounded-full px-2.5 py-0.5 ml-1">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-600 text-xs font-medium">Système opérationnel</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <a href="mailto:admin@sunufarmasi.sn" className="hover:text-green-600 transition-colors flex items-center gap-1">
                <Mail size={12} /> admin@sunufarmasi.sn
              </a>
              <span className="text-gray-200">|</span>
              <a href="tel:+221770000000" className="hover:text-green-600 transition-colors flex items-center gap-1">
                <Phone size={12} /> +221 77 479 24 57
              </a>
              <span className="text-gray-200">|</span>
              <span>© 2025 SunuFarmasi</span>
            </div>
          </div>
        </footer>
      </div>

      {/* ── Modal Enregistrer Paiement ── */}
      {showPaiementModal && paiementSyndicat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">
                {paiementSyndicat?._renouvelerMois ? "Renouveler l'abonnement" : "Enregistrer un paiement"}
              </h3>
              <button onClick={() => setShowPaiementModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 mb-5">
              <p className="text-sm font-semibold text-gray-800">{paiementSyndicat.nom}</p>
              <p className="text-xs text-gray-500">{paiementSyndicat.code} · Plan : {paiementSyndicat.plan || "STANDARD"}</p>
              <p className="text-base font-bold text-green-600 mt-1">{(paiementSyndicat.montantMensuel || 5500).toLocaleString("fr-FR")} FCFA</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Date de paiement *</label>
                <input type="date" value={paiementForm.datePaiement} onChange={e => setPaiementForm({...paiementForm, datePaiement: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Référence de paiement *</label>
                <input type="text" placeholder="Ex: WAVE-2025-0042, OM-REF-123…" value={paiementForm.reference} onChange={e => setPaiementForm({...paiementForm, reference: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
                <input type="text" placeholder="Optionnel..." value={paiementForm.notes} onChange={e => setPaiementForm({...paiementForm, notes: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowPaiementModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium">Annuler</button>
              <button onClick={handleEnregistrerPaiement} disabled={paiementLoading || !paiementForm.reference.trim()} className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                {paiementLoading ? "Enregistrement..." : paiementSyndicat?._renouvelerMois ? "Renouveler" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Détail Patient ── */}
      {selectedPatientDetail && (() => {
        const p = selectedPatientDetail;
        const nom = p.nomComplet || `${p.prenom || ""} ${p.nom || ""}`.trim() || "—";
        const expiry = p.dateFinPremium ? new Date(p.dateFinPremium) : null;
        const daysLeft = expiry ? Math.ceil((expiry - new Date()) / 86400000) : null;
        const isTrial = p.premiumActif && (p.montantPremium === 0 || !p.montantPremium);
        const isPaid = p.premiumActif && p.montantPremium > 0;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[92vh] flex flex-col">
              {/* Header */}
              <div className="p-5 border-b border-gray-100 flex items-center gap-4 flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {nom[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-800 text-lg leading-tight">{nom}</h3>
                  <p className="text-sm text-gray-400">{p.email || "—"}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatutBadge statut={p.actif ? "ACTIF" : "SUSPENDU"} />
                  {isTrial && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-lg border ${daysLeft != null && daysLeft <= 0 ? "bg-red-50 text-red-700 border-red-100" : daysLeft != null && daysLeft <= 3 ? "bg-orange-50 text-orange-700 border-orange-100" : "bg-blue-50 text-blue-700 border-blue-100"}`}>
                      🎁 Essai{daysLeft != null ? ` · ${daysLeft}j` : ""}
                    </span>
                  )}
                  {isPaid && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-lg border ${daysLeft != null && daysLeft <= 0 ? "bg-red-50 text-red-700 border-red-100" : daysLeft != null && daysLeft <= 7 ? "bg-orange-50 text-orange-700 border-orange-100" : "bg-yellow-50 text-yellow-700 border-yellow-100"}`}>
                      <Star size={10} fill="currentColor"/> Premium{daysLeft != null ? ` · ${daysLeft}j` : ""}
                    </span>
                  )}
                  {!isTrial && !isPaid && (
                    <span className="inline-flex px-2 py-0.5 bg-gray-50 text-gray-500 text-xs font-medium rounded-lg border border-gray-100">Gratuit</span>
                  )}
                </div>
                <button onClick={() => setSelectedPatientDetail(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={20}/></button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto flex-1 p-5 space-y-4">
                {/* Infos personnelles */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Informations personnelles</p>
                  {[
                    { label: "Téléphone",    value: p.telephone || "—",                            icon: <Phone size={13}/> },
                    { label: "Sexe",         value: p.sexe === "M" ? "Masculin" : p.sexe === "F" ? "Féminin" : "—", icon: <User size={13}/> },
                    { label: "Naissance",    value: p.dateNaissance ? new Date(p.dateNaissance).toLocaleDateString("fr-FR") : "—", icon: <Calendar size={13}/> },
                    { label: "Localité",     value: p.commune?.nom || "—",                         icon: <MapPin size={13}/> },
                    { label: "Inscription",  value: p.createdAt ? new Date(p.createdAt).toLocaleDateString("fr-FR") : "—", icon: <Clock size={13}/> },
                    { label: "Dernière conn.", value: p.lastLoginAt ? new Date(p.lastLoginAt).toLocaleDateString("fr-FR") : "—", icon: <Clock size={13}/> },
                  ].map(({ label, value, icon }) => (
                    <div key={label} className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 flex items-center gap-1.5">{icon}{label}</span>
                      <span className="font-medium text-gray-800">{value}</span>
                    </div>
                  ))}
                </div>

                {/* Vérifications */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Vérifications</p>
                  <div className="flex gap-3">
                    <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${p.emailVerified ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                      {p.emailVerified ? "✓" : "✗"} Email
                    </div>
                    <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${p.telephoneVerified ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                      {p.telephoneVerified ? "✓" : "✗"} Téléphone
                    </div>
                  </div>
                </div>

                {/* Premium */}
                <div className={`rounded-xl p-4 ${isPaid ? "bg-amber-50 border border-amber-100" : isTrial ? "bg-blue-50 border border-blue-100" : "bg-gray-50 border border-gray-100"}`}>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Abonnement</p>
                  {[
                    { label: "Statut",         value: isTrial ? "Essai gratuit" : isPaid ? "Premium actif" : "Gratuit" },
                    { label: "Montant payé",   value: p.montantPremium ? `${p.montantPremium.toLocaleString("fr-FR")} FCFA` : "—" },
                    { label: "Expire le",      value: expiry ? expiry.toLocaleDateString("fr-FR") : "—" },
                    { label: "Jours restants", value: daysLeft != null ? (daysLeft > 0 ? `${daysLeft} jours` : "Expiré") : "—" },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-gray-500">{label}</span>
                      <span className="font-semibold text-gray-800">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer actions */}
              <div className="p-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
                <button onClick={() => openPatientPremiumModal(p.id, 1, nom)}
                  className="flex-1 py-2 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-xl text-xs font-semibold hover:bg-yellow-100">
                  ⭐ Premium 1M
                </button>
                <button onClick={() => openPatientPremiumModal(p.id, 12, nom)}
                  className="flex-1 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-xs font-semibold hover:bg-amber-100">
                  ⭐ Premium 1An
                </button>
                {p.actif ? (
                  <button onClick={() => { handleSuspendrePatient(p.id); setSelectedPatientDetail(null); }}
                    className="flex-1 py-2 bg-red-50 text-red-600 border border-red-100 rounded-xl text-xs font-semibold hover:bg-red-100">
                    ⊘ Suspendre
                  </button>
                ) : (
                  <button onClick={() => { handleReactiverPatient(p.id); setSelectedPatientDetail(null); }}
                    className="flex-1 py-2 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-xl text-xs font-semibold hover:bg-emerald-100">
                    ✓ Réactiver
                  </button>
                )}
                <button onClick={() => setSelectedPatientDetail(null)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-xs font-medium hover:bg-gray-50">
                  Fermer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal Paiement Pharmacie ── */}
      {showPharmPaiementModal && pharmPaiementTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">
                {pharmPaiementTarget.type === "renouveler" ? "Renouveler l'abonnement" : "Enregistrer le paiement"}
              </h3>
              <button onClick={() => setShowPharmPaiementModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 mb-5 border border-gray-100">
              <p className="text-sm font-semibold text-gray-800">{pharmPaiementTarget.nom}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {pharmPaiementTarget.type === "renouveler" ? "1 an (12 mois) · 5 000 FCFA" : "Activation · 5 000 FCFA"}
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Référence de paiement *</label>
              <input type="text" value={pharmPaiementRef} onChange={e => setPharmPaiementRef(e.target.value)}
                placeholder="Ex: WAVE-2025-0042, OM-REF-123…"
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"/>
              <p className="text-xs text-gray-400 mt-1">La référence est obligatoire pour valider l'opération.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowPharmPaiementModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium">Annuler</button>
              <button onClick={handleConfirmPharmPaiement} disabled={pharmPaiementLoading || !pharmPaiementRef.trim()}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors">
                {pharmPaiementLoading ? "Enregistrement..." : pharmPaiementTarget.type === "renouveler" ? "Renouveler" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Activation Premium Patient ── */}
      {showPatientPremiumModal && patientPremiumTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">Activer le Premium</h3>
              <button onClick={() => setShowPatientPremiumModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 mb-5 border border-amber-100">
              <p className="text-sm font-semibold text-gray-800">{patientPremiumTarget.nom || "Patient"}</p>
              <p className="text-xs text-amber-700 mt-0.5 font-medium">
                {patientPremiumTarget.mois === 12 ? "1 an (12 mois) · 5 000 FCFA" : `${patientPremiumTarget.mois} mois · ${patientPremiumTarget.mois * 500} FCFA`}
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Référence de paiement *</label>
              <input type="text" value={patientPremiumRef} onChange={e => setPatientPremiumRef(e.target.value)}
                placeholder="Ex: WAVE-2025-0042, OM-REF-123…"
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition-all"/>
              <p className="text-xs text-gray-400 mt-1">La référence est obligatoire pour valider l'opération.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowPatientPremiumModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium">Annuler</button>
              <button onClick={handleConfirmPatientPremium} disabled={patientPremiumLoading || !patientPremiumRef.trim()}
                className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 disabled:opacity-50 transition-colors">
                {patientPremiumLoading ? "Activation..." : "Activer Premium"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Ajouter Pharmacie ── */}
      {showAddPharmacieModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Ajouter une pharmacie</h3>
                <p className="text-xs text-gray-500 mt-0.5">La pharmacie sera immédiatement validée</p>
              </div>
              <button onClick={() => { setShowAddPharmacieModal(false); setSelectedRegionId(""); setCommunesList([]); setDetectedSyndicat(null); setErreursPharmacie({}); }} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              {/* Infos générales */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-600 uppercase">Informations générales</p>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Nom de la pharmacie *</label>
                  <input type="text" value={newPharmacie.nom} placeholder="Pharmacie du Plateau"
                    onChange={e => { setNewPharmacie({...newPharmacie, nom: e.target.value}); if (erreursPharmacie.nom) setErreursPharmacie(p => ({...p, nom: ""})); }}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${erreursPharmacie.nom ? "border-red-400" : "border-gray-200"}`} />
                  {erreursPharmacie.nom && <p className="text-xs text-red-500 mt-1">{erreursPharmacie.nom}</p>}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
                  <textarea value={newPharmacie.description} placeholder="Présentation de la pharmacie..."
                    onChange={e => setNewPharmacie({...newPharmacie, description: e.target.value})}
                    rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Services</label>
                  <input type="text" value={newPharmacie.services} placeholder="Ex: Livraison, garde de nuit, homéopathie..."
                    onChange={e => setNewPharmacie({...newPharmacie, services: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              {/* Contact */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-600 uppercase">Contact</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Téléphone *</label>
                    <input type="text" value={newPharmacie.telephone} placeholder="+221338234567"
                      onChange={e => { setNewPharmacie({...newPharmacie, telephone: e.target.value}); if (erreursPharmacie.telephone) setErreursPharmacie(p => ({...p, telephone: ""})); }}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${erreursPharmacie.telephone ? "border-red-400" : "border-gray-200"}`} />
                    {erreursPharmacie.telephone && <p className="text-xs text-red-500 mt-1">{erreursPharmacie.telephone}</p>}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Email</label>
                    <input type="email" value={newPharmacie.email} placeholder="contact@pharmacie.sn"
                      onChange={e => setNewPharmacie({...newPharmacie, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Site web</label>
                  <input type="text" value={newPharmacie.siteWeb} placeholder="https://..."
                    onChange={e => setNewPharmacie({...newPharmacie, siteWeb: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              {/* Localisation */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-600 uppercase">Localisation</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Adresse *</label>
                    <input type="text" value={newPharmacie.adresseComplete} placeholder="Rue 10, Point E"
                      onChange={e => { setNewPharmacie({...newPharmacie, adresseComplete: e.target.value}); if (erreursPharmacie.adresseComplete) setErreursPharmacie(p => ({...p, adresseComplete: ""})); }}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${erreursPharmacie.adresseComplete ? "border-red-400" : "border-gray-200"}`} />
                    {erreursPharmacie.adresseComplete && <p className="text-xs text-red-500 mt-1">{erreursPharmacie.adresseComplete}</p>}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Quartier</label>
                    <input type="text" value={newPharmacie.quartier} placeholder="Plateau"
                      onChange={e => setNewPharmacie({...newPharmacie, quartier: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Latitude</label>
                    <input type="number" step="any" value={newPharmacie.latitude} placeholder="14.6937"
                      onChange={e => setNewPharmacie({...newPharmacie, latitude: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Longitude</label>
                    <input type="number" step="any" value={newPharmacie.longitude} placeholder="-17.4441"
                      onChange={e => setNewPharmacie({...newPharmacie, longitude: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
              </div>
              {/* Documents */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-600 uppercase">Documents</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">N° Ordre</label>
                    <input type="text" value={newPharmacie.numeroOrdre} placeholder="ORD-XXXXX"
                      onChange={e => setNewPharmacie({...newPharmacie, numeroOrdre: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">N° Agrément Ministère</label>
                    <input type="text" value={newPharmacie.numeroAgrementMinistere} placeholder="AGR-XXXXX"
                      onChange={e => setNewPharmacie({...newPharmacie, numeroAgrementMinistere: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
              </div>
              {/* Configuration */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-600 uppercase">Configuration</p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={newPharmacie.accepteCommandes} onChange={e => setNewPharmacie({...newPharmacie, accepteCommandes: e.target.checked})} className="w-4 h-4 accent-green-600" />
                    <span className="text-gray-700">Accepte commandes</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={newPharmacie.proposeLivraison} onChange={e => setNewPharmacie({...newPharmacie, proposeLivraison: e.target.checked})} className="w-4 h-4 accent-green-600" />
                    <span className="text-gray-700">Propose livraison</span>
                  </label>
                </div>
                {newPharmacie.proposeLivraison && (
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Rayon livraison (km)</label>
                    <input type="number" value={newPharmacie.rayonLivraisonKm} placeholder="5"
                      onChange={e => setNewPharmacie({...newPharmacie, rayonLivraisonKm: e.target.value})}
                      className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                )}
              </div>
              {/* Horaires */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-xs font-bold text-gray-600 uppercase mb-3">Horaires d'ouverture</p>
                <div className="overflow-x-auto"><table className="w-full text-xs min-w-[600px]">
                  <thead><tr className="text-gray-500"><th className="text-left py-1 w-28">Jour</th><th className="text-center py-1">Ouverture</th><th className="text-center py-1">Fermeture</th><th className="text-center py-1">Fermé</th></tr></thead>
                  <tbody>
                    {JOURS_SEMAINE.map((j, i) => (
                      <tr key={j} className="border-t border-gray-100">
                        <td className="py-1.5 font-medium text-gray-700">{JOURS_LABELS[i]}</td>
                        <td className="py-1.5 text-center">
                          <input type="time" value={newPharmacieHoraires[j]?.ouverture || ""} disabled={newPharmacieHoraires[j]?.ferme}
                            onChange={e => setNewPharmacieHoraires(h => ({...h, [j]: {...h[j], ouverture: e.target.value}}))}
                            className="px-2 py-1 border border-gray-200 rounded text-xs disabled:opacity-40 disabled:bg-gray-100" />
                        </td>
                        <td className="py-1.5 text-center">
                          <input type="time" value={newPharmacieHoraires[j]?.fermeture || ""} disabled={newPharmacieHoraires[j]?.ferme}
                            onChange={e => setNewPharmacieHoraires(h => ({...h, [j]: {...h[j], fermeture: e.target.value}}))}
                            className="px-2 py-1 border border-gray-200 rounded text-xs disabled:opacity-40 disabled:bg-gray-100" />
                        </td>
                        <td className="py-1.5 text-center">
                          <input type="checkbox" checked={newPharmacieHoraires[j]?.ferme || false}
                            onChange={e => setNewPharmacieHoraires(h => ({...h, [j]: {...h[j], ferme: e.target.checked}}))}
                            className="w-4 h-4 accent-red-500" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
              {/* Région → Commune cascade */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Région *</label>
                  <select value={selectedRegionId} onChange={e => { handleRegionChange(e.target.value); if (erreursPharmacie.regionId) setErreursPharmacie(p => ({...p, regionId: ""})); }}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${erreursPharmacie.regionId ? "border-red-400" : "border-gray-200"}`}>
                    <option value="">— Choisir une région —</option>
                    {regions.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                  </select>
                  {erreursPharmacie.regionId && <p className="text-xs text-red-500 mt-1">{erreursPharmacie.regionId}</p>}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Commune *</label>
                  <select value={newPharmacie.communeId} onChange={e => { handleCommuneChange(e.target.value); if (erreursPharmacie.communeId) setErreursPharmacie(p => ({...p, communeId: ""})); }}
                    disabled={!selectedRegionId || communesList.length === 0}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-50 disabled:text-gray-400 ${erreursPharmacie.communeId ? "border-red-400" : "border-gray-200"}`}>
                    <option value="">— Choisir une commune —</option>
                    {communesList.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                  {erreursPharmacie.communeId && <p className="text-xs text-red-500 mt-1">{erreursPharmacie.communeId}</p>}
                </div>
              </div>
              {/* Syndicat */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assigner au syndicat</label>
                {detectedSyndicat && (
                  <div className="mb-1.5 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                    <span className="font-semibold">Syndicat détecté automatiquement :</span>
                    <span>{detectedSyndicat.nom} ({detectedSyndicat.code})</span>
                  </div>
                )}
                <select value={newPharmacie.syndicatId} onChange={e => { setNewPharmacie({...newPharmacie, syndicatId: e.target.value}); if (e.target.value !== detectedSyndicat?.id) setDetectedSyndicat(null); }}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">— Aucun syndicat —</option>
                  {syndicats.map(s => <option key={s.id} value={s.id}>{s.nom} ({s.code})</option>)}
                </select>
              </div>
              {/* Pharmacien propriétaire */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Pharmacien propriétaire</label>
                <select value={newPharmacie.pharmacienId} onChange={e => setNewPharmacie({...newPharmacie, pharmacienId: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">— Sélectionner un pharmacien disponible —</option>
                  {pharmaciens.filter(ph => !pharmacies.some(p => p.pharmacienProprietaireId === ph.id)).map(ph => (
                    <option key={ph.id} value={ph.id}>{ph.prenom} {ph.nom} · {ph.numeroOrdreNational || "—"}</option>
                  ))}
                </select>
                {pharmaciens.filter(ph => !pharmacies.some(p => p.pharmacienProprietaireId === ph.id)).length === 0 && (
                  <p className="text-xs text-orange-500 mt-1">Tous les pharmaciens ont déjà une pharmacie. Créez-en un nouveau d'abord.</p>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button onClick={() => { setShowAddPharmacieModal(false); setSelectedRegionId(""); setCommunesList([]); setDetectedSyndicat(null); setErreursPharmacie({}); }}
                className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium">Annuler</button>
              <button onClick={handleCreerPharmacie}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700">Créer & Valider</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Ajouter Pharmacien ── */}
      {showAddPharmacienModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Ajouter un pharmacien</h3>
                <p className="text-xs text-gray-500 mt-0.5">Renseignez toutes les informations du pharmacien</p>
              </div>
              <button onClick={() => { setShowAddPharmacienModal(false); setErreursPharmacien({}); }} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4 flex-1">
              {/* Identité */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 space-y-3">
                <p className="text-xs font-bold text-blue-700 uppercase">Identité</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Prénom *</label>
                    <input type="text" value={newPharmacien.prenom} placeholder="Moussa"
                      onChange={e => { setNewPharmacien({...newPharmacien, prenom: e.target.value}); if (erreursPharmacien.prenom) setErreursPharmacien(p => ({...p, prenom: ""})); }}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${erreursPharmacien.prenom ? "border-red-400" : "border-gray-200"}`} />
                    {erreursPharmacien.prenom && <p className="text-xs text-red-500 mt-1">{erreursPharmacien.prenom}</p>}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Nom *</label>
                    <input type="text" value={newPharmacien.nom} placeholder="Diallo"
                      onChange={e => { setNewPharmacien({...newPharmacien, nom: e.target.value}); if (erreursPharmacien.nom) setErreursPharmacien(p => ({...p, nom: ""})); }}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${erreursPharmacien.nom ? "border-red-400" : "border-gray-200"}`} />
                    {erreursPharmacien.nom && <p className="text-xs text-red-500 mt-1">{erreursPharmacien.nom}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Date de naissance</label>
                    <input type="date" value={newPharmacien.dateNaissance}
                      onChange={e => setNewPharmacien({...newPharmacien, dateNaissance: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Sexe</label>
                    <select value={newPharmacien.sexe} onChange={e => setNewPharmacien({...newPharmacien, sexe: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="M">Homme (M)</option>
                      <option value="F">Femme (F)</option>
                    </select>
                  </div>
                </div>
              </div>
              {/* Contact */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-600 uppercase">Contact</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Téléphone * (+221…)</label>
                    <input type="text" value={newPharmacien.telephone} placeholder="+221771234567"
                      onChange={e => { setNewPharmacien({...newPharmacien, telephone: e.target.value}); if (erreursPharmacien.telephone) setErreursPharmacien(p => ({...p, telephone: ""})); }}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${erreursPharmacien.telephone ? "border-red-400" : "border-gray-200"}`} />
                    {erreursPharmacien.telephone && <p className="text-xs text-red-500 mt-1">{erreursPharmacien.telephone}</p>}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Email</label>
                    <input type="email" value={newPharmacien.email} placeholder="pharmacien@example.sn"
                      onChange={e => setNewPharmacien({...newPharmacien, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              </div>
              {/* Formation & Ordre */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-600 uppercase">Formation & Ordre National</p>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">N° Ordre National</label>
                  <input type="text" value={newPharmacien.numeroOrdreNational} placeholder="ORD-NAT-XXXXX"
                    onChange={e => setNewPharmacien({...newPharmacien, numeroOrdreNational: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Université de formation</label>
                    <input type="text" value={newPharmacien.universiteFormation} placeholder="UGB, UCAD, FMPO..."
                      onChange={e => setNewPharmacien({...newPharmacien, universiteFormation: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Année du diplôme</label>
                    <input type="number" value={newPharmacien.anneeDiplome} placeholder="2020"
                      onChange={e => setNewPharmacien({...newPharmacien, anneeDiplome: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button onClick={() => { setShowAddPharmacienModal(false); setErreursPharmacien({}); }} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium">Annuler</button>
              <button onClick={handleCreerPharmacien} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">Créer le pharmacien</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Assigner Pharmacien → Pharmacie ── */}
      {showAssignPharmacienModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-gray-800">Assigner un pharmacien</h3>
                <p className="text-xs text-gray-500 mt-0.5">{assignPharmacienTarget?.nom}</p>
              </div>
              <button onClick={() => setShowAssignPharmacienModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Pharmacien propriétaire *</label>
              <select value={assignPharmacienId} onChange={e => setAssignPharmacienId(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">— Sélectionner un pharmacien disponible —</option>
                {pharmaciens.filter(ph => !pharmacies.some(p => p.pharmacienProprietaireId === ph.id)).map(ph => (
                  <option key={ph.id} value={ph.id}>{ph.prenom} {ph.nom} — {ph.numeroOrdreNational || "sans n° ordre"}</option>
                ))}
              </select>
              {pharmaciens.filter(ph => !pharmacies.some(p => p.pharmacienProprietaireId === ph.id)).length === 0 && (
                <p className="text-xs text-orange-500 mt-2">Aucun pharmacien disponible. Créez un pharmacien d'abord.</p>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowAssignPharmacienModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium">Annuler</button>
              <button onClick={handleAssignerPharmacienAPharmacie} disabled={!assignPharmacienId}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">Assigner</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Assigner Pharmacie → Pharmacien ── */}
      {showAssignPharmacieToPharmacienModal && assignPharmacieToPharmacienTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-gray-800">Assigner une pharmacie</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Dr. {assignPharmacieToPharmacienTarget.prenom} {assignPharmacieToPharmacienTarget.nom}
                </p>
              </div>
              <button onClick={() => setShowAssignPharmacieToPharmacienModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Pharmacie *</label>
              <select value={assignPharmacieForPharmacienId} onChange={e => setAssignPharmacieForPharmacienId(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">— Sélectionner une pharmacie —</option>
                {pharmacies.filter(p => !p.pharmacienProprietaireId).map(p => (
                  <option key={p.id} value={p.id}>{p.nom} — {p.communeNom || p.commune || ""}</option>
                ))}
              </select>
              {pharmacies.filter(p => !p.pharmacienProprietaireId).length === 0 && (
                <p className="text-xs text-orange-500 mt-2">Toutes les pharmacies ont déjà un pharmacien.</p>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowAssignPharmacieToPharmacienModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium">Annuler</button>
              <button onClick={handleAssignerPharmacieAPharmacien} disabled={!assignPharmacieForPharmacienId}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">Assigner</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Assigner Pharmacie → Syndicat ── */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">Assigner une pharmacie à un syndicat</h3>
              <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="space-y-4">
              {assignPharmacieId ? (
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <p className="text-xs text-gray-500 font-medium mb-0.5">Pharmacie</p>
                  <p className="text-sm font-semibold text-gray-800">{pharmacies.find(p => p.id === assignPharmacieId)?.nom || assignPharmacieId}</p>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Pharmacie *</label>
                  <select value={assignPharmacieId} onChange={e => setAssignPharmacieId(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="">— Sélectionner une pharmacie —</option>
                    {pharmacies.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Syndicat *</label>
                <select value={assignSyndicatId} onChange={e => setAssignSyndicatId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">— Sélectionner un syndicat —</option>
                  {syndicats.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAssignModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium">Annuler</button>
              <button onClick={handleAssignerPharmacie} disabled={!assignPharmacieId || !assignSyndicatId}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">Assigner</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Détail Pharmacie ── */}
      {selectedPharmacieDetail && (() => {
        const p = selectedPharmacieDetail;
        const horairesObj = (() => { try { return p.horaires ? (typeof p.horaires === "string" ? JSON.parse(p.horaires) : p.horaires) : null; } catch { return null; } })();
        const JOURS_LABELS = { lundi:"Lundi", mardi:"Mardi", mercredi:"Mercredi", jeudi:"Jeudi", vendredi:"Vendredi", samedi:"Samedi", dimanche:"Dimanche" };
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl max-h-[92vh] flex flex-col">
              {/* Header */}
              <div className="p-5 border-b border-gray-100 flex items-center gap-4 flex-shrink-0">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {(p.nom || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-800 text-lg">{p.nom}</h3>
                  <p className="text-sm text-gray-500">{p.code || ""} · {p.communeNom || p.commune || ""}</p>
                </div>
                <PharmacieStatutBadge statut={p.pharmacienProprietaireId ? p.statut : "EN_ATTENTE"} />
                <button onClick={() => setSelectedPharmacieDetail(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={20}/></button>
              </div>
              <div className="overflow-y-auto flex-1 p-5 space-y-5">
                {/* Pharmacien */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <p className="text-xs font-semibold text-blue-700 mb-2">PHARMACIEN RESPONSABLE</p>
                  {p.pharmacienProprietaireId ? (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {(p.pharmacienProprietaireNom || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Dr. {p.pharmacienProprietaireNom}</p>
                        <p className="text-xs text-gray-400">ID: {p.pharmacienProprietaireId}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-yellow-700">
                      <AlertCircle size={16}/><span className="text-sm font-medium">Aucun pharmacien assigné — pharmacie incomplète</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                {p.description && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</p>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{p.description}</p>
                  </div>
                )}

                {/* Infos */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    { label: "Adresse", value: p.adresseComplete || p.adresse, icon: <MapPin size={13}/> },
                    { label: "Syndicat", value: p.syndicatNom, icon: <Building2 size={13}/> },
                    { label: "Téléphone", value: p.telephone, icon: <Phone size={13}/> },
                    { label: "Tél. secondaire", value: p.telephoneSecondaire, icon: <Phone size={13}/> },
                    { label: "Email", value: p.email, icon: <Mail size={13}/> },
                    { label: "Site web", value: p.siteWeb, icon: <TrendingUp size={13}/> },
                  ].filter(f => f.value).map(f => (
                    <div key={f.label} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 flex items-center gap-1 mb-0.5">{f.icon}{f.label}</p>
                      <p className="font-medium text-gray-700 truncate">{f.value}</p>
                    </div>
                  ))}
                </div>

                {/* Coordonnées GPS + Maps */}
                {(p.latitude || p.longitude) && (
                  <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">GPS :</span> {p.latitude}, {p.longitude}
                    </div>
                    <a href={p.lienGoogleMaps || `https://maps.google.com/?q=${p.latitude},${p.longitude}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors">
                      <MapPin size={12}/> Google Maps
                    </a>
                  </div>
                )}

                {/* Services */}
                {p.services && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Services</p>
                    <p className="text-sm text-gray-700">{p.services}</p>
                  </div>
                )}

                {/* Horaires */}
                {horairesObj && Object.keys(horairesObj).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Horaires</p>
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto"><table className="w-full text-sm min-w-[600px]">
                        <tbody className="divide-y divide-gray-100">
                          {Object.entries(horairesObj).map(([jour, h]) => (
                            <tr key={jour} className={h.ferme ? "bg-gray-50" : ""}>
                              <td className="px-3 py-2 font-medium text-gray-700 w-28">{JOURS_LABELS[jour] || jour}</td>
                              <td className="px-3 py-2 text-gray-600">
                                {h.ferme ? <span className="text-red-400 italic text-xs">Fermé</span>
                                  : <span>{h.ouverture || "—"} → {h.fermeture || "—"}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table></div>
                    </div>
                  </div>
                )}

                {/* Configuration */}
                <div className="flex gap-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${p.accepteCommandes ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {p.accepteCommandes ? <CheckCircle size={12}/> : <XCircle size={12}/>} Commandes
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${p.proposeLivraison ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {p.proposeLivraison ? <CheckCircle size={12}/> : <XCircle size={12}/>} Livraison
                  </span>
                </div>
              </div>
              {/* Actions */}
              <div className="p-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
                {p.statut === "EN_ATTENTE" && p.pharmacienProprietaireId && (
                  <>
                    <button onClick={() => { handleValiderPharmacie(p.id); setSelectedPharmacieDetail(null); }}
                      className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Valider</button>
                    <button onClick={() => { handleRejeterPharmacie(p.id); setSelectedPharmacieDetail(null); }}
                      className="flex-1 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-medium hover:bg-red-200">Rejeter</button>
                  </>
                )}
                <button onClick={() => { setSelectedPharmacieDetail(null); handleEditPharmacie(p); }}
                  className="flex-1 py-2 border border-blue-200 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 flex items-center justify-center gap-1">
                  <Pencil size={14}/> Modifier
                </button>
                <button onClick={() => setSelectedPharmacieDetail(null)} className="flex-1 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal Détail Pharmacien ── */}
      {selectedPharmacienDetail && (() => {
        const ph = selectedPharmacienDetail;
        const pharmacieLiee = pharmacies.find(p => p.pharmacienProprietaireId === ph.id);
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
              <div className="p-5 border-b border-gray-100 flex items-center gap-4 flex-shrink-0">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {((ph.prenom || "?")[0] + (ph.nom || "?")[0]).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800 text-lg">Dr. {ph.prenom} {ph.nom}</h3>
                  <p className="text-sm text-gray-500">{ph.email || ""}</p>
                </div>
                <StatutBadge statut={ph.actif ? "ACTIF" : "INACTIF"} />
                <button onClick={() => setSelectedPharmacienDetail(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={20}/></button>
              </div>
              <div className="overflow-y-auto flex-1 p-5 space-y-4">
                {/* Pharmacie liée */}
                <div className={`rounded-xl p-4 border ${pharmacieLiee ? "bg-green-50 border-green-100" : "bg-yellow-50 border-yellow-100"}`}>
                  <p className="text-xs font-semibold mb-2 ${pharmacieLiee ? 'text-green-700' : 'text-yellow-700'}">PHARMACIE GÉRÉE</p>
                  {pharmacieLiee ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">{(pharmacieLiee.nom || "?")[0]}</div>
                      <div>
                        <p className="font-semibold text-gray-800">{pharmacieLiee.nom}</p>
                        <p className="text-xs text-gray-500">{pharmacieLiee.communeNom || ""}</p>
                      </div>
                      <PharmacieStatutBadge statut={pharmacieLiee.statut} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-yellow-700">
                      <AlertCircle size={16}/><span className="text-sm">Aucune pharmacie assignée</span>
                    </div>
                  )}
                </div>

                {/* Infos personnelles */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    { label: "Téléphone", value: ph.telephone },
                    { label: "N° Ordre National", value: ph.numeroOrdreNational },
                    { label: "Type", value: ph.type },
                    { label: "Sexe", value: ph.sexe },
                  ].filter(f => f.value).map(f => (
                    <div key={f.label} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">{f.label}</p>
                      <p className="font-medium text-gray-700">{f.value}</p>
                    </div>
                  ))}
                </div>

                {/* Formation */}
                {(ph.universiteFormation || ph.anneeDiplome) && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-blue-700 mb-2">FORMATION</p>
                    <p className="font-semibold text-gray-800">{ph.universiteFormation || "—"}</p>
                    {ph.anneeDiplome && <p className="text-sm text-gray-500">Diplômé en {ph.anneeDiplome}</p>}
                  </div>
                )}

                {/* Abonnement */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2">ABONNEMENT</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{ph.plan || "—"}</span>
                    <StatutBadge statut={ph.statutAbonnement === "ACTIF" ? "ACTIF" : "INACTIF"} />
                  </div>
                  {ph.essaiGratuit && ph.dateFinEssai && (
                    <p className="text-xs text-gray-400 mt-1">Essai gratuit jusqu'au {new Date(ph.dateFinEssai).toLocaleDateString("fr-FR")}</p>
                  )}
                  {ph.nombrePharmaciesActuelles !== undefined && (
                    <p className="text-xs text-gray-400 mt-1">Pharmacies : {ph.nombrePharmaciesActuelles}/{ph.nombrePharmaciesMax}</p>
                  )}
                </div>

                {ph.derniereConnexion && (
                  <p className="text-xs text-gray-400">Dernière connexion : {new Date(ph.derniereConnexion).toLocaleString("fr-FR")}</p>
                )}
              </div>
              <div className="p-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
                <button onClick={() => { setSelectedPharmacienDetail(null); handleEditPharmacien(ph); }}
                  className="flex-1 py-2 border border-purple-200 text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-50 flex items-center justify-center gap-1">
                  <Pencil size={14}/> Modifier
                </button>
                <button onClick={() => setSelectedPharmacienDetail(null)} className="flex-1 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal nouveau syndicat ── */}
      {showNewSyndicatForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-5">Créer un nouveau syndicat</h3>
            <div className="space-y-4">
              {[
                { field: "nom",        label: "Nom du syndicat",  placeholder: "Syndicat des Pharmaciens de..." },
                { field: "username",   label: "Username",          placeholder: "syndicat.dakar" },
                { field: "motDePasse", label: "Mot de passe",      placeholder: "••••••••",         type: "password" },
                { field: "email",      label: "Email",             placeholder: "contact@syndicat.sn", type: "email" },
                { field: "telephone",  label: "Téléphone",         placeholder: "+221771234567" },
              ].map(({ field, label, placeholder, type }) => (
                <div key={field}>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">{label}</label>
                  <input
                    type={type || "text"}
                    value={newSyndicat[field]}
                    onChange={e => setNewSyndicat({ ...newSyndicat, [field]: e.target.value })}
                    placeholder={placeholder}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              ))}

              {/* Type selector */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Type de portée</label>
                <select
                  value={newSyndicat.type}
                  onChange={e => handleSyndicatTypeChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="DEPARTEMENT">Département (toutes les communes)</option>
                  <option value="ZONE">Zone multi-communes (communes spécifiques)</option>
                  <option value="COMMUNE">Commune unique</option>
                  <option value="REGION">Région entière</option>
                </select>
              </div>

              {/* Zone selector — REGION */}
              {newSyndicat.type === "REGION" && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Région couverte *</label>
                  {syndicatZonesLoading ? (
                    <p className="text-xs text-gray-400 py-2">Chargement des régions disponibles...</p>
                  ) : (
                    <select
                      value={newSyndicat.regionId}
                      onChange={e => setNewSyndicat({ ...newSyndicat, regionId: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">— Choisir une région —</option>
                      {syndicatZones.map(z => <option key={z.id} value={z.id}>{z.nom}</option>)}
                    </select>
                  )}
                  {!syndicatZonesLoading && syndicatZones.length === 0 && (
                    <p className="text-xs text-orange-500 mt-1">Toutes les régions ont déjà un syndicat assigné.</p>
                  )}
                </div>
              )}

              {/* Zone selector — DEPARTEMENT (cascade région → département disponible) */}
              {newSyndicat.type === "DEPARTEMENT" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Région</label>
                    <select
                      value={syndicatZoneRegionId}
                      onChange={e => handleSyndicatZoneRegionChange(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">— Filtrer par région —</option>
                      {syndicatZoneRegions.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Département disponible *</label>
                    {syndicatZonesLoading ? (
                      <p className="text-xs text-gray-400 py-2">Chargement...</p>
                    ) : (
                      <select
                        value={newSyndicat.departementId}
                        onChange={e => setNewSyndicat({ ...newSyndicat, departementId: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">— Choisir un département —</option>
                        {(syndicatZoneRegionId
                          ? syndicatZones.filter(z => syndicatZoneDeps.some(d => d.id === z.id))
                          : syndicatZones
                        ).map(z => <option key={z.id} value={z.id}>{z.nom}</option>)}
                      </select>
                    )}
                    {!syndicatZonesLoading && syndicatZones.length === 0 && (
                      <p className="text-xs text-orange-500 mt-1">Tous les départements ont déjà un syndicat assigné.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Zone selector — COMMUNE (cascade région → département → commune disponible) */}
              {newSyndicat.type === "COMMUNE" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Région</label>
                    <select
                      value={syndicatZoneRegionId}
                      onChange={e => handleSyndicatZoneRegionChange(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">— Filtrer par région —</option>
                      {syndicatZoneRegions.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                    </select>
                  </div>
                  {syndicatZoneRegionId && (
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Département</label>
                      <select
                        value={syndicatZoneDepId}
                        onChange={e => setSyndicatZoneDepId(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">— Filtrer par département —</option>
                        {syndicatZoneDeps.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Commune disponible *</label>
                    {syndicatZonesLoading ? (
                      <p className="text-xs text-gray-400 py-2">Chargement...</p>
                    ) : (
                      <select
                        value={newSyndicat.communeId}
                        onChange={e => setNewSyndicat({ ...newSyndicat, communeId: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">— Choisir une commune —</option>
                        {syndicatZones.map(z => <option key={z.id} value={z.id}>{z.nom}</option>)}
                      </select>
                    )}
                    {!syndicatZonesLoading && syndicatZones.length === 0 && (
                      <p className="text-xs text-orange-500 mt-1">Toutes les communes ont déjà un syndicat assigné.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Zone selector — ZONE (multi-communes dans un même département) */}
              {newSyndicat.type === "ZONE" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Filtrer par région</label>
                    <select value={syndicatZoneRegionId} onChange={e => handleSyndicatZoneRegionChange(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">— Toutes les régions —</option>
                      {syndicatZoneRegions.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                    </select>
                  </div>
                  {syndicatZoneRegionId && (
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Filtrer par département</label>
                      <select value={syndicatZoneDepId} onChange={e => setSyndicatZoneDepId(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                        <option value="">— Tous les départements —</option>
                        {syndicatZoneDeps.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      Communes à couvrir * <span className="text-green-600">({newSyndicat.communeIds?.length || 0} sélectionnée{newSyndicat.communeIds?.length > 1 ? "s" : ""})</span>
                    </label>
                    {syndicatZonesLoading ? (
                      <p className="text-xs text-gray-400 py-2">Chargement...</p>
                    ) : (
                      <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                        {(() => {
                          const communesFiltrees = syndicatZoneDepId
                            ? syndicatZones.filter(z => z.departementId === syndicatZoneDepId)
                            : syndicatZones;
                          return communesFiltrees.length === 0 ? (
                            <p className="text-xs text-orange-500 p-3">
                              {syndicatZoneRegionId ? "Aucune commune disponible pour ce filtre." : "Sélectionnez d'abord une région."}
                            </p>
                          ) : communesFiltrees.map(z => (
                            <label key={z.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0">
                              <input type="checkbox"
                                checked={newSyndicat.communeIds?.includes(z.id) || false}
                                onChange={e => {
                                  const prev = newSyndicat.communeIds || [];
                                  setNewSyndicat({ ...newSyndicat, communeIds: e.target.checked ? [...prev, z.id] : prev.filter(id => id !== z.id) });
                                }}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className="text-sm text-gray-800">{z.nom}</span>
                            </label>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNewSyndicatForm(false);
                  setSyndicatZones([]); setSyndicatZoneRegions([]); setSyndicatZoneDeps([]);
                  setSyndicatZoneRegionId(""); setSyndicatZoneDepId("");
                  setNewSyndicat({ nom: "", username: "", motDePasse: "", email: "", telephone: "", type: "DEPARTEMENT", departementId: "", communeId: "", regionId: "", communeIds: [] });
                }}
                className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleCreerSyndicat}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-all"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Sous-comptes Syndicat ────────────────────────────────────── */}
      {selectedSyndicatForComptes && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900">Comptes associés — {selectedSyndicatForComptes.nom}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{selectedSyndicatForComptes.code}</p>
              </div>
              <button onClick={() => { setSelectedSyndicatForComptes(null); setSousComptes([]); }}
                className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {sousComptesLoading ? (
                <div className="text-center py-8"><div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
              ) : sousComptes.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun sous-compte pour ce syndicat</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sousComptes.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-900">{c.nom}</p>
                        <p className="text-xs text-gray-500 font-mono mt-0.5">{c.username}</p>
                        {c.contact?.email && <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><Mail size={10}/>{c.contact.email}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <StatutBadge statut={c.statut || "ACTIF"} />
                        <button onClick={() => { setSelectedSousCompte(c); setResetMdpForm({ mdp: "", confirm: "" }); setShowResetSousCompteModal(true); }}
                          className="flex items-center gap-1 px-3 py-1.5 border border-orange-300 text-orange-600 text-xs font-medium rounded-lg hover:bg-orange-50">
                          <Lock size={11}/>Changer mdp
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Reset Mot de Passe Sous-compte ──────────────────────────── */}
      {showResetSousCompteModal && selectedSousCompte && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900">Changer le mot de passe</h3>
                <p className="text-xs text-gray-500 mt-0.5 font-mono">{selectedSousCompte.username}</p>
              </div>
              <button onClick={() => setShowResetSousCompteModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nouveau mot de passe</label>
                <input type="password" value={resetMdpForm.mdp} onChange={e => setResetMdpForm({...resetMdpForm, mdp: e.target.value})}
                  placeholder="••••••••" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirmer</label>
                <input type="password" value={resetMdpForm.confirm} onChange={e => setResetMdpForm({...resetMdpForm, confirm: e.target.value})}
                  placeholder="••••••••" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowResetSousCompteModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
                <button onClick={handleResetSousCompteMdp} disabled={resetMdpLoading}
                  className="flex-1 py-2.5 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50">
                  {resetMdpLoading ? "..." : "Changer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Modifier Syndicat ──────────────────────────── */}
      {showEditSyndicatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Pencil size={18} className="text-green-600"/>Modifier le syndicat</h3>
                <p className="text-xs text-gray-500 mt-0.5">Modifiez les informations du syndicat</p>
              </div>
              <button onClick={() => { setShowEditSyndicatModal(false); setEditZoneMode(false); setSyndicatZones([]); setSyndicatZoneRegions([]); setSyndicatZoneDeps([]); setSyndicatZoneRegionId(""); setSyndicatZoneDepId(""); }} className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100"><X size={20}/></button>
            </div>
            <form onSubmit={submitEditSyndicat} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom du syndicat *</label>
                  <input type="text" value={editSyndicatData.nom} onChange={e => setEditSyndicatData({...editSyndicatData, nom: e.target.value})} required
                    placeholder="Syndicat des Pharmaciens de..."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input type="email" value={editSyndicatData.email} onChange={e => setEditSyndicatData({...editSyndicatData, email: e.target.value})}
                    placeholder="contact@syndicat.sn"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone</label>
                  <input type="text" value={editSyndicatData.telephone} onChange={e => setEditSyndicatData({...editSyndicatData, telephone: e.target.value})}
                    placeholder="+221771234567"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom du responsable</label>
                  <input type="text" value={editSyndicatData.nomResponsable} onChange={e => setEditSyndicatData({...editSyndicatData, nomResponsable: e.target.value})}
                    placeholder="Dr. DIOP Mamadou"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone responsable</label>
                  <input type="text" value={editSyndicatData.telephoneResponsable} onChange={e => setEditSyndicatData({...editSyndicatData, telephoneResponsable: e.target.value})}
                    placeholder="+221771234567"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"/>
                </div>
              </div>

              {/* Zone géographique — modifiable */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-green-600"/>
                    <span className="text-xs font-medium text-gray-600">Zone géographique</span>
                    {!editZoneMode && editSyndicatData.typeZone && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        {editSyndicatData.typeZone === "DEPARTEMENT" ? "Département" : editSyndicatData.typeZone === "COMMUNE" ? "Commune" : editSyndicatData.typeZone === "ZONE" ? "Multi-communes" : "Région"}
                      </span>
                    )}
                  </div>
                  <button type="button" onClick={() => {
                    if (!editZoneMode) {
                      setEditZoneMode(true);
                      loadSyndicatZonesForType(editSyndicatData.typeZone || "DEPARTEMENT");
                      setEditSyndicatData(prev => ({ ...prev, newType: prev.typeZone || "DEPARTEMENT", newDepartementId: "", newCommuneId: "", newRegionId: "", newCommuneIds: [] }));
                    } else {
                      setEditZoneMode(false);
                      setSyndicatZones([]); setSyndicatZoneRegions([]); setSyndicatZoneDeps([]);
                      setSyndicatZoneRegionId(""); setSyndicatZoneDepId("");
                    }
                  }} className="text-xs text-green-600 hover:text-green-700 font-medium">
                    {editZoneMode ? "Annuler" : "Modifier"}
                  </button>
                </div>
                {!editZoneMode ? (
                  <div className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-800">{editSyndicatData.zoneNom || "—"}</span>
                  </div>
                ) : (
                  <div className="px-4 py-3 space-y-3">
                    {/* Type selector */}
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Type de portée</label>
                      <select value={editSyndicatData.newType || "DEPARTEMENT"}
                        onChange={e => {
                          const t = e.target.value;
                          setEditSyndicatData(prev => ({ ...prev, newType: t, newDepartementId: "", newCommuneId: "", newRegionId: "", newCommuneIds: [] }));
                          loadSyndicatZonesForType(t);
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                        <option value="DEPARTEMENT">Département (toutes les communes)</option>
                        <option value="ZONE">Zone multi-communes</option>
                        <option value="COMMUNE">Commune unique</option>
                        <option value="REGION">Région entière</option>
                      </select>
                    </div>

                    {/* Zone selector selon type */}
                    {syndicatZonesLoading ? (
                      <p className="text-xs text-gray-400">Chargement des zones...</p>
                    ) : (editSyndicatData.newType === "REGION") ? (
                      <select value={editSyndicatData.newRegionId || ""}
                        onChange={e => setEditSyndicatData(prev => ({ ...prev, newRegionId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                        <option value="">— Choisir une région —</option>
                        {syndicatZones.map(z => <option key={z.id} value={z.id}>{z.nom}</option>)}
                      </select>
                    ) : (editSyndicatData.newType === "DEPARTEMENT") ? (
                      <div className="space-y-2">
                        <select value={syndicatZoneRegionId} onChange={e => handleSyndicatZoneRegionChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                          <option value="">— Filtrer par région —</option>
                          {syndicatZoneRegions.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                        </select>
                        <select value={editSyndicatData.newDepartementId || ""}
                          onChange={e => setEditSyndicatData(prev => ({ ...prev, newDepartementId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                          <option value="">— Choisir un département —</option>
                          {(syndicatZoneRegionId ? syndicatZones.filter(z => syndicatZoneDeps.some(d => d.id === z.id)) : syndicatZones).map(z => <option key={z.id} value={z.id}>{z.nom}</option>)}
                        </select>
                      </div>
                    ) : (editSyndicatData.newType === "COMMUNE") ? (
                      <div className="space-y-2">
                        <select value={syndicatZoneRegionId} onChange={e => handleSyndicatZoneRegionChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                          <option value="">— Filtrer par région —</option>
                          {syndicatZoneRegions.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                        </select>
                        <select value={editSyndicatData.newCommuneId || ""}
                          onChange={e => setEditSyndicatData(prev => ({ ...prev, newCommuneId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                          <option value="">— Choisir une commune —</option>
                          {syndicatZones.map(z => <option key={z.id} value={z.id}>{z.nom}</option>)}
                        </select>
                      </div>
                    ) : (editSyndicatData.newType === "ZONE") ? (
                      <div className="space-y-2">
                        <select value={syndicatZoneRegionId} onChange={e => handleSyndicatZoneRegionChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                          <option value="">— Filtrer par région —</option>
                          {syndicatZoneRegions.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                        </select>
                        <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                          {syndicatZones.length === 0 ? <p className="text-xs text-gray-400 p-3">Aucune commune disponible</p> :
                            syndicatZones.map(z => (
                              <label key={z.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0">
                                <input type="checkbox"
                                  checked={(editSyndicatData.newCommuneIds || []).includes(z.id)}
                                  onChange={e => {
                                    const prev = editSyndicatData.newCommuneIds || [];
                                    setEditSyndicatData(p => ({ ...p, newCommuneIds: e.target.checked ? [...prev, z.id] : prev.filter(id => id !== z.id) }));
                                  }}
                                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"/>
                                <span className="text-sm text-gray-800">{z.nom}</span>
                              </label>
                            ))
                          }
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEditSyndicatModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={editLoading}
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-all">
                  {editLoading ? "Enregistrement..." : "Enregistrer les modifications"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal Modifier Pharmacie ──────────────────────────── */}
      {showEditPharmacieModal && (() => {
        const JOURS = [
          { key: "lundi",    label: "Lundi" },
          { key: "mardi",    label: "Mardi" },
          { key: "mercredi", label: "Mercredi" },
          { key: "jeudi",    label: "Jeudi" },
          { key: "vendredi", label: "Vendredi" },
          { key: "samedi",   label: "Samedi" },
          { key: "dimanche", label: "Dimanche" },
        ];
        const setHoraire = (jour, field, val) => {
          setEditPharmacieHoraires(prev => ({
            ...prev,
            [jour]: { ...(prev[jour] || {}), [field]: val, ferme: false }
          }));
        };
        const toggleFerme = (jour, ferme) => {
          setEditPharmacieHoraires(prev => ({
            ...prev,
            [jour]: ferme ? { ferme: true } : { ouverture: "08:00", fermeture: "21:00", ferme: false }
          }));
        };
        return (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <h3 className="font-bold text-gray-900 flex items-center gap-2"><Pencil size={16} className="text-blue-600"/>Modifier la pharmacie</h3>
                <button onClick={() => setShowEditPharmacieModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
              </div>
              <form onSubmit={submitEditPharmacie} className="p-5 space-y-4 overflow-y-auto flex-1">

                {/* Informations de base */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Informations générales</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom de la pharmacie *</label>
                  <input type="text" value={editPharmacieData.nom} onChange={e => setEditPharmacieData({...editPharmacieData, nom: e.target.value})} required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <textarea value={editPharmacieData.description} onChange={e => setEditPharmacieData({...editPharmacieData, description: e.target.value})}
                    rows={2} placeholder="Présentation de la pharmacie..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Services proposés</label>
                  <textarea value={editPharmacieData.services} onChange={e => setEditPharmacieData({...editPharmacieData, services: e.target.value})}
                    rows={2} placeholder="Ex: Garde de nuit, Livraison, Analyse médicale..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
                </div>

                {/* Contact */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">Contact</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone</label>
                    <input type="text" value={editPharmacieData.telephone} onChange={e => setEditPharmacieData({...editPharmacieData, telephone: e.target.value})}
                      placeholder="+221XXXXXXXXX" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Tél. secondaire</label>
                    <input type="text" value={editPharmacieData.telephoneSecondaire} onChange={e => setEditPharmacieData({...editPharmacieData, telephoneSecondaire: e.target.value})}
                      placeholder="+221XXXXXXXXX" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                    <input type="email" value={editPharmacieData.email} onChange={e => setEditPharmacieData({...editPharmacieData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Site web</label>
                    <input type="text" value={editPharmacieData.siteWeb} onChange={e => setEditPharmacieData({...editPharmacieData, siteWeb: e.target.value})}
                      placeholder="https://..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  </div>
                </div>

                {/* Localisation */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">Localisation</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Adresse complète</label>
                  <input type="text" value={editPharmacieData.adresseComplete} onChange={e => setEditPharmacieData({...editPharmacieData, adresseComplete: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Quartier</label>
                    <input type="text" value={editPharmacieData.quartier} onChange={e => setEditPharmacieData({...editPharmacieData, quartier: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                      <input type="number" step="any" value={editPharmacieData.latitude} onChange={e => setEditPharmacieData({...editPharmacieData, latitude: e.target.value})}
                        placeholder="14.6928" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                      <input type="number" step="any" value={editPharmacieData.longitude} onChange={e => setEditPharmacieData({...editPharmacieData, longitude: e.target.value})}
                        placeholder="-17.4467" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    </div>
                  </div>
                </div>
                {editPharmacieData.latitude && editPharmacieData.longitude && (
                  <a href={`https://maps.google.com/?q=${editPharmacieData.latitude},${editPharmacieData.longitude}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline">
                    <MapPin size={12}/> Voir sur Google Maps
                  </a>
                )}

                {/* Horaires */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">Horaires d'ouverture</p>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto"><table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Jour</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Ouverture</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Fermeture</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Fermé</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {JOURS.map(({ key, label }) => {
                        const h = editPharmacieHoraires[key] || {};
                        const isFerme = h.ferme === true;
                        return (
                          <tr key={key} className={isFerme ? "bg-gray-50" : ""}>
                            <td className="px-3 py-2 font-medium text-gray-700 text-sm w-24">{label}</td>
                            <td className="px-3 py-2">
                              <input type="time" value={isFerme ? "" : (h.ouverture || "08:00")}
                                disabled={isFerme}
                                onChange={e => setHoraire(key, "ouverture", e.target.value)}
                                className="border border-gray-200 rounded px-2 py-1 text-sm disabled:opacity-40 disabled:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400"/>
                            </td>
                            <td className="px-3 py-2">
                              <input type="time" value={isFerme ? "" : (h.fermeture || "21:00")}
                                disabled={isFerme}
                                onChange={e => setHoraire(key, "fermeture", e.target.value)}
                                className="border border-gray-200 rounded px-2 py-1 text-sm disabled:opacity-40 disabled:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400"/>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <input type="checkbox" checked={isFerme}
                                onChange={e => toggleFerme(key, e.target.checked)}
                                className="w-4 h-4 rounded text-red-500"/>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table></div>
                </div>

                {/* Services */}
                <div className="flex gap-6 pt-1">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={editPharmacieData.accepteCommandes} onChange={e => setEditPharmacieData({...editPharmacieData, accepteCommandes: e.target.checked})}
                      className="rounded text-blue-600"/>
                    Accepte commandes
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={editPharmacieData.proposeLivraison} onChange={e => setEditPharmacieData({...editPharmacieData, proposeLivraison: e.target.checked})}
                      className="rounded text-blue-600"/>
                    Propose livraison
                  </label>
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowEditPharmacieModal(false)}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">Annuler</button>
                  <button type="submit" disabled={editLoading}
                    className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    {editLoading ? "Enregistrement..." : "Enregistrer"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* ── Modal Modifier Pharmacien ──────────────────────────── */}
      {showEditPharmacienModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <h3 className="font-bold text-gray-900 flex items-center gap-2"><Pencil size={16} className="text-purple-600"/>Modifier le pharmacien</h3>
              <button onClick={() => setShowEditPharmacienModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
            </div>
            <form onSubmit={submitEditPharmacien} className="p-5 space-y-3 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Prénom *</label>
                  <input type="text" value={editPharmacienData.prenom} onChange={e => setEditPharmacienData({...editPharmacienData, prenom: e.target.value})} required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom *</label>
                  <input type="text" value={editPharmacienData.nom} onChange={e => setEditPharmacienData({...editPharmacienData, nom: e.target.value})} required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input type="email" value={editPharmacienData.email} onChange={e => setEditPharmacienData({...editPharmacienData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone</label>
                  <input type="text" value={editPharmacienData.telephone} onChange={e => setEditPharmacienData({...editPharmacienData, telephone: e.target.value})}
                    placeholder="+221XXXXXXXXX" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"/>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">N° Ordre National</label>
                <input type="text" value={editPharmacienData.numeroOrdreNational} onChange={e => setEditPharmacienData({...editPharmacienData, numeroOrdreNational: e.target.value})}
                  placeholder="Ex: PH-2019-001234" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Université / Formation</label>
                  <input type="text" value={editPharmacienData.universiteFormation} onChange={e => setEditPharmacienData({...editPharmacienData, universiteFormation: e.target.value})}
                    placeholder="Ex: UCAD, UGB..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Année diplôme</label>
                  <input type="number" value={editPharmacienData.anneeDiplome} onChange={e => setEditPharmacienData({...editPharmacienData, anneeDiplome: e.target.value})}
                    placeholder="2019" min="1980" max="2030" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sexe</label>
                  <select value={editPharmacienData.sexe || 'M'} onChange={e => setEditPharmacienData({...editPharmacienData, sexe: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option value="M">Homme (H)</option>
                    <option value="F">Femme (F)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select value={editPharmacienData.type} onChange={e => setEditPharmacienData({...editPharmacienData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option value="PROPRIETAIRE">Propriétaire</option>
                    <option value="GERANT">Gérant</option>
                    <option value="ADJOINT">Adjoint</option>
                    <option value="STAGIAIRE">Stagiaire</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Statut</label>
                  <select value={editPharmacienData.actif ? "true" : "false"} onChange={e => setEditPharmacienData({...editPharmacienData, actif: e.target.value === "true"})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option value="true">Actif</option>
                    <option value="false">Inactif</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEditPharmacienModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={editLoading}
                  className="flex-1 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                  {editLoading ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}